import isaSpecJson from "../../../spec/am8_isa.json";

import type { QuickEntry, ReferenceKind } from "./types.js";

type IsaModeSpec = {
  group_nibble?: number;
  size: number;
};

type IsaGroupSpec = {
  group_nibble: number;
  kind: string;
  mnemonic: string;
  modes: string[];
};

type IsaSpecialSpec = {
  code: number;
  mnemonic: string;
  mode: string;
};

type IsaSpec = {
  groups: IsaGroupSpec[];
  modes: Record<string, IsaModeSpec>;
  specials: IsaSpecialSpec[];
};

export const ISA_SPEC = isaSpecJson as IsaSpec;

export const CATEGORY_ORDER: ReferenceKind[] = [
  "assembler-guide",
  "basic-guide",
  "debugger-guide",
  "tracker-guide",
  "forth-guide",
  "shell-command",
  "opcode",
  "addressing-mode",
  "public-api",
  "interrupt",
  "register",
  "flag",
  "memory-range"
];

export const CATEGORY_LABELS: Record<ReferenceKind, string> = {
  "addressing-mode": "Addressing Modes",
  "assembler-guide": "Assembler Guide",
  "basic-guide": "BASIC Guide",
  "debugger-guide": "Debugger Guide",
  flag: "Flags",
  "forth-guide": "FORTH Guide",
  "tracker-guide": "Tracker Guide",
  interrupt: "Interrupts",
  "memory-range": "Memory Map",
  opcode: "Opcodes",
  "public-api": "Public APIs",
  register: "Registers",
  "shell-command": "Shell Commands"
};

export const QUICK_ENTRIES: QuickEntry[] = [
  {
    category: "assembler-guide",
    description: "Assembler directives, label forms, expression helpers, and browser-supported source features.",
    entryId: "assembler-guide-directive-screenz",
    title: "Assembler Guide"
  },
  {
    category: "basic-guide",
    description: "Resident Tiny BASIC statements, signed and hex input, shorthand IF targets, DATA flow, and current PRINT behavior.",
    entryId: "basic-guide-if-else",
    title: "BASIC Guide"
  },
  {
    category: "debugger-guide",
    description: "Breakpoint and watchpoint DSL syntax for PC conditions, mem[] reads, and on read/write/access rules.",
    entryId: "debugger-guide-breakpoints-watchpoints",
    title: "Debugger Guide"
  },
  {
    category: "tracker-guide",
    description: "Tracker workflow, tracker_player workspace details, keyboard note mapping, effect codes, and explicit emulator write/start handoff.",
    entryId: "tracker-guide-overview",
    title: "Tracker Guide"
  },
  {
    category: "forth-guide",
    description: "Module-backed FORTH editor flow, stack and data-space tools, metaprogramming words, and the current interactive helpers.",
    entryId: "forth-guide-definitions-compile",
    title: "FORTH Guide"
  },
  {
    category: "shell-command",
    description: "User-ROM shell commands, mode switches, storage verbs, and direct memory/jump helpers.",
    entryId: "shell-command-load",
    title: "Shell Commands"
  },
  {
    category: "opcode",
    description: "Mnemonic families, variants, sizes, cycles, and affected flags.",
    entryId: "opcode-adc",
    title: "Opcodes"
  },
  {
    category: "public-api",
    description: "System ROM, lang ROM callgates, and extension-module ABI entry points.",
    entryId: "public-api-gfx-clear",
    title: "Public APIs"
  },
  {
    category: "memory-range",
    description: "Address-space windows, reserved areas, ROM overlays, and device ranges.",
    entryId: "memory-range-zero-page",
    title: "Memory Map"
  },
  {
    category: "register",
    description: "CPU registers plus high-touch MMIO control points like AUD_VOICE_CTRL and AUD_PITCH_CFG.",
    entryId: "register-aud-voice-ctrl",
    title: "Registers"
  },
  {
    category: "flag",
    description: "Status-bit meaning, producers, consumers, and flag-control instructions.",
    entryId: "flag-c",
    title: "Flags"
  },
  {
    category: "interrupt",
    description: "IRQ, NMI, BRK, vectors, masks, and device-local acknowledge rules.",
    entryId: "interrupt-irq",
    title: "Interrupts"
  }
];

export const COMMON_LOOKUP_IDS = [
  "assembler-guide-directive-screenz",
  "basic-guide-if-else",
  "debugger-guide-breakpoints-watchpoints",
  "tracker-guide-overview",
  "forth-guide-definitions-compile",
  "forth-guide-data-space",
  "forth-guide-metaprogramming-repl",
  "shell-command-load",
  "opcode-lda",
  "public-api-put-char-at",
  "opcode-jmp",
  "interrupt-irq",
  "register-p",
  "register-aud-voice-ctrl",
  "register-aud-pitch-cfg",
  "register-aud-filt-ctrl",
  "register-stg-status",
  "register-voc-raster",
  "memory-range-zero-page",
  "memory-range-storage-block"
];

export const HIGHLIGHTED_IDS = [
  "assembler-guide-local-named",
  "basic-guide-data-read-restore",
  "basic-guide-expressions",
  "debugger-guide-breakpoints-watchpoints",
  "tracker-guide-effects-and-transport",
  "forth-guide-data-space",
  "forth-guide-loop-control",
  "forth-guide-metaprogramming-repl",
  "shell-command-basic",
  "opcode-adc",
  "opcode-sbc",
  "opcode-jsr",
  "public-api-cursor-setxy",
  "public-api-init-language-state",
  "addressing-mode-imm",
  "addressing-mode-indy",
  "interrupt-nmi",
  "register-bank-reg",
  "register-aud-voice-ctrl",
  "register-aud-pitch-cfg",
  "register-aud-filt-ctrl",
  "register-stg-status",
  "register-ioc-vctrl",
  "register-voc-memptr",
  "flag-c",
  "memory-range-audio-block",
  "memory-range-io-window",
  "memory-range-storage-block",
  "memory-range-system-rom"
];

export const ADDRESSING_MODE_SEEDS = [
  {
    aliases: ["implicit", "implied"],
    bytes: 1,
    description: "The opcode fully defines the operation; no operand bytes are fetched.",
    effectiveAddress: "No operand address. The instruction acts on fixed CPU state.",
    id: "addressing-mode-imp",
    keywords: ["implied", "no operand"],
    notes: [
      "Used by stack, transfer, control, and flag instructions such as RTS, INX, and SEC.",
      "Instructions still consume cycles even without operand bytes."
    ],
    syntax: "MNEMONIC",
    title: "Implicit",
    valueKind: "none"
  },
  {
    aliases: ["accumulator"],
    bytes: 1,
    description: "The accumulator is the explicit operand and destination.",
    effectiveAddress: "No memory address. The operation reads and writes register A directly.",
    id: "addressing-mode-acc",
    keywords: ["a register", "shift accumulator"],
    notes: ["Used by ASL, LSR, ROL, ROR, and ASR when the target is A instead of memory."],
    syntax: "MNEMONIC A",
    title: "Accumulator",
    valueKind: "register A"
  },
  {
    aliases: ["immediate", "literal"],
    bytes: 2,
    description: "Embeds one literal byte directly after the opcode.",
    effectiveAddress: "The operand byte is read from PC+1; no extra memory lookup happens.",
    id: "addressing-mode-imm",
    keywords: ["literal", "constant", "#"],
    notes: [
      "Typical form for loads, ALU operations, and BRK's vector selector byte.",
      "Immediate values are shown with a leading #, for example #$10."
    ],
    syntax: "MNEMONIC #$10",
    title: "Immediate",
    valueKind: "8-bit literal"
  },
  {
    aliases: ["zero page", "zp"],
    bytes: 2,
    description: "Uses one zero-page byte as the effective address.",
    effectiveAddress: "EA = $00xx using the operand byte as the low address byte.",
    id: "addressing-mode-zp",
    keywords: ["fast memory", "page zero"],
    notes: [
      "Useful for compact, fast access to frequently used state.",
      "Search for Zero Page to see the reserved and recommended ranges in the memory map."
    ],
    syntax: "MNEMONIC $20",
    title: "Zero Page",
    valueKind: "8-bit address"
  },
  {
    aliases: ["absolute"],
    bytes: 3,
    description: "Uses a full 16-bit address stored in the operand bytes.",
    effectiveAddress: "EA = low byte at PC+1 plus high byte at PC+2.",
    id: "addressing-mode-abs",
    keywords: ["16-bit address", "full address"],
    notes: ["This is the most direct way to target any location in the 64 KiB address space."],
    syntax: "MNEMONIC $1234",
    title: "Absolute",
    valueKind: "16-bit address"
  },
  {
    aliases: ["indirect", "pointer"],
    bytes: 3,
    description: "Reads a 16-bit pointer from memory and jumps to the fetched target.",
    effectiveAddress: "Target = mem[addr] + 256*mem[addr+1].",
    id: "addressing-mode-ind",
    keywords: ["pointer jump", "jmp indirect"],
    notes: ["Currently only JMP uses this mode in the public opcode table."],
    syntax: "JMP ($1234)",
    title: "Indirect",
    valueKind: "16-bit pointer"
  },
  {
    aliases: ["zero page x", "zp,x"],
    bytes: 2,
    description: "Adds X to a zero-page base address with zero-page wraparound.",
    effectiveAddress: "EA = ($00 + operand + X) & $00FF.",
    id: "addressing-mode-zpx",
    keywords: ["indexed zero page", "wraparound", "x index"],
    notes: ["The sum wraps inside zero page instead of carrying into page 1."],
    syntax: "MNEMONIC $20,X",
    title: "Zero Page,X",
    valueKind: "8-bit address plus X"
  },
  {
    aliases: ["zero page y", "zp,y"],
    bytes: 2,
    description: "Adds Y to a zero-page base address with zero-page wraparound.",
    effectiveAddress: "EA = ($00 + operand + Y) & $00FF.",
    id: "addressing-mode-zpy",
    keywords: ["indexed zero page", "wraparound", "y index"],
    notes: ["This mirrors Zero Page,X but uses Y as the index register."],
    syntax: "MNEMONIC $20,Y",
    title: "Zero Page,Y",
    valueKind: "8-bit address plus Y"
  },
  {
    aliases: ["absolute x", "abs,x"],
    bytes: 3,
    description: "Adds X to a full 16-bit base address.",
    effectiveAddress: "EA = base16 + X.",
    id: "addressing-mode-absx",
    keywords: ["indexed absolute", "x index"],
    notes: ["Many load and ALU families add an extra cycle when the addition crosses a page boundary."],
    syntax: "MNEMONIC $1234,X",
    title: "Absolute,X",
    valueKind: "16-bit address plus X"
  },
  {
    aliases: ["absolute y", "abs,y"],
    bytes: 3,
    description: "Adds Y to a full 16-bit base address.",
    effectiveAddress: "EA = base16 + Y.",
    id: "addressing-mode-absy",
    keywords: ["indexed absolute", "y index"],
    notes: ["Many load and ALU families add an extra cycle when the addition crosses a page boundary."],
    syntax: "MNEMONIC $1234,Y",
    title: "Absolute,Y",
    valueKind: "16-bit address plus Y"
  },
  {
    aliases: ["relative", "branch offset"],
    bytes: 2,
    description: "Branches relative to the next instruction using a signed 8-bit offset.",
    effectiveAddress: "Target = PC after operand fetch plus signed rel8.",
    id: "addressing-mode-rel",
    keywords: ["branch", "signed offset"],
    notes: [
      "Branch timings vary: base cost when not taken, plus extra work when taken and when a page is crossed.",
      "Used by BEQ, BNE, BCS, BCC, BMI, BPL, BVS, and BVC."
    ],
    syntax: "BNE LABEL",
    title: "Relative",
    valueKind: "signed 8-bit offset"
  },
  {
    aliases: ["indexed indirect", "(zp,x)"],
    bytes: 2,
    description: "Adds X in zero page, then reads a 16-bit pointer from the wrapped location.",
    effectiveAddress: "ptr = mem16_zp((operand + X) & $FF); EA = ptr.",
    id: "addressing-mode-indx",
    keywords: ["pointer", "indexed indirect", "x pointer"],
    notes: ["Often used for table-driven dispatch or compact pointer arrays in zero page."],
    syntax: "MNEMONIC ($20,X)",
    title: "Indexed Indirect",
    valueKind: "zero-page pointer plus X"
  },
  {
    aliases: ["indirect indexed", "(zp),y"],
    bytes: 2,
    description: "Reads a 16-bit zero-page pointer, then adds Y to the fetched address.",
    effectiveAddress: "ptr = mem16_zp(operand); EA = ptr + Y.",
    id: "addressing-mode-indy",
    keywords: ["pointer", "indirect indexed", "y pointer"],
    notes: ["Loads and ALU families usually gain an extra cycle when ptr + Y crosses a page boundary."],
    syntax: "MNEMONIC ($20),Y",
    title: "Indirect Indexed",
    valueKind: "zero-page pointer then +Y"
  },
  {
    aliases: ["pre-indexed y", "(zp,y)"],
    bytes: 2,
    description: "Forms a zero-page pointer address by adding Y before reading the pointer bytes.",
    effectiveAddress: "ptr = mem16_zp((operand + Y) & $FF); EA = ptr.",
    id: "addressing-mode-indy-pre",
    keywords: ["pointer", "pre-indexed y"],
    notes: ["This is distinct from Indirect Indexed because Y is applied before pointer fetch, not after."],
    syntax: "MNEMONIC ($20,Y)",
    title: "Zero-Page Pre-Indexed Y",
    valueKind: "zero-page pointer address plus Y"
  },
  {
    aliases: ["post-indexed x", "(zp),x"],
    bytes: 2,
    description: "Reads a zero-page pointer, then adds X to the fetched 16-bit target.",
    effectiveAddress: "ptr = mem16_zp(operand); EA = ptr + X.",
    id: "addressing-mode-indx-post",
    keywords: ["pointer", "post-indexed x"],
    notes: ["This is distinct from Indexed Indirect because X is applied after pointer fetch, not before."],
    syntax: "MNEMONIC ($20),X",
    title: "Zero-Page Post-Indexed X",
    valueKind: "zero-page pointer then +X"
  }
] as const;

export const FLAG_SEEDS = [
  {
    aliases: ["negative", "sign"],
    bit: "P.7",
    clearedBy: ["loads and ALU results with bit 7 clear", "branch/compare outcomes that leave a non-negative result"],
    id: "flag-n",
    keywords: ["negative", "sign bit", "branch on minus"],
    meaning: "Reflects bit 7 of the last flag-producing result.",
    relatedIds: ["register-p", "opcode-bmi", "opcode-bpl"],
    setBy: ["loads and ALU results with bit 7 set", "INX/DEX/INY/DEY and transfer results with bit 7 set"],
    summary: "Tracks the sign bit of the most recent flag-producing result.",
    title: "N - Negative"
  },
  {
    aliases: ["overflow", "signed overflow"],
    bit: "P.6",
    clearedBy: ["ADC/SBC results without signed overflow"],
    id: "flag-v",
    keywords: ["overflow", "branch on overflow"],
    meaning: "Records signed overflow for arithmetic operations.",
    relatedIds: ["register-p", "opcode-bvs", "opcode-bvc", "opcode-adc", "opcode-sbc"],
    setBy: ["ADC", "SBC"],
    summary: "Captures signed overflow so code can branch on arithmetic wrap conditions.",
    title: "V - Overflow"
  },
  {
    aliases: ["interrupt disable", "irq mask"],
    bit: "P.2",
    clearedBy: ["CLI"],
    id: "flag-i",
    keywords: ["interrupt", "irq", "mask"],
    meaning: "Masks maskable IRQ handling when set.",
    relatedIds: ["register-p", "opcode-cli", "opcode-sei", "opcode-brk", "opcode-rti"],
    setBy: ["SEI", "BRK/IRQ/NMI entry"],
    summary: "Controls whether normal IRQ requests are accepted at instruction boundaries.",
    title: "I - Interrupt Disable"
  },
  {
    aliases: ["zero", "equal"],
    bit: "P.1",
    clearedBy: ["flag-producing results that are non-zero"],
    id: "flag-z",
    keywords: ["zero", "equal", "branch on equal"],
    meaning: "Set when the last flag-producing result was zero.",
    relatedIds: ["register-p", "opcode-beq", "opcode-bne", "opcode-jeq", "opcode-jne"],
    setBy: ["loads, ALU ops, transfers, and increments/decrements that produce 0", "CMP when A equals the operand"],
    summary: "Supports equality tests and zero-result checks across loads, ALU ops, and compares.",
    title: "Z - Zero"
  },
  {
    aliases: ["carry", "borrow"],
    bit: "P.0",
    clearedBy: ["CLC", "ADC results without carry-out", "SBC/CMP results that borrow"],
    id: "flag-c",
    keywords: ["carry", "borrow", "branch on carry"],
    meaning: "Acts as the carry-out for addition and inverted borrow for subtraction and compare.",
    relatedIds: [
      "register-p",
      "opcode-adc",
      "opcode-sbc",
      "opcode-cmp",
      "opcode-bcs",
      "opcode-bcc",
      "opcode-jcs",
      "opcode-jcc",
      "opcode-sec",
      "opcode-clc"
    ],
    setBy: ["SEC", "ADC results with carry-out", "SBC/CMP results without borrow"],
    summary: "Feeds multi-byte arithmetic and carry-sensitive control flow.",
    title: "C - Carry"
  }
] as const;

export const INTERRUPT_SEEDS = [
  {
    acknowledge: [
      "Clear the device-local pending source in the originating block, for example TICx_ICR reads or VOC_IRQ_IFR W1C writes.",
      "Return from the handler with RTI so P and PC are restored from the stack."
    ],
    aliases: ["irq", "maskable interrupt", "interrupt request", "$fffe", "$ffff", "0xfffe", "0xffff"],
    id: "interrupt-irq",
    keywords: ["wired-or irq line", "instruction boundary", "p.i", "mask bit", "irq vector", "brk shares vector"],
    notes: [
      "IRQ is sampled only when the CPU reaches an eligible PH_FETCH instruction boundary.",
      "A pending IRQ that appears mid-instruction waits until a later eligible fetch boundary.",
      "CLI can expose a pending IRQ only after CLI finishes and the next eligible fetch boundary is reached."
    ],
    relatedIds: [
      "flag-i",
      "register-p",
      "opcode-cli",
      "opcode-sei",
      "opcode-rti",
      "opcode-brk",
      "register-tic1-icr",
      "register-voc-irq-ifr",
      "register-voc-irq-ier"
    ],
    response: "Maskable: accepted only when P.I=0. Trap entry consumes the fetch-boundary tick for the first stack push before any vector fetch.",
    sources: [
      "TIC1 timer underflow sources route to the shared IRQ line when their local mask bits are enabled.",
      "VOC raster and sprite interrupt sources contribute to IRQ through VOC_IRQ_IFR and VOC_IRQ_IER.",
      "UART, audio, and other IRQ-capable devices contribute through their own local IFR/IER logic."
    ],
    summary: "Maskable interrupt line sampled at instruction boundaries and serviced through the shared IRQ/BRK vector.",
    title: "IRQ - Maskable Interrupt",
    vectors: [{ address: 0xfffe, label: "IRQ/BRK vector low/high" }]
  },
  {
    acknowledge: [
      "Clear or drop the originating NMI source in the device so the line can fall low.",
      "A second NMI requires a fresh low-to-high edge before the CPU may take it again."
    ],
    aliases: ["nmi", "non-maskable interrupt", "$fffa", "$fffb", "0xfffa", "0xfffb"],
    id: "interrupt-nmi",
    keywords: ["edge latched", "nmi wins over irq", "ignores p.i", "nmi vector"],
    notes: [
      "NMI line edges are observed before CPU phase work and latched until the next eligible PH_FETCH boundary consumes them.",
      "If both NMI and IRQ are eligible at the same fetch boundary, NMI wins.",
      "NMI acceptance ignores the interrupt-disable bit in P."
    ],
    relatedIds: ["flag-i", "register-p", "opcode-rti", "register-tic2-icr", "interrupt-irq", "interrupt-vectors"],
    response: "Non-maskable and edge-latched: once pending, it is serviced at the next eligible fetch boundary regardless of P.I.",
    sources: [
      "TIC2 timer underflow sources currently route to the machine NMI line.",
      "Future NMI-capable devices would join the same wired-OR NMI line and still follow the edge-latched CPU rule."
    ],
    summary: "Edge-latched non-maskable interrupt that ignores P.I and uses its own vector priority over IRQ.",
    title: "NMI - Non-Maskable Interrupt",
    vectors: [{ address: 0xfffa, label: "NMI vector low/high" }]
  },
  {
    acknowledge: [
      "Service the shared BRK/IRQ handler and return with RTI after the software trap work is complete.",
      "BRK itself is an opcode, so there is no external device latch to clear."
    ],
    aliases: ["brk", "software interrupt", "trap opcode", "0xf2", "$f2"],
    id: "interrupt-brk",
    keywords: ["software trap", "shared irq vector", "pc+2", "sets i"],
    notes: [
      "BRK uses the same vector as IRQ but is initiated by executing opcode F2 instead of by an external line level.",
      "The trap sequence pushes PC+2 and P, then sets I before vectoring through $FFFE/$FFFF."
    ],
    relatedIds: ["opcode-brk", "opcode-rti", "interrupt-irq", "interrupt-vectors", "register-p", "flag-i"],
    response: "Software initiated: execution of BRK starts trap entry immediately through the shared IRQ/BRK vector and sets I on entry.",
    sources: ["Opcode BRK (`0xF2`) in the currently executing instruction stream."],
    summary: "Software interrupt path entered by executing BRK, sharing the IRQ vector but not the external IRQ line.",
    title: "BRK - Software Interrupt",
    vectors: [{ address: 0xfffe, label: "BRK/IRQ vector low/high" }]
  },
  {
    acknowledge: [
      "Reset is not an acknowledgeable interrupt source, but the vector table sits in the same top-of-ROM region.",
      "IRQ, BRK, and NMI handlers return through RTI after reading their vectors from this table."
    ],
    aliases: ["interrupt vectors", "vector table", "$fffa", "$fffc", "$fffe", "0xfffa", "0xfffc", "0xfffe"],
    id: "interrupt-vectors",
    keywords: ["top of rom", "fffa fffb", "fffc fffd", "fffe ffff", "system rom"],
    notes: [
      "The final six bytes of the 8 KiB system ROM hold the NMI, RESET, and IRQ/BRK vectors.",
      "In the current ROM build, NMI and RESET point to RESET while IRQ/BRK points to IRQ_ENTRY.",
      "IRQ_ENTRY then jumps through the low-memory RAM IRQ vector at $0314/$0315 so the live IRQ handler can be swapped without rewriting top-of-ROM bytes.",
      "RESET is included here for completeness even though it is not part of the normal interrupt-reference category."
    ],
    relatedIds: ["interrupt-irq", "interrupt-nmi", "interrupt-brk", "memory-range-system-rom", "memory-range-ram-irq-vector", "register-pc"],
    response: "The CPU reads one of these vector pairs after trap or reset entry to choose the next PC target.",
    sources: [
      "NMI uses $FFFA/$FFFB.",
      "RESET uses $FFFC/$FFFD.",
      "IRQ and BRK share $FFFE/$FFFF."
    ],
    summary: "Top-of-ROM vector table that dispatches NMI, RESET, and IRQ/BRK entry targets.",
    title: "Interrupt Vectors",
    vectors: [
      { address: 0xfffa, label: "NMI vector" },
      { address: 0xfffc, label: "RESET vector" },
      { address: 0xfffe, label: "IRQ/BRK vector" }
    ]
  }
] as const;

export const REGISTER_SEEDS = [
  {
    access: "R/W",
    address: null,
    addressLabel: null,
    aliases: ["accumulator"],
    bitFields: [],
    id: "register-a",
    keywords: ["accumulator", "alu", "load", "store"],
    notes: [
      "Primary destination for loads and ALU families such as ADC, SBC, AND, ORA, and EOR.",
      "PLA and TXA/TYA can also move values into A."
    ],
    relatedIds: ["flag-n", "flag-z", "flag-c", "flag-v", "opcode-lda", "opcode-sta", "opcode-adc", "opcode-sbc"],
    reset: "0x00",
    scope: "CPU",
    summary: "Main 8-bit accumulator used by arithmetic, logic, loads, stores, and transfers.",
    title: "A",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: null,
    addressLabel: null,
    aliases: ["index x"],
    bitFields: [],
    id: "register-x",
    keywords: ["index", "x register"],
    notes: [
      "Used for indexed addressing modes such as Zero Page,X and Absolute,X.",
      "Can be moved to and from A and SP through TAX, TXA, TSX, and TXS."
    ],
    relatedIds: ["opcode-ldx", "opcode-stx", "opcode-inx", "opcode-dex", "opcode-tax", "opcode-txa", "opcode-tsx", "opcode-txs"],
    reset: "0x00",
    scope: "CPU",
    summary: "Primary X index register for addressing, loops, and register transfers.",
    title: "X",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: null,
    addressLabel: null,
    aliases: ["index y"],
    bitFields: [],
    id: "register-y",
    keywords: ["index", "y register"],
    notes: [
      "Used for indexed addressing modes such as Zero Page,Y and Absolute,Y.",
      "Can be moved to and from A and SP through TAY, TYA, TSY, and TYS."
    ],
    relatedIds: ["opcode-ldy", "opcode-sty", "opcode-iny", "opcode-dey", "opcode-tay", "opcode-tya", "opcode-tsy", "opcode-tys"],
    reset: "0x00",
    scope: "CPU",
    summary: "Primary Y index register for addressing, loops, and register transfers.",
    title: "Y",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: null,
    addressLabel: null,
    aliases: ["stack pointer"],
    bitFields: [],
    id: "register-sp",
    keywords: ["stack", "stack pointer"],
    notes: [
      "Points into page $0100 and grows downward on pushes.",
      "JSR, RTS, RTI, BRK, PHA, PLA, PHP, and PLP all use the stack."
    ],
    relatedIds: ["memory-range-stack", "opcode-jsr", "opcode-rts", "opcode-rti", "opcode-pha", "opcode-pla", "opcode-php", "opcode-plp"],
    reset: "0xFF",
    scope: "CPU",
    summary: "8-bit stack pointer for the fixed hardware stack in page $0100.",
    title: "SP",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: null,
    addressLabel: null,
    aliases: ["program counter"],
    bitFields: [],
    id: "register-pc",
    keywords: ["program counter", "instruction pointer"],
    notes: [
      "Reset loads PC from the reset vector at $FFFC/$FFFD.",
      "Branches and jumps replace PC in different ways depending on their addressing mode."
    ],
    relatedIds: ["memory-range-system-rom", "opcode-jmp", "opcode-jsr", "opcode-rts", "opcode-rti", "opcode-brk"],
    reset: "mem16($FFFC)",
    scope: "CPU",
    summary: "16-bit instruction pointer that tracks the next opcode fetch.",
    title: "PC",
    width: "16-bit"
  },
  {
    access: "R/W",
    address: null,
    addressLabel: null,
    aliases: ["status register", "processor status", "p"],
    bitFields: [
      { description: "Negative result flag.", label: "N", value: "bit 7" },
      { description: "Signed overflow flag.", label: "V", value: "bit 6" },
      { description: "Reserved bit kept high by the core.", label: "1", value: "bit 5" },
      { description: "Interrupt disable flag.", label: "I", value: "bit 2" },
      { description: "Zero-result flag.", label: "Z", value: "bit 1" },
      { description: "Carry / no-borrow flag.", label: "C", value: "bit 0" }
    ],
    id: "register-p",
    keywords: ["status", "flags", "processor status"],
    notes: [
      "PLP and RTI restore P from the stack; the core normalizes reserved bits afterward.",
      "Branch and conditional jump families consume these flags without modifying them."
    ],
    relatedIds: ["flag-n", "flag-v", "flag-i", "flag-z", "flag-c", "opcode-php", "opcode-plp"],
    reset: "0x28",
    scope: "CPU",
    summary: "Processor-status register containing the machine's negative, overflow, interrupt, zero, and carry state.",
    title: "P",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0x0001,
    addressLabel: "$0001",
    aliases: ["bank reg", "loram", "hiram", "charen"],
    bitFields: [
      { description: "Allows the upper ROM window at $A000..$BFFF when HIRAM is also set.", label: "LORAM", value: "bit 0" },
      { description: "Enables the system ROM window at $E000..$FFFF.", label: "HIRAM", value: "bit 1" },
      { description: "Shows I/O instead of char ROM in the $D000 window when ROM banking is active.", label: "CHAREN", value: "bit 2" }
    ],
    id: "register-bank-reg",
    keywords: ["banking", "overlay", "rom window", "char rom", "board decode"],
    notes: [
      "Controls whether RAM, ROM, or I/O is visible in the CPU's classic C64-like overlay windows.",
      "The fixed board decoder applies this register before routing the CPU's $A000, $D000, and $E000 windows.",
      "Changing this register affects what the CPU sees, not what the VOC fetch path sees.",
      "VOC now uses IOC_VCTRL at $D05B for the authoritative 4x16 KiB bank latch."
    ],
    relatedIds: ["memory-range-upper-rom", "memory-range-io-window", "memory-range-system-rom", "register-ioc-vctrl"],
    reset: "0x07",
    scope: "MMIO",
    summary: "CPU-port banking register for ROM and I/O overlay selection.",
    title: "BANK_REG",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd011,
    addressLabel: "$D011",
    aliases: ["voc_ctrl1", "voc ctrl1", "bmm", "den", "yscroll"],
    bitFields: [
      { description: "Vertical fine scroll value 0..7.", label: "YSCROLL", value: "bits 0..2" },
      { description: "Display enable.", label: "DEN", value: "bit 4" },
      { description: "Bitmap-family mode select; combine with VOC_CTRL2.MCM to pick text, Mode A, Mode B, or Mode C.", label: "BMM", value: "bit 5" },
      { description: "Reserved ECM bit in the shipped VOC-II MVP; does not select a separate productive mode.", label: "ECM", value: "bit 6" },
      { description: "Raster compare bit 8 on write, current raster bit 8 on read.", label: "RASTER_BIT8", value: "bit 7" }
    ],
    id: "register-voc-ctrl1",
    keywords: ["display enable", "bitmap mode", "raster bit 8"],
    notes: [
      "Together with VOC_CTRL2.MCM, the productive mode map is: 00=text, 10=Mode A (320x200 1bpp), 11=Mode B (160x200 2bpp with metadata colors), 01=Mode C (160x200 2bpp direct palette).",
      "Bitmap-family modes use bitmap_base..bitmap_base+0x1F3F relative to the active IOC-selected 16 KiB video bank base.",
      "Together with $D012 it forms the raster compare target."
    ],
    relatedIds: ["register-voc-raster", "memory-range-graphics-plane", "memory-range-io-window"],
    reset: "0x30",
    scope: "MMIO",
    summary: "Primary video control register for display enable, bitmap mode, Y scroll, and raster compare bit 8.",
    title: "VOC_CTRL1",
    width: "8-bit"
  },
  {
    access: "R/W*",
    address: 0xd012,
    addressLabel: "$D012",
    aliases: ["voc_raster", "voc raster", "raster compare", "$d012"],
    bitFields: [],
    id: "register-voc-raster",
    keywords: ["raster", "interrupt compare", "scanline"],
    notes: [
      "Reads return the current raster low byte; writes set the compare low byte.",
      "Use with $D011 bit 7 for full 9-bit raster compare values."
    ],
    relatedIds: ["register-voc-ctrl1", "register-voc-irq-ifr", "register-voc-irq-ier"],
    reset: "0x00 compare low byte",
    scope: "MMIO",
    summary: "Raster counter low byte on read and raster compare low byte on write.",
    title: "VOC_RASTER",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd016,
    addressLabel: "$D016",
    aliases: [
      "voc_ctrl2",
      "voc ctrl2",
      "xscroll",
      "csel",
      "text_bit7_internal",
      "text bit7 internal",
      "reverse text",
      "$d016"
    ],
    bitFields: [
      { description: "Horizontal fine scroll value 0..7.", label: "XSCROLL", value: "bits 0..2" },
      { description: "Compatibility control bit; no visible width switch today.", label: "CSEL", value: "bit 3" },
      {
        description: "Multicolor / packed-pixel mode bit; combine with VOC_CTRL1.BMM to pick text, Mode A, Mode B, or Mode C.",
        label: "MCM",
        value: "bit 4"
      },
      {
        description:
          "Selects text bit7 handling: 0 keeps the legacy upper-glyph-half fetch, 1 uses bits 0..6 as the glyph index and inverts the fetched row internally when bit 7 is set.",
        label: "TEXT_BIT7_INTERNAL",
        value: "bit 5"
      }
    ],
    id: "register-voc-ctrl2",
    keywords: ["x scroll", "video control", "reverse text", "glyph mode", "bit7"],
    notes: [
      "Reset default is $08, so CSEL is set while MCM and TEXT_BIT7_INTERNAL stay clear for legacy-compatible reset state.",
      "The normal ROM text preset also keeps $08, so a fresh boot stays in the 256-glyph direct screen-code mode unless guest code later enables TEXT_BIT7_INTERNAL.",
      "With bit 5 clear, screen-code bit7 remains part of the glyph index and can address the upper character-ROM half directly.",
      "With bit 5 set, VOC uses only screen-code bits 0..6 for the glyph fetch and applies reverse display by inverting the fetched row internally."
    ],
    relatedIds: ["memory-range-io-window", "register-voc-ctrl1", "register-voc-memptr"],
    reset: "$08",
    scope: "MMIO",
    summary: "Secondary video control register for horizontal fine scroll, compatibility control, and selectable text bit7 rendering.",
    title: "VOC_CTRL2",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd018,
    addressLabel: "$D018",
    aliases: ["voc_memptr", "voc memptr", "screen_off", "char_off", "gfx_off"],
    bitFields: [
      {
        description: "Selects screen_base in 0x0400 steps relative to the active IOC-selected 16 KiB video bank; color_base stays screen_base+0x0400.",
        label: "SCREEN_OFF",
        value: "bits 7..4"
      },
      {
        description: "Selects char_base in 0x0400 steps inside the bank-local char window at $1000..$1FFF.",
        label: "CHAR_OFF",
        value: "bits 3..2"
      },
      {
        description: "Selects bitmap_base as $2000, $3000, $4000, or $5000 relative to the active IOC-selected 16 KiB video bank base.",
        label: "GFX_OFF",
        value: "bits 1..0"
      }
    ],
    id: "register-voc-memptr",
    keywords: ["screen base", "char base", "bitmap base", "sprite pointer table", "packed color ram"],
    notes: [
      "Reset default $10 means screen=$0400, packed color=$0800, char_base=$1000, bitmap=$2000 in video bank 0.",
      "Changing SCREEN_OFF also moves the sprite pointer table because it sits at screen_base+0x03E8 after the 40x25 text matrix.",
      "Packed text/Mode-B Color-RAM always stays at screen_base+0x0400 and spans 500 bytes with low=even and high=odd cell nibbles.",
      "With VOC_CTRL2.TEXT_BIT7_INTERNAL clear, CHAR_OFF=3 is a reserved legacy combination because the full 2 KiB glyph range would run past the 4 KiB char window."
    ],
    relatedIds: ["register-ioc-vctrl", "memory-range-text-screen", "memory-range-sprite-pointers", "memory-range-color-ram", "memory-range-voc-char-window", "memory-range-graphics-plane"],
    reset: "$10",
    scope: "MMIO",
    summary: "Video memory-pointer register that relocates screen, char-window, sprite-pointer, and graphics bases relative to the active IOC-selected 16 KiB video bank.",
    title: "VOC_MEMPTR",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd020,
    addressLabel: "$D020",
    aliases: ["voc_border_color", "border color", "border"],
    bitFields: [{ description: "4-bit palette index for blank / border output.", label: "COLOR", value: "bits 0..3" }],
    id: "register-voc-border-color",
    keywords: ["border", "palette", "blank area"],
    notes: [
      "Only the low nibble is implemented; upper bits are ignored on write.",
      "The value is used for border / blank output outside active text or bitmap pixels."
    ],
    relatedIds: ["register-voc-bg-color", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "VOC border-color register using a 4-bit palette index.",
    title: "VOC_BORDER_COLOR",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd021,
    addressLabel: "$D021",
    aliases: ["voc_bg_color", "background color", "global background"],
    bitFields: [{ description: "4-bit palette index for the global text background and Mode B color source 00.", label: "COLOR", value: "bits 0..3" }],
    id: "register-voc-bg-color",
    keywords: ["background", "palette", "text mode", "mode b"],
    notes: [
      "Only the low nibble is implemented; upper bits are ignored on write.",
      "Text mode uses this as the global background color, while Mode B maps pixel code 00 to this color."
    ],
    relatedIds: ["register-voc-border-color", "memory-range-color-ram", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "VOC global background-color register using a 4-bit palette index.",
    title: "VOC_BG_COLOR",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd05b,
    addressLabel: "$D05B",
    aliases: ["ioc_vctrl", "ioc video control", "video bank select", "video bank"],
    bitFields: [
      {
        description: "Authoritative video-bank select: 0=>$0000..$3FFF, 1=>$4000..$7FFF, 2=>$8000..$BFFF, 3=>$C000..$FFFF.",
        label: "VIDEO_BANK",
        value: "bits 1..0"
      }
    ],
    id: "register-ioc-vctrl",
    keywords: ["ioc", "video bank", "16 KiB", "bank latch"],
    notes: [
      "This is the authoritative guest-visible write path for the coarse 4x16 KiB video-bank selection.",
      "Bits 7..2 are reserved, read back as 0, and ignore writes.",
      "D022 no longer has a documented video-banking role in the current machine model."
    ],
    relatedIds: ["memory-range-io-window", "register-voc-memptr", "memory-range-voc-char-window", "memory-range-graphics-plane"],
    reset: "$00",
    scope: "MMIO",
    summary: "IOC video-control register holding the authoritative 4x16 KiB video-bank latch.",
    title: "IOC_VCTRL",
    width: "8-bit"
  },
  {
    access: "W",
    address: 0xd060,
    addressLabel: "$D060",
    aliases: ["storage command", "disk command", "stg cmd"],
    bitFields: [],
    id: "register-stg-cmd",
    keywords: ["storage", "disk", "controller", "file level", "command"],
    notes: [
      "Write one of the file-level command ids such as STATUS, DIR_FIRST, LOAD_PRG, SAVE_PRG, DELETE, RENAME, SYNC, or LABEL, then pulse GO through STG_CTRL.",
      "DESC_PUSH, DESC_PULL, and RENAME_NAME_PUSH also use this command port to move descriptor and rename helper data through STG_DATA.",
      "The command vocabulary is intentionally file-level and IEC-inspired rather than a raw sector/block interface."
    ],
    relatedIds: ["memory-range-storage-block", "register-stg-ctrl", "register-stg-status", "register-stg-data", "register-stg-unit"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Storage-controller command register for file-level disk operations.",
    title: "STG_CMD",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd061,
    addressLabel: "$D061",
    aliases: ["storage control", "disk control", "stg ctrl"],
    bitFields: [
      { description: "Write-1 strobe that latches the command in STG_CMD for execution on the next storage-maintenance tick.", label: "GO", value: "bit 0" },
      { description: "Enable DONE/ERR interrupt forwarding from the storage block onto the shared IRQ line.", label: "IRQ_EN", value: "bit 1" },
      { description: "Write-1 acknowledge strobe that clears the sticky DONE/ERR result latches.", label: "ACK", value: "bit 7" }
    ],
    id: "register-stg-ctrl",
    keywords: ["storage", "go", "ack", "irq enable"],
    notes: [
      "GO and ACK are command strobes; software typically reads back only the IRQ_EN latch.",
      "The storage block follows the normal BUSY -> DONE/ERR pattern and does not create a separate CPU/video timing domain."
    ],
    relatedIds: ["memory-range-storage-block", "register-stg-cmd", "register-stg-status"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Storage-controller control register for GO, ACK, and IRQ enable.",
    title: "STG_CTRL",
    width: "8-bit"
  },
  {
    access: "R",
    address: 0xd062,
    addressLabel: "$D062",
    aliases: ["storage status", "disk status", "stg status"],
    bitFields: [
      { description: "The controller is busy executing a command or waiting for guest-side descriptor/payload streaming to complete.", label: "BUSY", value: "bit 0" },
      { description: "The last command completed successfully and remains latched until ACK.", label: "DONE", value: "bit 1" },
      { description: "The last command failed and the error code remains latched in STG_ERR until ACK.", label: "ERR", value: "bit 2" },
      { description: "A disk/backend is mounted for the currently selected drive unit.", label: "MOUNTED", value: "bit 3" },
      { description: "The selected drive currently has unsynchronized dirty state.", label: "DIRTY", value: "bit 4" },
      { description: "The selected drive is mounted read-only.", label: "RO", value: "bit 5" }
    ],
    id: "register-stg-status",
    keywords: ["storage", "disk", "busy", "done", "error", "mounted"],
    notes: [
      "This is the guest-visible summary of the storage controller frontend; the current runtime may satisfy commands through the controller/link/service split behind it.",
      "The machine-side contract remains file-level and descriptor-based even when the backend path is implemented by a host-side intelligent drive service."
    ],
    relatedIds: ["memory-range-storage-block", "register-stg-cmd", "register-stg-ctrl", "register-stg-data", "public-api-disk-status"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Storage-controller status register with busy/result/media bits for the active drive.",
    title: "STG_STATUS",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd068,
    addressLabel: "$D068",
    aliases: ["storage data", "disk data", "stg data"],
    bitFields: [],
    id: "register-stg-data",
    keywords: ["storage", "stream", "descriptor", "payload"],
    notes: [
      "Descriptor pushes/pulls, rename secondary names, and LOAD/SAVE payload bytes all stream through this port.",
      "Large file transfers are chunked internally at 256 bytes even though the guest-visible contract stays file-level."
    ],
    relatedIds: ["memory-range-storage-block", "register-stg-cmd", "register-stg-status", "public-api-disk-load", "public-api-disk-save"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Streaming data port for storage descriptors, helper names, and file payload bytes.",
    title: "STG_DATA",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd069,
    addressLabel: "$D069",
    aliases: ["storage unit", "disk unit", "drive select", "stg unit"],
    bitFields: [{ description: "Select active drive unit: 0=drive 0, 1=drive 1; upper bits are ignored.", label: "UNIT", value: "bit 0" }],
    id: "register-stg-unit",
    keywords: ["storage", "disk", "drive", "unit select"],
    notes: [
      "This selects which mounted drive instance the storage controller frontend talks to for subsequent commands.",
      "The current ROM and shell helpers generally default to drive 0 but also support drive 1 where documented."
    ],
    relatedIds: ["memory-range-storage-block", "register-stg-cmd", "register-stg-status", "shell-command-dir", "shell-command-load", "shell-command-save"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Drive-unit select register for the storage controller block.",
    title: "STG_UNIT",
    width: "8-bit"
  },
  {
    access: "R/W1C",
    address: 0xd019,
    addressLabel: "$D019",
    aliases: ["voc_irq_ifr", "voc irq ifr", "raster irq", "sprite collision irq"],
    bitFields: [
      { description: "Raster interrupt pending latch.", label: "RASTER", value: "bit 0" },
      { description: "Sprite-vs-background interrupt pending latch.", label: "SPR_BG", value: "bit 1" },
      { description: "Sprite-vs-sprite interrupt pending latch.", label: "SPR_SPR", value: "bit 2" },
      { description: "Summary bit when an enabled source is pending.", label: "SUMMARY", value: "bit 7" }
    ],
    id: "register-voc-irq-ifr",
    keywords: ["interrupt flags", "w1c", "pending"],
    notes: [
      "Source latches can be set even while the enable mask is zero.",
      "Writing 1 clears the selected pending bits."
    ],
    relatedIds: ["register-voc-raster", "register-voc-irq-ier"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Video interrupt-flag register for raster and sprite-collision sources.",
    title: "VOC_IRQ_IFR",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd01a,
    addressLabel: "$D01A",
    aliases: ["voc_irq_ier", "voc irq ier", "irq enable"],
    bitFields: [
      { description: "Enable raster IRQ forwarding.", label: "RASTER_EN", value: "bit 0" },
      { description: "Enable sprite-vs-background IRQ forwarding.", label: "SPR_BG_EN", value: "bit 1" },
      { description: "Enable sprite-vs-sprite IRQ forwarding.", label: "SPR_SPR_EN", value: "bit 2" }
    ],
    id: "register-voc-irq-ier",
    keywords: ["interrupt enable", "mask"],
    notes: ["The enable mask gates whether pending video sources contribute to the summary bit and IRQ line."],
    relatedIds: ["register-voc-irq-ifr", "register-voc-raster"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Video interrupt-enable register for raster and sprite collision sources.",
    title: "VOC_IRQ_IER",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd040,
    addressLabel: "$D040",
    aliases: ["serial data", "uart data"],
    bitFields: [],
    id: "register-ser-data",
    keywords: ["serial", "tx", "rx"],
    notes: ["Read/write data register for the serial compatibility path."],
    relatedIds: ["memory-range-io-window", "register-ser-stat"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Serial data register for compatibility RX/TX traffic.",
    title: "SER_DATA",
    width: "8-bit"
  },
  {
    access: "R",
    address: 0xd041,
    addressLabel: "$D041",
    aliases: ["serial status", "uart status"],
    bitFields: [],
    id: "register-ser-stat",
    keywords: ["serial", "status"],
    notes: ["Pairs with SER_DATA and the serial control/interrupt registers in the same device block."],
    relatedIds: ["memory-range-io-window", "register-ser-data"],
    reset: "device-defined",
    scope: "MMIO",
    summary: "Serial status register for the compatibility RX/TX block.",
    title: "SER_STAT",
    width: "8-bit"
  },
  {
    access: "R",
    address: 0xd044,
    addressLabel: "$D044",
    aliases: ["board_cfg0", "board config", "console route", "ui_screen_mode"],
    bitFields: [
      {
        description: "Latched board console route: 0=screen, 1=serial, 2=both, 3 reserved.",
        label: "CONSOLE_ROUTE",
        value: "bits 1..0"
      }
    ],
    id: "register-board-cfg0",
    keywords: ["board config", "console routing", "transcript", "tty mirror"],
    notes: [
      "Guest software can read this latched board configuration value through the normal MMIO path, but guest writes are ignored.",
      "screen keeps ROM TTY output on the screen-oriented path only, serial keeps UART transcript mirroring active while screen state stays coherent, and both also mirrors interactive line-input echo to the screen.",
      "This is board-level console routing, not part of the IOC keyboard controller itself."
    ],
    relatedIds: ["memory-range-io-window", "register-ser-data", "public-api-chrout", "public-api-tty-out"],
    reset: "$00 on screen-first hosts",
    scope: "MMIO",
    summary: "Read-only board configuration register exposing the latched console-routing mode.",
    title: "BOARD_CFG0",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd050,
    addressLabel: "$D050",
    aliases: ["keyboard data", "ioc kbd data"],
    bitFields: [],
    id: "register-ioc-kbd-data",
    keywords: ["keyboard", "input queue", "fifo"],
    notes: [
      "This byte FIFO remains as a compatibility path while the ROM keyboard path prefers the IOC matrix scan registers.",
      "Logical desktop/web keyboard input no longer needs to generate PETSCII on the host side before reaching the guest."
    ],
    relatedIds: ["register-ioc-kbd-stat", "register-ioc-pra", "register-ioc-idcap", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Keyboard-data register for the input controller.",
    title: "IOC_KBD_DATA",
    width: "8-bit"
  },
  {
    access: "R/W1C*",
    address: 0xd051,
    addressLabel: "$D051",
    aliases: ["keyboard status", "ioc kbd stat", "fifo overflow"],
    bitFields: [
      { description: "FIFO ready: at least one queued byte can be read from IOC_KBD_DATA.", label: "RDY", value: "bit 0" },
      { description: "FIFO overflow latch; clear by writing bit 1.", label: "OVF", value: "bit 1" },
      { description: "IOC interrupt summary when an enabled keyboard/FIFO source is pending.", label: "IRQ", value: "bit 7" }
    ],
    id: "register-ioc-kbd-stat",
    keywords: ["keyboard status", "overflow", "fifo"],
    notes: [
      "The FIFO is now the legacy byte-oriented compatibility path beside the primary IOC keyboard matrix registers.",
      "Writing bit 1 clears the overflow latch without affecting matrix state."
    ],
    relatedIds: ["register-ioc-kbd-data", "register-ioc-pra", "register-ioc-idcap", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Keyboard FIFO status register for queue state, overflow handling, and IOC interrupt summary.",
    title: "IOC_KBD_STAT",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd052,
    addressLabel: "$D052",
    aliases: ["ioc_pra", "keyboard matrix row port", "pra"],
    bitFields: [],
    id: "register-ioc-pra",
    keywords: ["keyboard matrix", "row select", "active low"],
    notes: [
      "Together with IOC_PRB and the DDR registers, this forms the primary C64-like IOC keyboard matrix scan interface.",
      "Output bits drive row/column select levels; input bits read active-low intersections against the opposite port."
    ],
    relatedIds: ["register-ioc-prb", "register-ioc-ddra", "register-ioc-ddrb", "register-ioc-idcap", "memory-range-io-window"],
    reset: "0xFF",
    scope: "MMIO",
    summary: "IOC port A for keyboard-matrix row/column selection and active-low scan reads.",
    title: "IOC_PRA",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd053,
    addressLabel: "$D053",
    aliases: ["ioc_prb", "keyboard matrix column port", "prb"],
    bitFields: [],
    id: "register-ioc-prb",
    keywords: ["keyboard matrix", "column read", "active low"],
    notes: [
      "Pairs with IOC_PRA so guest software or ROM code can scan the IOC keyboard matrix in either direction.",
      "Input bits are pulled high until a pressed key connects them to a driven-low line on the opposite port."
    ],
    relatedIds: ["register-ioc-pra", "register-ioc-ddra", "register-ioc-ddrb", "register-ioc-idcap", "memory-range-io-window"],
    reset: "0xFF",
    scope: "MMIO",
    summary: "IOC port B for keyboard-matrix row/column selection and active-low scan reads.",
    title: "IOC_PRB",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd054,
    addressLabel: "$D054",
    aliases: ["ioc_ddra", "pra ddr", "row direction"],
    bitFields: [],
    id: "register-ioc-ddra",
    keywords: ["keyboard matrix", "direction register", "ddr"],
    notes: ["Set a bit to 1 for output drive on IOC_PRA; clear it to 0 for pull-up input reads during matrix scans."],
    relatedIds: ["register-ioc-pra", "register-ioc-prb", "register-ioc-ddrb", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "IOC port-A direction register used by the keyboard matrix scan path.",
    title: "IOC_DDRA",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd055,
    addressLabel: "$D055",
    aliases: ["ioc_ddrb", "prb ddr", "column direction"],
    bitFields: [],
    id: "register-ioc-ddrb",
    keywords: ["keyboard matrix", "direction register", "ddr"],
    notes: ["Set a bit to 1 for output drive on IOC_PRB; clear it to 0 for pull-up input reads during matrix scans."],
    relatedIds: ["register-ioc-pra", "register-ioc-prb", "register-ioc-ddra", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "IOC port-B direction register used by the keyboard matrix scan path.",
    title: "IOC_DDRB",
    width: "8-bit"
  },
  {
    access: "R",
    address: 0xd05f,
    addressLabel: "$D05F",
    aliases: ["ioc_idcap", "ioc capabilities", "kbd matrix capability"],
    bitFields: [
      { description: "Legacy keyboard FIFO path is available.", label: "KBD_FIFO", value: "bit 0" },
      { description: "IOC keyboard-matrix scan path through PRA/PRB/DDRA/DDRB is available.", label: "KBD_MATRIX", value: "bit 1" }
    ],
    id: "register-ioc-idcap",
    keywords: ["capabilities", "fifo", "matrix"],
    notes: ["ROM keyboard code uses this register to distinguish matrix-capable IOC behavior from legacy FIFO-only fallback paths during migration."],
    relatedIds: ["register-ioc-kbd-data", "register-ioc-kbd-stat", "register-ioc-pra", "register-ioc-prb", "memory-range-io-window"],
    reset: "0x03",
    scope: "MMIO",
    summary: "IOC capability register advertising legacy FIFO and keyboard-matrix support.",
    title: "IOC_IDCAP",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd404,
    addressLabel: "$D404/$D40C/$D414/$D41C",
    aliases: [
      "aud_voice_ctrl",
      "aud_v1_ctrl",
      "aud_v2_ctrl",
      "aud_v3_ctrl",
      "aud_v4_ctrl",
      "voice ctrl",
      "audio sync",
      "audio ring",
      "hard sync",
      "ring mod"
    ],
    bitFields: [
      { description: "Gate the voice envelope: 0->1 enters ATTACK, 1->0 enters RELEASE.", label: "GATE", value: "bit 0" },
      {
        description:
          "Hard-sync from the fixed predecessor voice chain V1<-V4, V2<-V1, V3<-V2, V4<-V3; a source wrap forces this voice's phase accumulator back to 0.",
        label: "SYNC",
        value: "bit 1"
      },
      {
        description:
          "Triangle-only ring modulation from the same fixed predecessor chain; xor the triangle phase bit with the source voice's phase_acc.bit23.",
        label: "RING",
        value: "bit 2"
      },
      { description: "Hold the oscillator phase accumulator at 0 while set.", label: "TEST", value: "bit 3" },
      { description: "Triangle waveform select.", label: "TRI", value: "bit 4" },
      { description: "Saw waveform select, resolved through the fixed 48 kHz BLEP bandlimiting path.", label: "SAW", value: "bit 5" },
      { description: "Pulse waveform select, resolved through the fixed 48 kHz BLEP bandlimiting path.", label: "PULSE", value: "bit 6" },
      { description: "Noise waveform select.", label: "NOISE", value: "bit 7" }
    ],
    id: "register-aud-voice-ctrl",
    keywords: ["audio", "voice ctrl", "sync", "ring", "hard sync", "triangle", "blep", "bandlimited"],
    notes: [
      "Each voice block exposes the same CTRL register at +4 within $D400..$D41F.",
      "SYNC and RING are modifiers, not waveform-select bits, so the valid one-waveform-at-a-time rule for bits 4..7 stays unchanged.",
      "RING affects only TRI; for SAW, PULSE, and NOISE it is intentionally inert in the current model.",
      "The shipped SAW/PULSE baseline uses one fixed 48 kHz, 256x16 BLEP lookup table with bounded per-voice tail state instead of global oversampling.",
      "TEST, silent phase resets, same-sample hard-sync resets, and waveform-select changes clear any pending saw/pulse BLEP tail before the next waveform sample.",
      "Bits 1 and 2 are now implemented, while later global sync-routing work still lives in the separate reserved register AUD_SYNC_RSVD at $D42A."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-modevol", "register-aud-status"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Per-voice control register for gate, hard-sync, triangle ring modulation, test, waveform selection, and fixed bandlimited saw/pulse behavior.",
    title: "AUD_VOICE_CTRL",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd420,
    addressLabel: "$D420",
    aliases: ["aud_modevol", "audio modevol", "master volume"],
    bitFields: [{ description: "Global linear output volume 0..15 before softclip.", label: "MASTER_VOL", value: "bits 3..0" }],
    id: "register-aud-modevol",
    keywords: ["audio", "master volume", "softclip"],
    notes: [
      "Bits 7..4 are reserved and read back as 0.",
      "The shared master softclip always runs after the volume-scaled mix so multi-voice peaks round off instead of hard clipping."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-filt-ctrl", "register-aud-filt-route"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Master audio volume register that feeds the shared post-mix softclip stage.",
    title: "AUD_MODEVOL",
    width: "8-bit"
  },
  {
    access: "R",
    address: 0xd421,
    addressLabel: "$D421",
    aliases: ["aud_status", "audio status", "voice active"],
    bitFields: [
      { description: "Voice 1 active: GATE=1 or its internal envelope is still non-zero.", label: "V1_ACTIVE", value: "bit 0" },
      { description: "Voice 2 active.", label: "V2_ACTIVE", value: "bit 1" },
      { description: "Voice 3 active.", label: "V3_ACTIVE", value: "bit 2" },
      { description: "Voice 4 active.", label: "V4_ACTIVE", value: "bit 3" },
      { description: "Periodic audio tick source is armed because AUD_TICK is non-zero.", label: "TICK_RUN", value: "bit 4" },
      { description: "Mirrors AUD_IRQ_IFR.TICK_PEND.", label: "TICK_PEND", value: "bit 5" }
    ],
    id: "register-aud-status",
    keywords: ["audio", "status", "voice active", "tick"],
    notes: ["Bits 7..6 are reserved and read back as 0."],
    relatedIds: ["memory-range-audio-block", "register-aud-irq-ifr", "register-aud-tick"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Read-only audio status register for voice activity and the shared periodic tick state.",
    title: "AUD_STATUS",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd422,
    addressLabel: "$D422",
    aliases: ["aud_irq_en", "audio irq enable", "tick ie"],
    bitFields: [{ description: "Enable forwarding of the periodic audio tick pending bit into the shared IRQ line.", label: "TICK_IE", value: "bit 0" }],
    id: "register-aud-irq-en",
    keywords: ["audio", "irq", "tick", "interrupt enable"],
    notes: ["Bits 7..1 remain reserved for future voice-done events and read back as 0 today."],
    relatedIds: ["memory-range-audio-block", "register-aud-irq-ifr", "register-aud-tick"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Audio interrupt-enable register for the shared periodic sample tick.",
    title: "AUD_IRQ_EN",
    width: "8-bit"
  },
  {
    access: "R/W1C",
    address: 0xd423,
    addressLabel: "$D423",
    aliases: ["aud_irq_ifr", "audio irq ifr", "tick pend"],
    bitFields: [
      { description: "Latched periodic audio tick pending bit; clear it by writing 1.", label: "TICK_PEND", value: "bit 0" },
      { description: "Read-only summary bit when an enabled audio source is pending.", label: "SUMMARY", value: "bit 7" }
    ],
    id: "register-aud-irq-ifr",
    keywords: ["audio", "irq", "w1c", "tick pending"],
    notes: [
      "Reading bit 7 reports whether AUD_IRQ_EN and AUD_IRQ_IFR currently overlap on any implemented source bit.",
      "Bits 4..1 stay reserved for future voice-complete latches."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-irq-en", "register-aud-status"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Audio interrupt-flag register with write-one-to-clear tick handling and a summary bit on reads.",
    title: "AUD_IRQ_IFR",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd424,
    addressLabel: "$D424/$D425",
    aliases: ["aud_tick", "audio tick", "sample scheduler"],
    bitFields: [],
    id: "register-aud-tick",
    keywords: ["audio", "tick", "48 khz", "scheduler"],
    notes: [
      "The audio block now emits samples at an exact 48 kHz when the default 1 MHz machine clock is active.",
      "AUD_TICK counts emitted audio samples, so one tick unit is one 48 kHz sample at the default clock."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-status", "register-aud-irq-en", "register-aud-irq-ifr"],
    reset: "0x0000",
    scope: "MMIO",
    summary: "16-bit reload pair for the shared periodic audio tick, counted in emitted audio samples.",
    title: "AUD_TICK",
    width: "16-bit pair"
  },
  {
    access: "R/W",
    address: 0xd426,
    addressLabel: "$D426/$D427",
    aliases: ["aud_fc", "audio cutoff", "filter cutoff"],
    bitFields: [{ description: "Shared 11-bit cutoff code 0..2047 across AUD_FC_LO/AUD_FC_HI[2:0].", label: "FC", value: "bits 10..0" }],
    id: "register-aud-fc",
    keywords: ["audio", "filter cutoff", "svf", "80 hz", "12 khz"],
    notes: [
      "Reset opens the filter fully at 0x07FF so the routed path behaves like a near-bypass until software lowers the cutoff.",
      "The current machine model maps the cutoff code roughly onto 80 Hz..12 kHz."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-filt-ctrl", "register-aud-filt-route"],
    reset: "0x07FF",
    scope: "MMIO",
    summary: "Shared state-variable-filter cutoff pair for the audio block's routed voice path.",
    title: "AUD_FC",
    width: "11-bit pair"
  },
  {
    access: "R/W",
    address: 0xd428,
    addressLabel: "$D428",
    aliases: ["aud_filt_ctrl", "audio filter ctrl", "resonance", "lp bp hp"],
    bitFields: [
      { description: "Shared resonance code 0..15 mapped to a stable, non-self-oscillating damping range.", label: "RES", value: "bits 3..0" },
      { description: "Filter mode select: 00=LP, 01=BP, 10=HP, 11 coerces back to LP.", label: "MODE", value: "bits 5..4" }
    ],
    id: "register-aud-filt-ctrl",
    keywords: ["audio", "filter", "resonance", "lp", "bp", "hp", "svf"],
    notes: [
      "The browser machine model uses one shared digital state-variable filter after the routed voices are summed.",
      "Bits 7..6 are reserved and read back as 0."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-fc", "register-aud-filt-route", "register-aud-modevol"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Shared SVF resonance and LP/BP/HP mode register for the audio mixer.",
    title: "AUD_FILT_CTRL",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd429,
    addressLabel: "$D429",
    aliases: ["aud_filt_route", "audio filter route", "voice mask"],
    bitFields: [
      { description: "Route voice 1 through the shared filter instead of the direct mix path.", label: "V1_FILT", value: "bit 0" },
      { description: "Route voice 2 through the shared filter.", label: "V2_FILT", value: "bit 1" },
      { description: "Route voice 3 through the shared filter.", label: "V3_FILT", value: "bit 2" },
      { description: "Route voice 4 through the shared filter.", label: "V4_FILT", value: "bit 3" }
    ],
    id: "register-aud-filt-route",
    keywords: ["audio", "filter route", "voice mask", "per voice"],
    notes: [
      "Reset leaves the mask at 0x00, so the filter is bypassed until software explicitly routes voices into it.",
      "Bits 7..4 are reserved and read back as 0."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-fc", "register-aud-filt-ctrl", "register-aud-modevol"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Per-voice routing mask that selects which of the four voices feed the shared SVF path.",
    title: "AUD_FILT_ROUTE",
    width: "8-bit"
  },
  {
    access: "R",
    address: 0xd430,
    addressLabel: "$D430",
    aliases: ["aud_osc3_ro", "osc3", "osc3 random", "voice 3 osc", "voice 3 waveform"],
    bitFields: [],
    id: "register-aud-osc3-ro",
    keywords: ["audio", "osc3", "voice 3", "readback", "waveform", "sid-like", "blep", "bandlimited"],
    notes: [
      "This register is sample-latched and observes Voice 3 only.",
      "It returns the upper 8 bits of the resolved 12-bit Voice 3 waveform before envelope scaling, filter routing, master volume, and softclip.",
      "For SAW and PULSE, the returned 12-bit value is the readback-domain clamp of the BLEP-corrected centered waveform, not the naive edge-only oscillator state.",
      "SYNC, RING, and TEST affect this readback exactly as they affect the current Voice 3 oscillator state.",
      "When TEST is set, no waveform is selected, or a reserved multi-wave combination is active, the readback falls back to 0x00.",
      "Writes are ignored and reset reads as 0x00."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-voice-ctrl", "register-aud-env3-ro"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Read-only sample-latched Voice 3 oscillator tap using the upper 8 bits of the resolved waveform.",
    title: "AUD_OSC3_RO",
    width: "8-bit"
  },
  {
    access: "R",
    address: 0xd431,
    addressLabel: "$D431",
    aliases: ["aud_env3_ro", "env3", "voice 3 env", "voice 3 envelope"],
    bitFields: [],
    id: "register-aud-env3-ro",
    keywords: ["audio", "env3", "voice 3", "envelope", "readback", "adsr"],
    notes: [
      "This register is sample-latched and observes Voice 3 only.",
      "It returns the current 8-bit Voice 3 envelope level after the per-sample ADSR update for that emitted sample.",
      "Writes are ignored and reset reads as 0x00."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-voice-ctrl", "register-aud-osc3-ro"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Read-only sample-latched Voice 3 envelope level tap.",
    title: "AUD_ENV3_RO",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xd432,
    addressLabel: "$D432",
    aliases: [
      "aud_pitch_cfg",
      "audio pitch cfg",
      "audio pitch mode",
      "pitch mode",
      "coarse_16",
      "balanced_19",
      "fine_20",
      "legacy_16",
      "standard_19"
    ],
    bitFields: [
      {
        description: "Global pitch resolution: 00=COARSE_16, 01=BALANCED_19, 10=FINE_20, 11 sanitizes back to COARSE_16.",
        label: "PITCH_MODE",
        value: "bits 1..0"
      }
    ],
    id: "register-aud-pitch-cfg",
    keywords: [
      "audio",
      "pitch",
      "pitch mode",
      "phase increment",
      "coarse 16",
      "balanced 19",
      "fine 20",
      "legacy 16",
      "standard 19",
      "48000"
    ],
    notes: [
      "Reset selects BALANCED_19 and reads back as 0x01.",
      "Writes with PITCH_MODE=11 sanitize to COARSE_16 and read back as 0x00.",
      "Mode changes take effect on the next emitted audio sample and only change later phase increments; phase, gate, and envelope state stay untouched.",
      "At the current default pitch clock of 48000 Hz the shipped step sizes are about 0.732 Hz (COARSE_16), 0.0916 Hz (BALANCED_19), and 0.0458 Hz (FINE_20)."
    ],
    relatedIds: ["memory-range-audio-block", "register-aud-voice-ctrl", "register-aud-tick"],
    reset: "0x01",
    scope: "MMIO",
    summary: "Global audio pitch-mode register that freezes the supported phase-increment resolutions.",
    title: "AUD_PITCH_CFG",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xdc0d,
    addressLabel: "$DC0D",
    aliases: ["tic1_icr", "tic1 icr", "timer1 icr"],
    bitFields: [
      { description: "Timer A interrupt latch on read; mask selector on write.", label: "TA", value: "bit 0" },
      { description: "Timer B interrupt latch on read; mask selector on write.", label: "TB", value: "bit 1" },
      { description: "Mask-set / mask-clear selector on write and active-summary bit on read.", label: "CTRL", value: "bit 7" }
    ],
    id: "register-tic1-icr",
    keywords: ["timer irq", "tic1", "interrupt mask"],
    notes: [
      "Reads clear the timer interrupt flags that were observed.",
      "TIC1 routes to the machine IRQ line."
    ],
    relatedIds: ["register-tic1-cra", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Timer-chip 1 interrupt-control register for flags and masks.",
    title: "TIC1_ICR",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xdc0e,
    addressLabel: "$DC0E",
    aliases: ["tic1_cra", "tic1 cra", "timer1 control a"],
    bitFields: [
      { description: "Start / stop Timer A.", label: "START", value: "bit 0" },
      { description: "Run in one-shot mode.", label: "ONE_SHOT", value: "bit 3" },
      { description: "Load latch into counter as a strobe.", label: "LOAD", value: "bit 4" },
      { description: "Prescaler selection /1, /8, /64, /1024 with bit 6.", label: "PRESCALE", value: "bits 5..6" }
    ],
    id: "register-tic1-cra",
    keywords: ["timer control", "prescale"],
    notes: ["Use together with the Timer A latch registers at $DC04/$DC05 and TIC1_ICR."],
    relatedIds: ["register-tic1-icr", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Timer-chip 1 control register for Timer A start, one-shot, load, and prescaler selection.",
    title: "TIC1_CRA",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xdd0d,
    addressLabel: "$DD0D",
    aliases: ["tic2_icr", "tic2 icr", "timer2 icr"],
    bitFields: [
      { description: "Timer A interrupt latch on read; mask selector on write.", label: "TA", value: "bit 0" },
      { description: "Timer B interrupt latch on read; mask selector on write.", label: "TB", value: "bit 1" },
      { description: "Mask-set / mask-clear selector on write and active-summary bit on read.", label: "CTRL", value: "bit 7" }
    ],
    id: "register-tic2-icr",
    keywords: ["timer nmi", "tic2", "interrupt mask"],
    notes: [
      "Reads clear the timer interrupt flags that were observed.",
      "TIC2 routes to the machine NMI line."
    ],
    relatedIds: ["register-tic2-crb", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Timer-chip 2 interrupt-control register for flags and masks.",
    title: "TIC2_ICR",
    width: "8-bit"
  },
  {
    access: "R/W",
    address: 0xdd0f,
    addressLabel: "$DD0F",
    aliases: ["tic2_crb", "tic2 crb", "timer2 control b"],
    bitFields: [
      { description: "Start / stop Timer B.", label: "START", value: "bit 0" },
      { description: "Run in one-shot mode.", label: "ONE_SHOT", value: "bit 3" },
      { description: "Load latch into counter as a strobe.", label: "LOAD", value: "bit 4" },
      { description: "Prescaler / cascade selection with bit 6 controlling underflow cascade mode.", label: "MODE", value: "bits 5..6" }
    ],
    id: "register-tic2-crb",
    keywords: ["timer control", "cascade"],
    notes: ["Timer B can either use its own prescaler or count Timer A underflows in cascade mode."],
    relatedIds: ["register-tic2-icr", "memory-range-io-window"],
    reset: "0x00",
    scope: "MMIO",
    summary: "Timer-chip 2 control register for Timer B start, one-shot, load, and cascade behavior.",
    title: "TIC2_CRB",
    width: "8-bit"
  }
] as const;

export const MEMORY_RANGE_SEEDS = [
  {
    access: "R/W",
    aliases: ["page zero", "zp"],
    end: 0x00ff,
    id: "memory-range-zero-page",
    keywords: ["zero page", "fast addressing"],
    notes: [
      "Contains CPU-port controls at $0000/$0001 plus ROM scratch space in $0010..$004B.",
      "Zero-page indexed and pointer-based addressing modes make this area especially valuable."
    ],
    owner: "CPU and ROM workspace",
    relatedIds: ["addressing-mode-zp", "addressing-mode-zpx", "addressing-mode-zpy", "addressing-mode-indx", "addressing-mode-indy", "addressing-mode-indy-pre", "addressing-mode-indx-post", "register-bank-reg"],
    start: 0x0000,
    summary: "Fast-access low memory containing bank control, ROM workspace, and zero-page pointer tables.",
    title: "Zero Page"
  },
  {
    access: "R/W",
    aliases: ["hardware stack"],
    end: 0x01ff,
    id: "memory-range-stack",
    keywords: ["stack", "page 1"],
    notes: ["The fixed CPU stack lives here and is addressed through SP."],
    owner: "CPU stack",
    relatedIds: ["register-sp", "opcode-jsr", "opcode-rts", "opcode-rti", "opcode-pha", "opcode-pla", "opcode-php", "opcode-plp"],
    start: 0x0100,
    summary: "Fixed hardware stack page used by calls, returns, traps, and explicit push/pop instructions.",
    title: "Stack"
  },
  {
    access: "R/W",
    aliases: ["command buffer", "system buffers"],
    end: 0x03ff,
    id: "memory-range-system-buffers",
    keywords: ["buffers", "vectors", "line input"],
    notes: [
      "Includes the global line buffer at $0200..$027F and vector / dispatcher space at $0300..$033F.",
      "The currently active low-memory IRQ dispatcher hook lives at $0314/$0315 and is seeded by the system ROM during reset.",
      "Storage descriptors also live in this region at $0340..$035F."
    ],
    owner: "System and ROM buffers",
    relatedIds: ["memory-range-zero-page", "memory-range-ram-irq-vector"],
    start: 0x0200,
    summary: "System buffer and vector region shared by ROM services and higher-level tooling.",
    title: "System Buffers"
  },
  {
    access: "R/W",
    aliases: ["ram irq vector", "irq dispatch vector", "$0314", "$0315", "0x0314", "0x0315"],
    end: 0x0315,
    id: "memory-range-ram-irq-vector",
    keywords: ["low memory vector", "irq handler hook", "system rom", "irq entry", "vectors_init"],
    notes: [
      "RESET calls VECTORS_INIT, which seeds $0314/$0315 to the ROM's default IRQ_HANDLER.",
      "The top IRQ/BRK vector at $FFFE/$FFFF enters IRQ_ENTRY first, and IRQ_ENTRY immediately dispatches through JMP ($0314).",
      "Patching this pair retargets the live ROM IRQ dispatch path without rewriting the final six bytes of system ROM."
    ],
    owner: "System ROM IRQ dispatcher hook",
    relatedIds: ["interrupt-irq", "interrupt-brk", "interrupt-vectors", "memory-range-system-buffers", "memory-range-system-rom"],
    start: 0x0314,
    summary: "Low-memory RAM vector pair used as the live IRQ dispatcher hook by system ROM.",
    title: "RAM IRQ Vector Table"
  },
  {
    access: "R/W",
    aliases: ["screen ram", "text buffer"],
    end: 0x07e7,
    id: "memory-range-text-screen",
    keywords: ["text mode", "screen"],
    notes: ["Default text screen is 40x25 characters and can move when IOC_VCTRL or VOC_MEMPTR.SCREEN_OFF changes."],
    owner: "Video text plane",
    relatedIds: ["register-ioc-vctrl", "register-voc-memptr"],
    start: 0x0400,
    summary: "Default text-screen character buffer for 40x25 cells.",
    title: "Text Screen"
  },
  {
    access: "R/W",
    aliases: ["sprite ptr table"],
    end: 0x07ef,
    id: "memory-range-sprite-pointers",
    keywords: ["sprites", "pointer table"],
    notes: [
      "Eight bytes immediately after screen RAM select sprite image sources.",
      "The table stays screen-relative at screen_base+0x03E8, so moving SCREEN_OFF also moves the pointer table."
    ],
    owner: "Video sprite setup",
    relatedIds: ["register-ioc-vctrl", "register-voc-memptr", "memory-range-text-screen", "memory-range-graphics-plane"],
    start: 0x07e8,
    summary: "Sprite pointer table colocated after the text screen in the current text-page selection.",
    title: "Sprite Pointers"
  },
  {
    access: "R/W",
    aliases: ["color ram", "text colors"],
    end: 0x09f3,
    id: "memory-range-color-ram",
    keywords: ["foreground", "background", "palette index"],
    notes: [
      "Packed Color-RAM stores two 4-bit foreground colors per byte (low nibble = even cell, high nibble = odd cell).",
      "color_base is always screen_base+0x0400 relative to the active IOC-selected 16 KiB video bank rather than a separately banked plane.",
      "Text mode uses these nibbles as per-cell foreground colors; the background comes from VOC_BG_COLOR."
    ],
    owner: "Video text attributes",
    relatedIds: ["register-ioc-vctrl", "register-voc-memptr", "memory-range-text-screen"],
    start: 0x0800,
    summary: "Default packed text foreground-color plane with two cells per byte.",
    title: "Color RAM"
  },
  {
    access: "Char ROM in video banks 0 and 2, RAM in video banks 1 and 3",
    aliases: ["voc char window", "character window", "glyph window"],
    end: 0x1fff,
    id: "memory-range-voc-char-window",
    keywords: ["character rom", "glyph fetch", "bank-local window", "text mode"],
    notes: [
      "This is a VOC-local window, not the CPU's $D000 overlay window.",
      "Video banks 0 and 2 read character ROM here; video banks 1 and 3 read RAM instead.",
      "VOC_MEMPTR.CHAR_OFF selects 0x0400-step starts inside this 4 KiB window."
    ],
    owner: "VOC glyph source window",
    relatedIds: ["register-ioc-vctrl", "register-voc-memptr", "register-voc-ctrl2", "memory-range-io-window"],
    start: 0x1000,
    summary: "Bank-local 4 KiB VOC char window used for text glyph fetches.",
    title: "VOC Char Window"
  },
  {
    access: "R/W",
    aliases: ["program store", "tokenized program area", "$0b00", "0x0b00", "store owner", "active mode"],
    end: 0x1fff,
    id: "memory-range-language-program",
    keywords: ["basic", "forth", "assembler", "program storage", "editor state", "packed mode byte"],
    notes: [
      "Packed language metadata lives at $0B00: the low nibble is the active mode and the high nibble is the token-store owner.",
      "Tokenized program records still start at $0B01.",
      "EXIT clears only the active-mode nibble, so re-entering the same language keeps the store while switching languages resets it."
    ],
    owner: "Language program storage",
    relatedIds: [],
    start: 0x0b00,
    summary: "Shared program-storage area for tokenized language sources plus packed active-mode and store-owner metadata at $0B00.",
    title: "Language Program Store"
  },
  {
    access: "R/W in VOC-II bitmap modes",
    aliases: ["bitmap plane", "gfx"],
    end: 0x3f3f,
    id: "memory-range-graphics-plane",
    keywords: ["bitmap", "graphics mode"],
    notes: [
      "Used by all productive VOC-II bitmap modes: Mode A, Mode B, and Mode C.",
      "The base can move to $2000, $3000, $4000, or $5000 relative to the active IOC-selected 16 KiB video bank base through VOC_MEMPTR.GFX_OFF, with an active bitmap footprint of 8000 bytes.",
      "Active sprite rows use bank_base+(PTR<<6), so moving the graphics plane does not move active sprite image data."
    ],
    owner: "Video graphics plane",
    relatedIds: ["register-ioc-vctrl", "register-voc-ctrl1", "register-voc-memptr", "memory-range-sprite-pointers"],
    start: 0x2000,
    summary: "Default graphics / bitmap plane used by the video system in bitmap mode.",
    title: "Graphics Plane"
  },
  {
    access: "R/W or module-owned",
    aliases: ["language slot", "module rom"],
    end: 0xbfff,
    id: "memory-range-language-slot",
    keywords: ["module", "forth", "assembler runtime"],
    notes: [
      "Shared language-module slot reused by FORTH and assembler runtimes.",
      "The visible ABI header and public stubs live at 0x9000..0x9FFF.",
      "The larger module body may continue into banked RAM under 0xA000..0xBFFF while lang.rom remains the default upper-ROM image."
    ],
    owner: "Language module slot",
    relatedIds: [],
    start: 0x9000,
    summary: "Shared runtime slot for optional language modules, with visible ABI stubs below the upper-ROM overlap.",
    title: "Language Module Slot"
  },
  {
    access: "R/W when FORTH active",
    aliases: ["forth runtime"],
    end: 0x8fff,
    id: "memory-range-forth-window",
    keywords: ["forth", "dictionary", "stacks"],
    notes: ["Reserved for the FORTH dictionary and data/return stacks while FORTH is active."],
    owner: "FORTH runtime",
    relatedIds: [],
    start: 0x7000,
    summary: "Reserved FORTH runtime window for dictionary and stack storage when FORTH owns the machine.",
    title: "FORTH Runtime Window"
  },
  {
    access: "ROM when banked in, RAM underneath",
    aliases: ["lang rom", "upper rom"],
    end: 0xbfff,
    id: "memory-range-upper-rom",
    keywords: ["rom overlay", "user rom", "lang rom"],
    notes: [
      "Visible when LORAM=1 and HIRAM=1; otherwise the CPU reads underlying RAM.",
      "Even while this ROM is visible, CPU writes still land in the underlying RAM because the write decoder does not target ROM."
    ],
    owner: "Upper ROM window",
    relatedIds: ["register-bank-reg"],
    start: 0xa000,
    summary: "Banked upper-ROM window for the default language / monitor layer.",
    title: "Upper ROM Window"
  },
  {
    access: "R/W",
    aliases: ["preferred code area", "c000"],
    end: 0xcfff,
    id: "memory-range-code-ram",
    keywords: ["code staging", "entry point"],
    notes: ["The memory contract names $C000 as the preferred user-code entry point."],
    owner: "General RAM",
    relatedIds: [],
    start: 0xc000,
    summary: "General-purpose RAM often used as a convenient code-entry and staging area.",
    title: "Code RAM"
  },
  {
    access: "I/O or char ROM depending on BANK_REG",
    aliases: ["io window", "char rom", "$d000"],
    end: 0xdfff,
    id: "memory-range-io-window",
    keywords: ["mmio", "char rom", "video registers", "timers", "open bus", "board decode"],
    notes: [
      "When CHAREN=1 and ROM banking is active, the CPU sees I/O here.",
      "When CHAREN=0 and LORAM or HIRAM is set, the CPU sees the character ROM instead.",
      "When the character ROM is visible, CPU writes still land in the underlying RAM; only the active I/O overlay switches writes away from RAM.",
      "The fixed board decoder only routes the documented subranges here: video/palette, serial, BOARD_CFG0, IOC, storage, audio, TIC1, and TIC2.",
      "Addresses in this window that are not assigned to one of those subranges are explicit no-target holes: CPU reads/peeks return $00 and writes are ignored.",
      "VOC text glyph fetches no longer use this CPU overlay window directly; they use the VOC-local char window selected by IOC_VCTRL and VOC_MEMPTR.",
      "The board-level console-routing register at $D044 also lives in this window alongside the serial and IOC blocks."
    ],
    owner: "Fixed I/O and character-ROM overlay",
    relatedIds: [
      "register-bank-reg",
      "memory-range-audio-block",
      "memory-range-storage-block",
      "register-aud-filt-ctrl",
      "register-board-cfg0",
      "register-ioc-vctrl",
      "register-stg-cmd",
      "register-stg-status",
      "register-stg-data",
      "register-stg-unit",
      "register-voc-ctrl1",
      "register-voc-raster",
      "register-voc-memptr",
      "register-ser-data",
      "register-ioc-kbd-data",
      "register-tic1-icr",
      "register-tic2-icr"
    ],
    start: 0xd000,
    summary: "Shared I/O / character-ROM window with fixed board subdecode plus explicit open-bus holes.",
    title: "I/O and Char ROM Window"
  },
  {
    access: "MMIO",
    aliases: ["storage block", "disk controller", "storage mmio", "$d060"],
    end: 0xd06f,
    id: "memory-range-storage-block",
    keywords: ["storage", "disk", "controller", "descriptor", "file level", "drive"],
    notes: [
      "This 16-byte window is the guest-visible storage-controller frontend for file-level disk operations.",
      "The command vocabulary and descriptor contract stay file-level and IEC-inspired even when the current runtime routes requests through a controller/link/service split behind the block.",
      "STG_DATA carries descriptor bytes, rename helper names, and LOAD/SAVE payload streaming; large transfers are chunked internally at 256 bytes.",
      "The host/runtime side currently acts as the intelligent drive service for the default software path."
    ],
    owner: "Storage controller frontend",
    relatedIds: ["memory-range-io-window", "register-stg-cmd", "register-stg-ctrl", "register-stg-status", "register-stg-data", "register-stg-unit", "public-api-disk-load", "public-api-disk-save"],
    start: 0xd060,
    summary: "Guest-visible storage-controller MMIO block for file-level disk commands and streamed descriptor/payload I/O.",
    title: "Storage Controller Block"
  },
  {
    access: "MMIO",
    aliases: ["audio block", "sid-like audio", "$d400", "filter block", "svf", "hard sync", "ring modulation", "blep", "bandlimited", "pitch mode"],
    end: 0xd43f,
    id: "memory-range-audio-block",
    keywords: ["audio", "adsr", "filter", "softclip", "48 khz", "voice stride", "rate_x8", "sync", "ring", "blep", "bandlimited", "pitch mode"],
    notes: [
      "The four voice blocks live at $D400..$D41F with the fixed stride FREQ_LO, FREQ_HI, PW_LO, PW_HI, CTRL, AD, SR, and AUX_RSVD.",
      "Within each CTRL register, bit1 is SYNC and bit2 is RING, both using the fixed predecessor chain V1<-V4, V2<-V1, V3<-V2, V4<-V3.",
      "SYNC resets the target phase on a predecessor wrap, while RING only changes TRI output and stays inert for SAW, PULSE, and NOISE.",
      "SAW and PULSE now use one fixed 48 kHz, 256x16 BLEP bandlimited lookup table with 16 bounded residual taps per voice so the shipped baseline stays deterministic and hardware-plausible.",
      "TEST, silent phase resets, same-sample hard-sync resets, and waveform-select changes clear any pending saw/pulse BLEP tail before waveform generation continues.",
      "AUX_RSVD bit 0 is RATE_X8, which extends the resolved ADSR timing for that voice without changing the default nibble meanings.",
      "Global registers at $D420..$D429 provide master volume, status, IRQ/tick control, the shared filter cutoff, SVF resonance/mode, and per-voice routing into the filter path.",
      "The extension window now assigns AUD_OSC3_RO at $D430, AUD_ENV3_RO at $D431, and AUD_PITCH_CFG at $D432; only $D433..$D43F stay reserved in the current model.",
      "At the default 1 MHz machine clock the audio scheduler emits exactly 48 kHz samples, then applies the shared SVF to routed voices and a fixed master softclip to the final mixed output."
    ],
    owner: "Audio synthesizer and shared filter",
    relatedIds: [
      "memory-range-io-window",
      "register-aud-voice-ctrl",
      "register-aud-modevol",
      "register-aud-status",
      "register-aud-irq-en",
      "register-aud-irq-ifr",
      "register-aud-tick",
      "register-aud-fc",
      "register-aud-filt-ctrl",
      "register-aud-filt-route",
      "register-aud-osc3-ro",
      "register-aud-env3-ro",
      "register-aud-pitch-cfg"
    ],
    start: 0xd400,
    summary: "Shared four-voice audio MMIO block with per-voice ADSR state, a routed SVF path, and fixed master softclip.",
    title: "Audio Block"
  },
  {
    access: "ROM when banked in, RAM underneath",
    aliases: ["system rom", "kernal-like rom", "vectors"],
    end: 0xffff,
    id: "memory-range-system-rom",
    keywords: ["system rom", "reset vector", "irq vector", "nmi vector"],
    notes: [
      "Visible when HIRAM=1; otherwise the CPU sees underlying RAM.",
      "Even while this ROM is visible, CPU writes still land in the underlying RAM because the write decoder does not target ROM.",
      "Contains the reset, NMI, and IRQ vectors at the top of the address space.",
      "The current build stores RESET in the NMI and RESET slots, and IRQ_ENTRY in the IRQ/BRK slot before IRQ dispatch continues through $0314/$0315."
    ],
    owner: "System ROM window",
    relatedIds: ["register-bank-reg", "register-pc", "opcode-brk", "memory-range-ram-irq-vector"],
    start: 0xe000,
    summary: "Banked system-ROM window that also contains the machine's top-of-memory vectors.",
    title: "System ROM Window"
  }
] as const;

export const ASSEMBLER_GUIDE_SEEDS = [
  {
    aliases: [".org", "origin directive", "program counter"],
    examples: [".org $C000"],
    id: "assembler-guide-directive-org",
    keywords: ["base address", "origin", "address control"],
    notes: [
      "Sets the current assembly address for all following output.",
      "The browser workbench derives assembled output addresses from in-source .org directives instead of a separate base-address control."
    ],
    relatedIds: ["memory-range-code-ram"],
    source: "Shared core directive",
    summary: "Sets the current assembly origin for subsequent bytes and instructions.",
    syntax: ".org expr",
    title: ".org"
  },
  {
    aliases: [".byte", "byte directive", "raw bytes"],
    examples: [".byte $01, $02, 3"],
    id: "assembler-guide-directive-byte",
    keywords: ["data", "bytes", "literals"],
    notes: ["Writes one byte per operand after expression evaluation."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Emits raw byte values into the output stream.",
    syntax: ".byte expr[, expr ...]",
    title: ".byte"
  },
  {
    aliases: [".word", "word directive", "16-bit literal"],
    examples: [".word START, $1234"],
    id: "assembler-guide-directive-word",
    keywords: ["data", "16-bit", "little endian"],
    notes: ["Emits 16-bit values in little-endian order."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Emits one little-endian 16-bit word per operand.",
    syntax: ".word expr[, expr ...]",
    title: ".word"
  },
  {
    aliases: [".fill", "fill directive"],
    examples: [".fill 64, $FF"],
    id: "assembler-guide-directive-fill",
    keywords: ["repeat", "padding", "bulk bytes"],
    notes: ["Writes count bytes using the same evaluated fill value each time."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Repeats one byte value for a requested number of output bytes.",
    syntax: ".fill count, value",
    title: ".fill"
  },
  {
    aliases: [".res", "reserve directive"],
    examples: [".res 32"],
    id: "assembler-guide-directive-res",
    keywords: ["reserve", "padding", "space"],
    notes: ["Advances the program counter by count bytes without requiring explicit literal operands."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Reserves a number of bytes and leaves them on the active fill pattern.",
    syntax: ".res count",
    title: ".res"
  },
  {
    aliases: [".align", "alignment directive"],
    examples: [".align 16", ".align 256, $FF"],
    id: "assembler-guide-directive-align",
    keywords: ["alignment", "padding", "boundary"],
    notes: ["Pads until the current assembly address is a multiple of the requested alignment."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Aligns the current output address to an N-byte boundary.",
    syntax: ".align n[, fill]",
    title: ".align"
  },
  {
    aliases: [".ascii", "raw string"],
    examples: ['.ascii "HELLO"'],
    id: "assembler-guide-directive-ascii",
    keywords: ["string", "raw bytes", "text"],
    notes: ["Writes decoded string-literal bytes unchanged and appends no terminator."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Emits raw string-literal bytes without any terminator.",
    syntax: '.ascii "..."[, "..." ...]',
    title: ".ascii"
  },
  {
    aliases: [".asciiz", "nul-terminated string"],
    examples: ['.asciiz "READY."'],
    id: "assembler-guide-directive-asciiz",
    keywords: ["string", "nul", "terminator"],
    notes: ["Appends exactly one 0x00 terminator after each string literal argument."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Emits raw string-literal bytes and adds a 0x00 terminator per string.",
    syntax: '.asciiz "..."[, "..." ...]',
    title: ".asciiz"
  },
  {
    aliases: [".petscii", "petscii string"],
    examples: ['.petscii "a"'],
    id: "assembler-guide-directive-petscii",
    keywords: ["string", "petscii", "text mapping"],
    notes: ["Uses the shared source-literal-to-PETSCII mapping from the tooling text-encoding spec."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Emits PETSCII bytes through the shared text-encoding mapping.",
    syntax: '.petscii "..."[, "..." ...]',
    title: ".petscii"
  },
  {
    aliases: [".petsciiz", "nul-terminated petscii"],
    examples: ['.petsciiz "LOAD"'],
    id: "assembler-guide-directive-petsciiz",
    keywords: ["string", "petscii", "nul"],
    notes: ["Adds one 0x00 terminator after each mapped PETSCII string."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Emits PETSCII strings with a 0x00 terminator after each argument.",
    syntax: '.petsciiz "..."[, "..." ...]',
    title: ".petsciiz"
  },
  {
    aliases: [".screen", "screen-code string"],
    examples: ['.screen "HELLO"'],
    id: "assembler-guide-directive-screen",
    keywords: ["screen code", "text screen", "voc text"],
    notes: [
      "Maps source text through the shared source-literal-to-PETSCII mapping and then through the shared PETSCII-to-screen mapping.",
      "Useful when the ROM API expects raw screen codes instead of PETSCII guest-text bytes."
    ],
    relatedIds: ["public-api-put-char-at"],
    source: "Shared core directive",
    summary: "Emits raw text-screen codes through the shared screen-code mapping.",
    syntax: '.screen "..."[, "..." ...]',
    title: ".screen"
  },
  {
    aliases: [".screenz", "nul-terminated screen-code string"],
    examples: ['.screenz "READY."'],
    id: "assembler-guide-directive-screenz",
    keywords: ["screen code", "nul", "text screen"],
    notes: [
      "Writes screen-code bytes using the shared mapping and appends 0x00 after each string.",
      "This is the convenience directive for APIs such as API_PRINT_AT that expect NUL-terminated screen-code strings."
    ],
    relatedIds: ["public-api-print-at"],
    source: "Shared core directive",
    summary: "Emits screen-code strings and appends a 0x00 terminator after each argument.",
    syntax: '.screenz "..."[, "..." ...]',
    title: ".screenz"
  },
  {
    aliases: [".assert", "build assertion"],
    examples: ['.assert SIZE < $2000, "ROM too large"'],
    id: "assembler-guide-directive-assert",
    keywords: ["assertion", "build failure", "validation"],
    notes: ["Assembly stops with a line-aware diagnostic when the expression evaluates to zero."],
    relatedIds: [],
    source: "Shared core directive",
    summary: "Checks an expression during assembly and aborts with a diagnostic on failure.",
    syntax: '.assert expr[, "msg"]',
    title: ".assert"
  },
  {
    aliases: [".incbin", "include binary"],
    examples: ['.incbin "blob.bin", 0, 256'],
    id: "assembler-guide-directive-incbin",
    keywords: ["file include", "binary blob", "native cli"],
    notes: [
      "Includes raw bytes from a file relative to the current assembly file.",
      "Supported by the native/Python assembler path but intentionally outside the browser workbench subset."
    ],
    relatedIds: [],
    source: "Native/CLI-only directive",
    summary: "Embeds bytes from an external file into the assembled output.",
    syntax: '.incbin "file"[, offset[, len]]',
    title: ".incbin"
  },
  {
    aliases: ["global label", "START:", "loop:"],
    examples: ["START:", "JMP START"],
    id: "assembler-guide-label-global",
    keywords: ["labels", "symbol", "scope"],
    notes: ["A global label opens a new named scope for following named local labels."],
    relatedIds: ["assembler-guide-local-named", "assembler-guide-local-numeric"],
    source: "Shared core syntax",
    summary: "Defines a globally visible symbol that can be referenced anywhere after parsing.",
    syntax: "LABEL:",
    title: "Global Labels"
  },
  {
    aliases: ["@label", "named local label", "@loop"],
    examples: ["@loop:", "BNE @loop"],
    id: "assembler-guide-local-named",
    keywords: ["labels", "local scope", "named locals"],
    notes: ["Named local labels live inside the current global-label scope and may be reused under another global label."],
    relatedIds: ["assembler-guide-label-global"],
    source: "Shared core syntax",
    summary: "Defines a named local label using the @prefix within the current global scope.",
    syntax: "@name:   /   BNE @name",
    title: "Named Local Labels"
  },
  {
    aliases: ["1:", "1f", "1b", "numeric local label"],
    examples: ["1:", "BNE 1b", "JMP 1f"],
    id: "assembler-guide-local-numeric",
    keywords: ["labels", "forward reference", "backward reference"],
    notes: ["Use `1b` to resolve the nearest previous `1:` and `1f` to resolve the nearest following `1:`."],
    relatedIds: ["assembler-guide-label-global"],
    source: "Shared core syntax",
    summary: "Defines numeric local labels with explicit backward and forward references.",
    syntax: "1:   /   1b   /   1f",
    title: "Numeric Local Labels"
  },
  {
    aliases: ["constant", "NAME = expr"],
    examples: ["PTR = $1234", "LDA #lo(PTR)"],
    id: "assembler-guide-constants",
    keywords: ["symbol", "constant", "equate"],
    notes: ["Constants evaluate expressions once and can be reused in operands, directives, and assertions."],
    relatedIds: ["assembler-guide-helper-lo", "assembler-guide-helper-hi"],
    source: "Shared core syntax",
    summary: "Defines reusable symbolic constants from expressions.",
    syntax: "NAME = expr",
    title: "Constants"
  },
  {
    aliases: ["lo", "low byte helper"],
    examples: ["LDA #lo(PTR)"],
    id: "assembler-guide-helper-lo",
    keywords: ["expression helper", "low byte"],
    notes: ["Evaluates to expr & 0xFF."],
    relatedIds: ["assembler-guide-helper-hi", "assembler-guide-constants"],
    source: "Shared core expression helper",
    summary: "Extracts the low byte of an expression result.",
    syntax: "lo(expr)",
    title: "lo()"
  },
  {
    aliases: ["hi", "high byte helper"],
    examples: ["LDX #hi(PTR)"],
    id: "assembler-guide-helper-hi",
    keywords: ["expression helper", "high byte"],
    notes: ["Evaluates to (expr >> 8) & 0xFF."],
    relatedIds: ["assembler-guide-helper-lo", "assembler-guide-constants"],
    source: "Shared core expression helper",
    summary: "Extracts the high byte of an expression result.",
    syntax: "hi(expr)",
    title: "hi()"
  },
  {
    aliases: ["listing prefix", "address-prefixed listing", "listed bytes"],
    examples: ["E01E: 11 07 LDA #$07"],
    id: "assembler-guide-listing-prefixes",
    keywords: ["listing", "roundtrip", "verify listed bytes"],
    notes: [
      "The assembler understands address/byte prefixes in ROM-style listings.",
      "When bytes are present they become the authoritative listing bytes for strict verification and roundtrip workflows."
    ],
    relatedIds: [],
    source: "Assembler CLI and native core",
    summary: "Supports address-prefixed ROM listings with optional listed-byte verification.",
    syntax: "ADDR: BYTES ASM",
    title: "Listing Prefixes"
  }
] as const;

export const BASIC_GUIDE_SEEDS = [
  {
    aliases: ["print statement", "print zones", "comma join", "semicolon chaining"],
    examples: ["PRINT A:B:C", "PRINT 1::2", 'PRINT "X"," Y"'],
    id: "basic-guide-print",
    keywords: ["output", "comma", "colon", "zones", "concatenation"],
    notes: [
      "Colon-separated items advance to the next 10-column print zone, so PRINT A:B:C lays values out in simple columns.",
      "Repeated colons skip multiple zones without needing empty string placeholders, so PRINT 1::2 or PRINT ::2 can jump farther right in one statement.",
      "If a zone advance would move past the last print zone, BASIC starts a fresh output line instead of squeezing more columns into the current one.",
      "Comma-separated items concatenate directly inside one PRINT statement, so any desired spacing should live in string literals such as PRINT \"X=\",A,\" Y=\",B.",
      "The semicolon is not a PRINT formatting modifier in the current dialect; it is the global BASIC statement separator used for same-line chaining.",
      "The same PRINT rules apply in immediate mode and while RUN executes stored lines."
    ],
    relatedIds: ["shell-command-basic", "public-api-basic-exec-immediate", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Prints expressions or strings, using : for 10-column zones and , for direct item concatenation.",
    syntax: "PRINT <item>[(:|,)<item>...]",
    title: "PRINT"
  },
  {
    aliases: ["let", "assignment", "implicit assignment", "lvalue"],
    examples: ["LET A=6*7+2", "A=@(2)+3"],
    id: "basic-guide-let-assignment",
    keywords: ["variables", "store expression", "array lvalue", "equals"],
    notes: [
      "LET is optional, so plain <lvalue>=<expr> uses the same assignment path as explicit LET.",
      "Single-letter variables A..Z and anonymous array cells @(index) are the writable lvalues in the current BASIC surface.",
      "The right-hand side uses the same integer expression rules documented under Expressions and Arrays."
    ],
    relatedIds: ["basic-guide-expressions", "shell-command-basic", "public-api-basic-exec-immediate", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Assigns integer expression results into variables or anonymous array cells, with or without the LET keyword.",
    syntax: "LET <lvalue>=<expr> | <lvalue>=<expr>",
    title: "LET and Assignment"
  },
  {
    aliases: ["input statement", "prompt string", "read values"],
    examples: ["INPUT A,B", 'INPUT "WERT: ",A'],
    id: "basic-guide-input",
    keywords: ["prompt", "comma-separated vars", "read numbers", "keyboard input"],
    notes: [
      "INPUT accepts one or more comma-separated lvalues in the current variable / @(index) surface.",
      "An optional prompt string may appear before the first lvalue and is followed by a comma.",
      "The resident runtime parses signed decimal and $-prefixed hexadecimal input into the current integer BASIC dialect.",
      "INPUT remains numeric in this revision, so the accepted targets are numeric variables or @(index) cells rather than general strings."
    ],
    relatedIds: ["basic-guide-let-assignment", "basic-guide-expressions", "shell-command-basic", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Reads one or more numeric values into BASIC lvalues, with optional inline prompt text.",
    syntax: 'INPUT <lvalue>[,<lvalue>...] | INPUT "prompt", <lvalue>[,<lvalue>...]',
    title: "INPUT"
  },
  {
    aliases: ["if then else", "else branch", "then"],
    examples: ["IF A=0 THEN POKE $5000,1 ELSE POKE $5000,2", "IF A=0 THEN 100 ELSE 200"],
    id: "basic-guide-if-else",
    keywords: ["branch", "condition", "inline command", "not equal"],
    notes: [
      "The resident parser accepts command-style IF with or without THEN before the taken command sequence.",
      "ELSE selects the alternative inline command sequence when the condition evaluates to 0.",
      "A THEN or ELSE branch may also be a target expression, which acts as shorthand for an implied GOTO target.",
      "Conditions use the current integer expression surface, including # as not-equal."
    ],
    relatedIds: ["basic-guide-expressions", "shell-command-basic", "public-api-basic-exec-immediate", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Branches between inline command sequences or shorthand THEN/ELSE target jumps.",
    syntax: "IF <expr> [THEN] <command...|target-expr> [ELSE <command...|target-expr>]",
    title: "IF / ELSE"
  },
  {
    aliases: ["on goto", "on gosub", "dispatch table"],
    examples: ["ON A GOTO 40,60,80", "ON B GOSUB 120,140"],
    id: "basic-guide-on-goto-gosub",
    keywords: ["branch table", "selector", "computed goto", "computed gosub"],
    notes: [
      "The evaluated expression is a 1-based selector over the comma-separated target list.",
      "A selector of 0 or a value beyond the last target falls through to the next statement without branching.",
      "Selected GOSUB targets use the normal RETURN stack path."
    ],
    relatedIds: ["basic-guide-expressions", "shell-command-run", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Dispatches to one of several line targets through a 1-based computed GOTO or GOSUB.",
    syntax: "ON <expr> GOTO <line>[,<line>...] | ON <expr> GOSUB <line>[,<line>...]",
    title: "ON ... GOTO / GOSUB"
  },
  {
    aliases: ["goto", "gosub", "return", "subroutine"],
    examples: ["GOTO A+20", "GOSUB 120", "RETURN"],
    id: "basic-guide-goto-gosub-return",
    keywords: ["jump", "call", "resume", "target expression"],
    notes: [
      "GOTO transfers control to the resolved target line, while GOSUB also pushes a return point for RETURN.",
      "RETURN resumes execution after the most recent active GOSUB site.",
      "Use the separate ON ... GOTO / GOSUB entry for selector-based multi-target dispatch."
    ],
    relatedIds: ["basic-guide-on-goto-gosub", "basic-guide-expressions", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Jumps to program lines directly or through subroutine calls, then resumes with RETURN.",
    syntax: "GOTO <expr> | GOSUB <expr> | RETURN",
    title: "GOTO / GOSUB / RETURN"
  },
  {
    aliases: ["for", "next", "step", "to", "loop"],
    examples: ["FOR I=1 TO 5 STEP 2", "NEXT I", "NEXT"],
    id: "basic-guide-for-next",
    keywords: ["counted loop", "optional step", "negative step", "loop variable"],
    notes: [
      "FOR initializes the loop variable, target bound, and optional STEP expression before the loop body runs.",
      "STEP is optional; omitting it keeps the default increment of 1, while negative STEP values drive countdown loops.",
      "NEXT may name the loop variable explicitly or omit it to continue the current innermost FOR frame."
    ],
    relatedIds: ["basic-guide-let-assignment", "basic-guide-expressions", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Runs counted loops with TO / STEP bounds and closes them with NEXT.",
    syntax: "FOR <var>=<expr> TO <expr> [STEP <expr>] ... NEXT [var]",
    title: "FOR / NEXT"
  },
  {
    aliases: ["poke statement", "memory write", "byte store"],
    examples: ["POKE $5000,A", "POKE 20480,42"],
    id: "basic-guide-poke",
    keywords: ["address expression", "value expression", "mmio", "ram write"],
    notes: [
      "POKE evaluates both arguments as integer expressions, then stores one byte to the resolved address.",
      "This BASIC statement is distinct from the shell-level POKE command even though both write memory.",
      "It is commonly used from immediate mode and stored RUN programs to touch RAM or MMIO state."
    ],
    relatedIds: ["basic-guide-expressions", "shell-command-poke", "memory-range-io-window", "public-api-basic-exec-immediate", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Writes one byte to the address resolved from BASIC expressions.",
    syntax: "POKE <expr>,<expr>",
    title: "POKE"
  },
  {
    aliases: ["data statements", "read statement", "restore statement"],
    examples: ["10 DATA 7,9", "20 READ A,B", "30 RESTORE"],
    id: "basic-guide-data-read-restore",
    keywords: ["numeric data", "data cursor", "tables", "read multiple values"],
    notes: [
      "DATA items are numeric-only in the current BASIC revision.",
      "READ can fill multiple variables in one statement and can continue across multiple DATA lines.",
      "RESTORE rewinds the DATA cursor to the first item and does not accept a line-number form in this revision."
    ],
    relatedIds: ["basic-guide-print", "shell-command-run", "public-api-run-basic-program"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Stores numeric inline data, reads it back through variables, and rewinds with RESTORE.",
    syntax: "DATA <num>[,<num>...] | READ <var>[,<var>...] | RESTORE",
    title: "DATA / READ / RESTORE"
  },
  {
    aliases: ["rem", "new", "end", "stop", "comment"],
    examples: ["REM IGNORE THIS", "NEW", "END", "STOP"],
    id: "basic-guide-comments-lifecycle",
    keywords: ["comment tail", "clear program", "finish run", "halt program"],
    notes: [
      "REM turns the rest of the current line into comment text, so later semicolon-chained source on that line is ignored.",
      "NEW clears the stored BASIC program and resets the current RUN target state.",
      "END finishes the current program normally, while STOP halts execution before later lines run."
    ],
    relatedIds: ["basic-guide-immediate-chaining", "shell-command-run", "public-api-run-basic-program", "public-api-basic-reset-state"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Covers BASIC comments plus the statements that clear or terminate the current program run.",
    syntax: "REM <text> | NEW | END | STOP",
    title: "Comments and Program Lifecycle"
  },
  {
    aliases: ["immediate mode", "direct mode", "statement separator", "semicolon chaining", "numbered lines"],
    examples: ["LET A=5;POKE $5000,A", "10 LET A=1;LET A=2"],
    id: "basic-guide-immediate-chaining",
    keywords: ["unnumbered input", "stored line", "direct execute", "semicolon"],
    notes: [
      "In BASIC mode, unnumbered non-command input executes immediately through the resident dispatcher.",
      "Numbered input stores or replaces a program line in the shared tokenized BASIC program area.",
      "The ; separator chains multiple BASIC statements both in immediate input and in stored lines during RUN."
    ],
    relatedIds: ["basic-guide-let-assignment", "basic-guide-comments-lifecycle", "shell-command-basic", "shell-command-line", "shell-command-run", "public-api-basic-exec-immediate", "public-api-edit-numbered-line"],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Explains the direct-execution path for unnumbered input and same-line semicolon chaining.",
    syntax: "<immediate command...>[;<command...>] | <line> <stored command...>[;<command...>]",
    title: "Immediate Execution and Chaining"
  },
  {
    aliases: ["mod", "and", "or", "xor", "not", "@()", "anonymous array"],
    examples: ["LET A=13 MOD 5", "LET B=6 XOR 3", "LET @(31)=42"],
    id: "basic-guide-expressions",
    keywords: ["integer expression", "array cells", "hex literals", "peek", "usr", "rnd", "size"],
    notes: [
      "Expressions use integer variables A..Z plus 32 fixed anonymous array cells @(0..31).",
      "Unary +, unary -, and NOT combine with binary + - * / MOD AND OR XOR in the current integer-only dialect.",
      "Literals may be decimal or $-prefixed hexadecimal, parentheses group subexpressions, and comparisons include =, <, >, <=, >=, and # as not-equal.",
      "Expression evaluation and comparisons use signed 16-bit two's-complement semantics across immediate execution and stored RUN code.",
      "PEEK(), USR(), ABS(), RND(expr), and SIZE are available as expression terms; RND(n) returns 1..n for positive n, reseeds from abs(n) when n is negative, and rejects 0.",
      "SIZE is a zero-argument expression term rather than a parenthesized function call."
    ],
    relatedIds: [
      "basic-guide-let-assignment",
      "basic-guide-if-else",
      "basic-guide-input",
      "basic-guide-on-goto-gosub",
      "basic-guide-goto-gosub-return",
      "basic-guide-for-next",
      "basic-guide-poke",
      "shell-command-basic",
      "public-api-basic-exec-immediate",
      "public-api-run-basic-program"
    ],
    source: "Resident lang.rom Tiny BASIC",
    summary: "Covers the current signed-16-bit integer expression surface, including bitwise operators and the expanded @() array range.",
    syntax: "<expr> := A..Z | @(0..31) | unary +/-/NOT | + - * / MOD AND OR XOR | PEEK()/USR()/ABS()/RND(expr)/SIZE",
    title: "Expressions and Arrays"
  }
] as const;

export const DEBUGGER_GUIDE_SEEDS = [
  {
    aliases: ["debugger", "breakpoint dsl", "watchpoint dsl", "tracepoint dsl", "debugger syntax"],
    examples: [
      "$E884",
      "$E884 and z == 0",
      "pc == $E884 and n == 1",
      "on write mem[$1234]",
      "on access mem[$D051] and pc == $E884",
      "trace rule: match on write mem[$1234], log pc, a"
    ],
    id: "debugger-guide-breakpoints-watchpoints",
    keywords: [
      "breakpoint",
      "watchpoint",
      "trace",
      "trace+stop",
      "pc shorthand",
      "mem",
      "on read",
      "on write",
      "on access",
      "changed",
      "rising"
    ],
    notes: [
      "A bare 16-bit address such as $E884 is shorthand for pc == $E884, and the same shorthand also works inside boolean expressions such as $E884 and z == 0.",
      "Condition and trace expressions can read CPU context through pc, cycle, frame, a, x, y, sp, p, n, v, i, z, c, irq, nmi, fetch, and halted.",
      "The current expression surface includes and/or/not, == != < <= > >=, + - * / %, bitwise & | ^ << >>, plus parentheses.",
      "Use mem[expr] for side-effect-free memory reads inside conditions. There is intentionally no implicit memory shorthand, so $D051 still means a PC shorthand term instead of mem[$D051].",
      "changed(expr) and rising(expr) keep per-rule edge state inside the debugger bridge and are intended for condition breakpoints rather than watchpoint prefixes.",
      "Watchpoints use on read mem[$ADDR], on write mem[$ADDR], or on access mem[$ADDR]. They observe CPU bus accesses only and may add a trailing and <condition> filter evaluated against the matching access context.",
      "The browser Trace and Trace+Stop modes reuse the same match DSL, add a separate log-fields list such as pc, a, x, y or mem[$D020], and sample trace emission through Every Nth hit plus Max events."
    ],
    relatedIds: ["register-p", "register-voc-irq-ifr", "memory-range-io-window"],
    source: "Browser workbench debugger",
    summary: "Documents the browser debugger match DSL for break, trace, and watch rules, including PC shorthand and explicit mem[] reads.",
    syntax: "match expr | on read|write|access mem[$ADDR] [and expr], plus trace log fields expr[, expr...]",
    title: "Breakpoints, Traces, and Watchpoints"
  }
] as const;

export const TRACKER_GUIDE_SEEDS = [
  {
    aliases: ["tracker", "sound tracker", "overview", "song workflow", "global settings", "composition"],
    examples: ["Tracker tab", "Play", "Write to Emulator", "Start in Emulator"],
    id: "tracker-guide-overview",
    keywords: ["pattern", "instrument", "instruments", "song", "composition", "order", "title", "global settings", "transport", "json", "bytecode", "main emulator"],
    notes: [
      "The browser project model is the authoritative editable source; the guest-player bytecode is derived on demand.",
      "New resets the browser project to one empty pattern plus the default instrument set without requiring a page reload.",
      "Play, the Composition Play button, and Play from all reserve the visible main emulator and route through the built tracker player.",
      "The Status / Export card also exposes browser-audio queue, underrun, and dropped-frame diagnostics so short web-playback glitches can be diagnosed without leaving the Tracker tab.",
      "The Song area handles the Title field, speed, tempo, AUD_PITCH_CFG, AUD_MODEVOL, AUD_FC, plus AUD_FILT_CTRL split into RES and MODE dropdowns, alongside New, Play, Load, Save, and Export, while the Composition card handles the order list, order pills, Add/Delete/Copy, the pattern preview buttons, Follow Row, and dropdowns for Edit Step and keyboard octave.",
      "Browser preview and guest playback now share the same equal-tempered pitch tables; COARSE_16 and BALANCED_19 cover C2..B7, while FINE_20 intentionally stops at C2..F#7.",
      "The Instruments card can add fresh instruments, duplicate the current one, preview the selected timbre directly through the main emulator without compiling a full song, and edit the A/D/S/R envelope nibbles as four 0..15 dropdowns.",
      "The waveform-adjacent number field is still the stored Pulse Width register, so the tracker only shows it for Pulse; Triangle, Saw, and Noise still get pitch from note data plus AUD_PITCH_CFG.",
      "Side rails at the far left and right edges collapse or restore the song/composition/status and instruments columns so the Pattern card can claim the freed width.",
      "The Composition card shows both the raw order-list text field and a draggable visual order strip so repeated patterns are easier to follow and reorder.",
      "The Pattern card's name field doubles as a dropdown-style selector: choose an existing pattern from the list, or type to rename the currently selected pattern.",
      "Write to Emulator stores player and song bytes without auto-starting them, while Start in Emulator launches the already-written workspace explicitly.",
      "The tracker workspace lives in RAM at $4600..$5FFF, with the player entry at $4600 and the detailed memory map documented in the workspace entry."
    ],
    relatedIds: [
      "tracker-guide-player-workspace-and-limits",
      "tracker-guide-keyboard-note-map",
      "tracker-guide-effects-and-transport",
      "tracker-guide-export-and-save",
      "memory-range-code-ram",
      "register-aud-modevol",
      "register-aud-pitch-cfg",
      "register-aud-fc"
    ],
    source: "Browser workbench tracker",
    summary: "Explains the v1 browser tracker workflow, guest-player playback path, and explicit emulator handoff model.",
    syntax: "Song Play | Composition Play | Play from | Write to Emulator | Start in Emulator",
    title: "Overview and Playback Workflow"
  },
  {
    aliases: ["tracker player", "tracker_player", "guest player", "status block", "workspace", "sys 4600"],
    examples: ["SYS 4600", "$4600", "$4F00", "$4F20", "$5FFF"],
    id: "tracker-guide-player-workspace-and-limits",
    keywords: ["player", "workspace", "status", "bytecode", "limits", "ram"],
    notes: [
      "The assembled tracker_player PRG loads and executes at $4600, which is why browser start actions ultimately queue SYS 4600.",
      "The browser reserves $4600..$5FFF for tracker playback: code starts at $4600, the 32-byte TRK1 status block starts at $4F00, and compiled bytecode starts at $4F20.",
      "The status block carries state, start_order, start_row, loop, mute_mask, solo_mask, stop_request, current_order, current_row, current_tick, current_pattern, current_speed, current_tempo, bytecode_size, tick_reload, and error_code.",
      "Write to Emulator fills that RAM workspace without auto-starting it, while Song Play, Composition Play, and Play from all write the workspace and then start the already-written player.",
      "v1 keeps hard limits of 4 channels, 64 rows per pattern, 1 effect column per event, 16 compiled instruments, 4 compiled patterns, 64 order slots, and 4320 bytecode bytes.",
      "Pitch validity is also part of that compile gate: retained G7..B7 notes are legal project data, but they block FINE_20 transport/write/export until you fix them or switch back to a wider pitch mode.",
      "The JSON field rowsPerPattern is present for explicitness, but v1 sanitizes it back to the fixed 64-row model during load and compile."
    ],
    relatedIds: [
      "tracker-guide-overview",
      "tracker-guide-export-and-save",
      "memory-range-code-ram",
      "register-aud-tick"
    ],
    source: "Browser workbench tracker + tracker contract",
    summary: "Documents the tracker_player RAM workspace, status-block addresses, start path, and fixed v1 compile limits.",
    syntax: "$4600 player | $4F00 status | $4F20 bytecode | $5FFF workspace end",
    title: "Tracker Player Workspace and Limits"
  },
  {
    aliases: ["tracker keyboard", "note input", "grid editing", "undo redo"],
    examples: ["Z S X D C", "Arrow keys", "Ctrl+C", "Ctrl+V", "[ / ]", "- / ="],
    id: "tracker-guide-keyboard-and-editing",
    keywords: ["keyboard", "selection", "transpose", "copy", "paste", "octave"],
    notes: [
      "The tracker grid accepts note entry while the grid has focus, using one lower keyboard row for the selected octave and one upper row for the octave above it.",
      "If Song or Composition playback is already running, keyboard note entry still edits the current pattern but no longer steals the active transport into a temporary browser audition state.",
      "Arrow keys move the cursor, Shift plus arrows expands the selection, and Tab moves across channels.",
      "Ctrl+C, Ctrl+V, Ctrl+Z, and Ctrl+Y cover clipboard and undo/redo flow, while [ and ] transpose by semitone or octave with Shift.",
      "The selected cell editor mirrors the same note, instrument, and effect data for keyboard-only editing without relying on pointer input.",
      "Mute and Solo live in the sticky pattern-grid header next to V1..V4 so per-channel transport debugging stays close to the rows you are reading, and solo visually isolates non-solo channels.",
      "The dedicated keyboard note-map entry lists every key-to-note mapping explicitly, including the number-row sharps.",
      "Undo and Redo show history depth and the latest reversible action so keyboard editing stays observable instead of opaque."
    ],
    relatedIds: ["tracker-guide-overview", "tracker-guide-keyboard-note-map"],
    source: "Browser workbench tracker",
    summary: "Documents keyboard note entry, selection movement, clipboard shortcuts, transpose, octave, and undo/redo behavior.",
    syntax: "Z/S/X... notes | Arrows / Tab move | Ctrl+C/V/Z/Y | [ / ] transpose | - / = octave",
    title: "Keyboard Editing"
  },
  {
    aliases: ["z/m row", "q/u row", "note keyboard", "which key note", "keyz", "digit2"],
    examples: ["Z S X D C V G B H N J M", "Q 2 W 3 E R 5 T 6 Y 7 U", "Octave 4: Z=C-4, Q=C-5"],
    id: "tracker-guide-keyboard-note-map",
    keywords: ["keyboard", "notes", "key map", "octave", "sharps"],
    notes: [
      "The selected tracker octave sets the base octave for the lower note row. If Octave is 4, the lower row plays C-4 through B-4 and the upper row plays C-5 through B-5.",
      "Lower row mapping: Z=C, S=C#, X=D, D=D#, C=E, V=F, G=F#, B=G, H=G#, N=A, J=A#, M=B in the selected octave.",
      "Upper row mapping: Q=C, 2=C#, W=D, 3=D#, E=E, R=F, 5=F#, T=G, 6=G#, Y=A, 7=A#, U=B in the octave above the selected octave.",
      "- lowers the selected octave and = raises it; direct keyboard entry clamps that base octave to 2..6, while the full tracker note range remains C-2..B-7.",
      "The keyboard note layout inserts note values only; explicit OFF and CUT notes plus effect arguments are still set through the cell editor or selection actions.",
      "Legacy projects that stored the old hard-stop note value keep that behavior by showing those rows as CUT.",
      "When FINE_20 is active, direct note entry stops at F#7 even though older projects may still retain higher note codes for diagnostics and later repair."
    ],
    relatedIds: ["tracker-guide-keyboard-and-editing", "tracker-guide-overview"],
    source: "Browser workbench tracker",
    summary: "Maps every tracker note-entry key to its note name and explains how the selected octave shifts the two keyboard rows.",
    syntax: "Lower row = selected octave | Upper row = selected octave + 1",
    title: "Keyboard Note Map"
  },
  {
    aliases: ["tracker effects", "transport", "mute", "solo", "loop"],
    examples: ["ARP 37", "POR 02", "VIB 31", "PWM F8", "SPD 06", "FCT 80"],
    id: "tracker-guide-effects-and-transport",
    keywords: ["effects", "arp", "portamento", "vibrato", "mute", "solo", "loop"],
    notes: [
      "Local v1 effect codes are CUT, ARP, POR, VIB, PWM, and RET; global row effects are SPD, BRK, JMP, FCT, FRS, and MVL.",
      "The effect-help card explains the currently selected effect, its scope, and how to read the compact 8-bit argument byte.",
      "BRK is the normal way to end a musically short phrase before row 63; for example BRK 00 on row 19 jumps to the next order immediately after that row completes.",
      "JMP targets an order-slot index instead of the next slot, so use it for loops, song restarts, or deliberate arrangement jumps rather than for routine short-pattern endings.",
      "Play previews the selected instrument on the selected cell note when possible, or on the current keyboard octave C when the cell is empty, and the same button flips to Stop while that preview is active.",
      "If Song or Composition playback is already running, stop that tracker playback before using the instrument preview so the transport toggle and guest-player state stay authoritative.",
      "That same transport-authoritative rule now applies to live note entry: typed notes can edit a running pattern, but they do not relabel the active Song or Composition button back to Play or switch control over to browser audition.",
      "Changing AUD_PITCH_CFG affects both guest playback and direct instrument preview because note lookup switches between the coarse, balanced, and fine pitch tables immediately.",
      "If FINE_20 becomes active while the project still contains G7..B7, the tracker keeps those notes visible, marks the affected cells, lists every Pattern/Row/Voice location, and blocks transport/write/export until the issue is resolved.",
      "Delete removes the active pattern from the arrangement, while the Instruments Delete button removes the selected timbre and clears any event references that used it.",
      "Mute and Solo are browser-side transport masks pushed into the tracker status block before playback starts.",
      "The Composition Play button compiles a one-pattern preview song instead of using a separate player mode, so follow-row and stop semantics stay aligned with song playback.",
      "Filter cutoff uses an 8-bit tracker value that expands into the shared 11-bit AUD_FC pair during playback."
    ],
    relatedIds: [
      "tracker-guide-overview",
      "tracker-guide-effect-off",
      "tracker-guide-effect-arp",
      "tracker-guide-effect-por",
      "tracker-guide-effect-vib",
      "tracker-guide-effect-pwm",
      "tracker-guide-effect-ret",
      "tracker-guide-effect-spd",
      "tracker-guide-effect-brk",
      "tracker-guide-effect-jmp",
      "tracker-guide-effect-fct",
      "tracker-guide-effect-frs",
      "tracker-guide-effect-mvl",
      "register-aud-filt-ctrl",
      "register-aud-filt-route",
      "register-aud-fc"
    ],
    source: "Browser workbench tracker",
    summary: "Lists the v1 tracker effect set plus transport semantics for loop, mute, solo, pattern preview, and stop behavior.",
    syntax: "NOTE INST FXARG with one effect column per channel event",
    title: "Effects and Transport"
  },
  {
    aliases: ["cut", "voice cut", "tracker cut", "tracker off", "hard off"],
    examples: ["CUT 00", "--- .. CUT00"],
    id: "tracker-guide-effect-off",
    keywords: ["effect", "cut", "hard stop", "gate"],
    notes: [
      "CUT is a voice-scoped effect that immediately silences the current voice when used on a row whose note column is otherwise empty.",
      "Use note-column OFF when you want the current instrument release phase to run instead of an immediate silence.",
      "Legacy projects that used the old 0xFF OFF note value now surface that note as CUT so playback stays backwards-compatible.",
      "The argument byte is normally 00; the current v1 player ignores it for CUT.",
      "Typical pattern use is an empty note plus CUT on the row where you want a hard stop."
    ],
    relatedIds: ["tracker-guide-effects-and-transport"],
    source: "Browser workbench tracker",
    summary: "Immediately silences the current voice on the current row.",
    syntax: "CUT 00",
    title: "CUT - Immediate Voice Cut"
  },
  {
    aliases: ["arp", "arpeggio", "tracker arp"],
    examples: ["ARP 37", "C-4 01 ARP37"],
    id: "tracker-guide-effect-arp",
    keywords: ["effect", "arp", "arpeggio", "chord illusion"],
    notes: [
      "ARP is a voice-scoped arpeggio effect. The high nibble is the first semitone offset and the low nibble is the second semitone offset from the row's base note.",
      "After the row starts, later ticks cycle base note, high-nibble offset, low-nibble offset, then repeat. ARP 37 therefore cycles root, +3, +7.",
      "Use ARP to fake fast triads or chord color on one voice without spending extra channels.",
      "The effect is most useful on sustained notes; a very short speed leaves little time for the arpeggio to be heard."
    ],
    relatedIds: ["tracker-guide-effects-and-transport"],
    source: "Browser workbench tracker",
    summary: "Cycles the row's base note and two semitone offsets across later ticks.",
    syntax: "ARP XY",
    title: "ARP - Arpeggio"
  },
  {
    aliases: ["por", "portamento", "slide"],
    examples: ["POR 02", "G-4 01 POR03"],
    id: "tracker-guide-effect-por",
    keywords: ["effect", "por", "portamento", "slide", "glide"],
    notes: [
      "POR is a voice-scoped pitch slide toward the row's note. In normal use you place POR on the same row as the destination note you want to glide to.",
      "The current v1 player uses the low nibble as the per-tick semitone step size; if that nibble is 0, it behaves as step size 1.",
      "Unlike a normal new note row, POR does not retrigger the gate when the slide starts. It keeps the current voice alive and retunes it over later ticks.",
      "Small values produce gentle slides, while larger low-nibble values reach the target faster."
    ],
    relatedIds: ["tracker-guide-effects-and-transport"],
    source: "Browser workbench tracker",
    summary: "Slides the current voice toward the row's note without retriggering its gate.",
    syntax: "POR 0X",
    title: "POR - Portamento"
  },
  {
    aliases: ["vib", "vibrato", "tracker vibrato"],
    examples: ["VIB 10", "VIB 20"],
    id: "tracker-guide-effect-vib",
    keywords: ["effect", "vib", "vibrato", "pitch wobble"],
    notes: [
      "VIB is a voice-scoped vibrato around the row's base note. The current v1 player reads the high nibble as depth in semitone steps.",
      "If the high nibble is 0, v1 still applies a depth of 1 semitone. The low nibble is currently ignored by the built player.",
      "The modulation flips above and below the base note over later ticks, so small depths such as 10 or 20 are usually more musical than extreme values.",
      "Vibrato works best on held notes; if the row changes immediately, there is little time to hear the wobble."
    ],
    relatedIds: ["tracker-guide-effects-and-transport"],
    source: "Browser workbench tracker",
    summary: "Applies a simple tick-driven pitch wobble around the current note.",
    syntax: "VIB X0",
    title: "VIB - Vibrato"
  },
  {
    aliases: ["pwm", "pulse width", "pulse width mod"],
    examples: ["PWM 08", "PWM F8"],
    id: "tracker-guide-effect-pwm",
    keywords: ["effect", "pwm", "pulse width", "signed delta"],
    notes: [
      "PWM is a voice-scoped pulse-width movement. The argument is treated as a signed 8-bit delta applied on later ticks.",
      "Positive values widen the duty cycle and negative values narrow it; for example 08 widens while F8 narrows.",
      "The player clamps the running pulse width to the legal 12-bit range 0..4095 so extreme settings do not wrap around.",
      "PWM is musically strongest on pulse instruments, although the underlying width registers still update on other waveforms."
    ],
    relatedIds: ["tracker-guide-effects-and-transport"],
    source: "Browser workbench tracker",
    summary: "Adds a signed per-tick delta to the current voice pulse width.",
    syntax: "PWM XX",
    title: "PWM - Pulse Width Movement"
  },
  {
    aliases: ["ret", "retrigger", "gate restart"],
    examples: ["RET 03", "C-4 01 RET03"],
    id: "tracker-guide-effect-ret",
    keywords: ["effect", "ret", "retrigger", "gate restart"],
    notes: [
      "RET is a voice-scoped retrigger effect. A non-zero argument restarts the current note gate every N ticks.",
      "You normally place RET on the same row as a note so the player has a current note to retrigger. RET 03 restarts it every three ticks.",
      "00 disables retriggering. Very small values create a chattering or hard-trill effect; larger values sound like rhythmic gate pulses.",
      "RET keeps using the current note and instrument; it does not fetch a new note from later rows by itself."
    ],
    relatedIds: ["tracker-guide-effects-and-transport"],
    source: "Browser workbench tracker",
    summary: "Restarts the current note gate at a fixed tick interval.",
    syntax: "RET XX",
    title: "RET - Retrigger"
  },
  {
    aliases: ["spd", "speed", "ticks per row"],
    examples: ["SPD 06", "SPD 03"],
    id: "tracker-guide-effect-spd",
    keywords: ["effect", "spd", "speed", "ticks per row"],
    notes: [
      "SPD is a song-scoped row effect that changes tracker speed, meaning ticks per row, for following rows.",
      "Use smaller values for snappier rows and less time for tick-driven effects, or larger values when ARP, VIB, PWM, or RET need more room to evolve.",
      "00 is ignored by the current v1 player, so use non-zero values when you want an actual change.",
      "SPD changes row subdivision only; the broader song pace still depends on the separate Tempo setting."
    ],
    relatedIds: ["tracker-guide-effects-and-transport"],
    source: "Browser workbench tracker",
    summary: "Changes ticks per row for subsequent playback rows.",
    syntax: "SPD XX",
    title: "SPD - Speed"
  },
  {
    aliases: ["brk", "break", "pattern break", "next order"],
    examples: ["BRK 00", "BRK 14"],
    id: "tracker-guide-effect-brk",
    keywords: ["effect", "brk", "break", "pattern length", "next order"],
    notes: [
      "BRK is a song-scoped pattern break. After the current row finishes, playback moves to the next order slot instead of continuing through the remaining rows of the current pattern.",
      "The argument is the start row in that next order slot, encoded as a hex row index. BRK 00 starts the next order at row 00, while BRK 14 starts it at row 20.",
      "BRK 00 is the standard way to end a musically short phrase early, such as a 20-row idea inside the fixed 64-row pattern format.",
      "BRK always targets the next order slot. If you need to choose an exact destination slot, use JMP instead."
    ],
    relatedIds: ["tracker-guide-effects-and-transport", "tracker-guide-effect-jmp"],
    source: "Browser workbench tracker",
    summary: "Ends the current pattern early and continues at the next order slot.",
    syntax: "BRK XX",
    title: "BRK - Pattern Break"
  },
  {
    aliases: ["jmp", "jump", "position jump", "order jump"],
    examples: ["JMP 00", "JMP 02"],
    id: "tracker-guide-effect-jmp",
    keywords: ["effect", "jmp", "jump", "order list", "loop"],
    notes: [
      "JMP is a song-scoped order jump. After the current row finishes, playback jumps to the specified order-slot index and restarts from row 00 there.",
      "The argument names an order position, not a pattern number. JMP 02 means order slot 02 in the order list, whatever pattern that slot currently references.",
      "Use JMP for explicit loops, song restarts, or arranged skips. For a simple short-pattern exit into the next slot, BRK is usually clearer.",
      "If the jump target lies outside the current order count, playback reaches the order-end handling path and may stop or loop depending on the transport state."
    ],
    relatedIds: ["tracker-guide-effects-and-transport", "tracker-guide-effect-brk"],
    source: "Browser workbench tracker",
    summary: "Jumps to a specific order-slot index after the current row.",
    syntax: "JMP XX",
    title: "JMP - Position Jump"
  },
  {
    aliases: ["fct", "filter cutoff", "cutoff"],
    examples: ["FCT 80", "FCT FF"],
    id: "tracker-guide-effect-fct",
    keywords: ["effect", "fct", "filter cutoff", "aud_fc"],
    notes: [
      "FCT is a song-scoped filter cutoff write. It converts one 8-bit tracker value into the shared 11-bit AUD_FC cutoff pair.",
      "The current player expands the byte by shifting it left three bits, so 00 maps near the darkest cutoff and FF maps near the brightest cutoff the tracker can request.",
      "Because AUD_FC is shared, the change affects every voice currently routed through the filter, not just the channel that carries the effect code.",
      "Use FCT together with instrument Filter Route settings and, when needed, FRS mode/resonance changes."
    ],
    relatedIds: ["tracker-guide-effects-and-transport", "register-aud-fc", "register-aud-filt-route"],
    source: "Browser workbench tracker",
    summary: "Sets the shared filter cutoff from an 8-bit tracker value.",
    syntax: "FCT XX",
    title: "FCT - Filter Cutoff"
  },
  {
    aliases: ["frs", "filter resonance", "filter mode"],
    examples: ["FRS 11", "FRS 20"],
    id: "tracker-guide-effect-frs",
    keywords: ["effect", "frs", "resonance", "lp", "bp", "hp"],
    notes: [
      "FRS is a song-scoped write to AUD_FILT_CTRL, the shared resonance and filter-mode register.",
      "Bits 3..0 set resonance 0..15. Bits 5..4 choose filter mode: 00=low-pass, 01=band-pass, 10=high-pass, and 11 coerces back to low-pass in the current model.",
      "Because the register is shared, the change affects all voices routed into the filter path.",
      "Use FRS when you want a deliberate change in resonance or LP/BP/HP flavor at one playback row."
    ],
    relatedIds: ["tracker-guide-effects-and-transport", "register-aud-filt-ctrl", "register-aud-filt-route"],
    source: "Browser workbench tracker",
    summary: "Writes the shared filter resonance and LP/BP/HP mode byte.",
    syntax: "FRS XX",
    title: "FRS - Filter Resonance and Mode"
  },
  {
    aliases: ["mvl", "master volume", "volume"],
    examples: ["MVL 0C", "MVL 0F"],
    id: "tracker-guide-effect-mvl",
    keywords: ["effect", "mvl", "master volume", "aud_modevol"],
    notes: [
      "MVL is a song-scoped master volume write to AUD_MODEVOL.",
      "Only the low nibble is used in the current player, so values normally live in the 00..0F range. Higher nibble bits are ignored.",
      "Use MVL to shape broad song dynamics or to leave headroom before a louder section or dense multi-voice chord arrives.",
      "Because the register is global, MVL affects the entire mixed tracker output instead of one voice."
    ],
    relatedIds: ["tracker-guide-effects-and-transport", "register-aud-modevol"],
    source: "Browser workbench tracker",
    summary: "Sets the global tracker master volume from the low nibble of the effect byte.",
    syntax: "MVL 0X",
    title: "MVL - Master Volume"
  },
  {
    aliases: ["tracker export", "json import", "bytecode export", "save workflow"],
    examples: ["Load", "Save", "Export", "SAVE"],
    id: "tracker-guide-export-and-save",
    keywords: ["export", "import", "draft", "save", "guest workflow", "bytecode"],
    notes: [
      "Save writes the browser-authoritative JSON project, while Export writes the compact guest-player artifact.",
      "Both Save and Export derive their suggested download filename from the current song title.",
      "Load validates the versioned JSON project shape and leaves the current project untouched on parse or version errors.",
      "The tracker stores the latest local draft in browser storage so reloads can restore the current editing session.",
      "Compile diagnostics in the status card show patterns, instruments, orders, and byte usage before transport or export actions fail.",
      "That same status area also surfaces retained-note pitch diagnostics such as Fine_20 range violations, and those diagnostics block browser playback/write/export until cleared.",
      "For a guest-side artifact, write the workspace, start it explicitly, inspect the result, and then use SAVE from inside the emulator."
    ],
    relatedIds: ["tracker-guide-overview", "shell-command-save", "shell-command-sys"],
    source: "Browser workbench tracker",
    summary: "Covers tracker JSON save/load, bytecode export, local draft persistence, and the guest-side save workflow.",
    syntax: "Load | Save | Export | Write to Emulator | Start in Emulator",
    title: "Import, Export, and Save Workflow"
  }
] as const;

export const FORTH_GUIDE_SEEDS = [
  {
    aliases: ["forth editor", "line", "list", "f>", "live repl"],
    examples: ["FORTH", ": SQ DUP * ;", "LINE 10 : SQ DUP * ;", "LIST", "RUN"],
    id: "forth-guide-editor-workflow",
    keywords: ["program store", "module load", "live session", "detokenize"],
    notes: [
      "FORTH mode loads and validates the FORTH module from drive 0 immediately and switches the prompt to F>.",
      "Unnumbered non-command input is interpreted live against the current FORTH session and is not automatically persisted into the stored source buffer.",
      "LINE <n> explicitly replaces or deletes one stored source line, LIST detokenizes that explicit source buffer, and SAVE/LOAD operate on that same buffer.",
      "RUN replays the explicit stored source buffer from a fresh FORTH session when stored source exists; with no stored source, RUN is a no-op and leaves the current live session untouched."
    ],
    relatedIds: [
      "shell-command-forth",
      "shell-command-line",
      "shell-command-list",
      "shell-command-run",
      "memory-range-language-program",
      "memory-range-language-slot",
      "memory-range-forth-window",
      "public-api-append-auto-line",
      "public-api-edit-numbered-line",
      "public-api-list-program",
      "public-api-module-tokenizer-callback",
      "public-api-module-detokenizer-callback"
    ],
    source: "FORTH module + ROM editor bridge",
    summary: "Explains how live FORTH input, the explicit source buffer, LIST, RUN, and SAVE/LOAD now fit together.",
    syntax: "FORTH | <live source> | LINE <n> [source] | LIST | RUN",
    title: "Editor Workflow and Program Store"
  },
  {
    aliases: ["dup", "drop", "swap", "over", "nip", "hex", "decimal", "signed literal", "."],
    examples: ["5 DUP + .", "HEX F . DECIMAL -15 .", "-$2A .", "$1234 DROP"],
    id: "forth-guide-stack-base",
    keywords: ["stack words", "numeric literals", "current base", "dot output"],
    notes: [
      "Core stack helpers DUP, DROP, SWAP, OVER, and NIP operate on 16-bit cells.",
      "Unprefixed numeric input follows the current base, while HEX and DECIMAL switch both number parsing and . output.",
      "Signed literals may start with + or -, and $-prefixed hexadecimal literals stay available regardless of the current base.",
      "In DECIMAL mode, . prints signed values; in HEX mode, it prints the current cell as hexadecimal."
    ],
    relatedIds: ["forth-guide-arithmetic-logic", "forth-guide-quick-stack-tools", "forth-guide-output-parsing", "shell-command-forth", "shell-command-run"],
    source: "FORTH module runtime",
    summary: "Covers the cell-stack helpers plus the current numeric-literal and base-control behavior.",
    syntax: "<n> DUP|DROP|SWAP|OVER|NIP | HEX | DECIMAL | .",
    title: "Stack, Literals, and Base"
  },
  {
    aliases: ["rot", "2dup", "2drop", "2swap", "2over", "2rot", "pick", "roll", "within", "depth", ".s"],
    examples: ["1 2 3 ROT .S", "1 2 2DUP .S", "1 2 3 4 2OVER .S", "10 20 30 1 PICK .", "5 3 7 WITHIN ."],
    id: "forth-guide-quick-stack-tools",
    keywords: ["stack inspection", "pair operations", "depth", "debugging"],
    notes: [
      "ROT rotates the top three cells, while 2DUP, 2DROP, 2SWAP, 2OVER, and 2ROT extend that idea to two-cell pairs.",
      "PICK duplicates the indexed stack item, ROLL moves the indexed stack item to the top, and WITHIN tests whether x is inside low..high using the current signed comparison semantics.",
      "DEPTH reports the current data-stack depth as a cell count.",
      ".S prints the current stack without consuming it, which makes it ideal for learning and debugging."
    ],
    relatedIds: ["forth-guide-stack-base", "forth-guide-arithmetic-logic", "forth-guide-metaprogramming-repl"],
    source: "FORTH module runtime",
    summary: "Adds the deeper stack-manipulation and stack-inspection helpers that make live debugging easier.",
    syntax: "ROT | 2DUP | 2DROP | 2SWAP | 2OVER | 2ROT | PICK | ROLL | WITHIN | DEPTH | .S",
    title: "Extended Stack Tools"
  },
  {
    aliases: ["1+", "1-", "/mod", "0=", "0<", "not", "invert", "lshift", "rshift", "negate", "abs", "min", "max"],
    examples: ["$FFF9 $0003 /MOD", "$0002 $0003 <", "-5 ABS", "$FF00 NOT"],
    id: "forth-guide-arithmetic-logic",
    keywords: ["signed 16-bit", "fig flags", "boolean words", "am8 extensions"],
    notes: [
      "Arithmetic words +, -, *, /, MOD, /MOD, 1+, and 1- use signed 16-bit cell semantics in the current module.",
      "NEGATE, ABS, MIN, and MAX provide the everyday signed-value helpers that keep many definitions short.",
      "Comparison words =, <, >, 0=, and 0< return FIG-style flags with TRUE=-1 and FALSE=0.",
      "AND, OR, XOR, and INVERT are available, NOT aliases INVERT, and LSHIFT/RSHIFT are AM8-specific extensions."
    ],
    relatedIds: ["forth-guide-stack-base", "forth-guide-quick-stack-tools", "forth-guide-memory-bit-access", "forth-guide-conditionals", "shell-command-run"],
    source: "FORTH module runtime",
    summary: "Groups the current arithmetic, comparison, boolean, and shift-word surface.",
    syntax: "a b +|-|*|/|MOD|/MOD|MIN|MAX | x 1+|1-|NEGATE|ABS | a b =|<|> | x 0=|0< | a b AND|OR|XOR | x NOT | x n LSHIFT|RSHIFT",
    title: "Arithmetic, Comparisons, and Logic"
  },
  {
    aliases: ["@", "!", "c@", "c!", "bset", "bclr", "btest"],
    examples: ["$5000 @", "$2A $5000 C!", "$01 $D020 BSET"],
    id: "forth-guide-memory-bit-access",
    keywords: ["fetch", "store", "byte access", "mmio bits"],
    notes: [
      "@ and ! move 16-bit cells, while C@ and C! access single bytes.",
      "BSET, BCLR, and BTEST are AM8-specific helpers for byte-oriented RAM or MMIO flag updates.",
      "These words are commonly used with absolute $-prefixed addresses in RAM and device space."
    ],
    relatedIds: ["forth-guide-arithmetic-logic", "memory-range-io-window", "shell-command-poke", "shell-command-run"],
    source: "FORTH module runtime",
    summary: "Documents the current cell, byte, and bit-oriented memory access words.",
    syntax: "addr @ | x addr ! | addr C@ | x addr C! | mask addr BSET|BCLR|BTEST",
    title: "Memory and Bit Access"
  },
  {
    aliases: [":", ";", "immediate", "literal", "[compile]", "brackets", "dot quote"],
    examples: [": TWICE DUP + ;", ": FLAG $2A $500E C! ; IMMEDIATE", ": BRK [ 1 2 + ] LITERAL ;", ": GIVE LIT $2A EXIT LIT $2B ;"],
    id: "forth-guide-definitions-compile",
    keywords: ["colon definition", "compile state", "threaded calls", "raw span"],
    notes: [
      "A colon definition enters compile state and closes with ; using the module's threaded compiled-body format.",
      "Known user-word calls compile as threaded call entries, so already-compiled callers keep their original target even after later redefinitions.",
      "IMMEDIATE, [, ], LITERAL, and [COMPILE] control compile-time execution, while low-level LIT and EXIT remain available as runtime words.",
      "Colon / semicolon syntax, dot-quote, tick ('), and comment forms such as \\ and ( ... ) remain runtime raw-span syntax rather than dedicated token IDs in the current token table."
    ],
    relatedIds: [
      "forth-guide-conditionals",
      "forth-guide-loop-control",
      "forth-guide-counted-loops-return-stack",
      "forth-guide-defining-words",
      "shell-command-run"
    ],
    source: "FORTH module compiler",
    summary: "Describes colon definitions, compile-state control words, and the current threaded-call compilation model.",
    syntax: ": NAME ... ; | IMMEDIATE | [ ... ] LITERAL | [COMPILE] WORD | LIT | EXIT",
    title: "Definitions and Compile Control"
  },
  {
    aliases: ["if", "else", "then"],
    examples: [": PICK IF $2A ELSE $2B THEN ;"],
    id: "forth-guide-conditionals",
    keywords: ["branch", "backpatch", "true false", "compile mismatch"],
    notes: [
      "IF / ELSE / THEN compiles forward branches with backpatched targets instead of runtime token scanning.",
      "The tested flag convention follows FIG style, so 0 is false and non-zero (typically -1) is true.",
      "Unmatched conditional state is rejected as a compile-time mismatch instead of running with undefined control flow."
    ],
    relatedIds: ["forth-guide-arithmetic-logic", "forth-guide-definitions-compile", "forth-guide-loop-control"],
    source: "FORTH module compiler",
    summary: "Builds conditional branches inside colon definitions with compile-time backpatching.",
    syntax: "IF ... [ELSE ...] THEN",
    title: "IF / ELSE / THEN"
  },
  {
    aliases: ["begin", "until", "again", "while", "repeat"],
    examples: [": INC3 0 BEGIN 1+ DUP 3 = UNTIL ;", ": COUNT2 0 BEGIN DUP 2 < WHILE 1+ REPEAT ;"],
    id: "forth-guide-loop-control",
    keywords: ["backward branch", "while repeat", "unconditional loop", "backpatch"],
    notes: [
      "BEGIN creates the loop head, UNTIL exits when the flag becomes true, and AGAIN branches unconditionally back to the head.",
      "WHILE / REPEAT splits a BEGIN loop into test and body sections with compile-time backpatching.",
      "These words compile threaded branch markers directly instead of token-scan helpers."
    ],
    relatedIds: ["forth-guide-conditionals", "forth-guide-counted-loops-return-stack", "forth-guide-definitions-compile"],
    source: "FORTH module compiler",
    summary: "Covers the current BEGIN-family loop words and their backpatched branch behavior.",
    syntax: "BEGIN ... UNTIL | BEGIN ... AGAIN | BEGIN ... WHILE ... REPEAT",
    title: "Loop Control"
  },
  {
    aliases: ["do", "?do", "loop", "+loop", "leave", "i", "j", ">r", "r>", "r@"],
    examples: [": SUMI 0 4 0 DO I + LOOP ;", ": STEP3 0 10 0 DO I 6 = IF LEAVE THEN 1 + 2 +LOOP ;", "$1234 >R R@ R>"],
    id: "forth-guide-counted-loops-return-stack",
    keywords: ["loop index", "return stack", "skip on equal", "compile context"],
    notes: [
      "?DO shares the counted-loop runtime path with DO and skips the loop body when start equals limit.",
      "LOOP and +LOOP use compile-time backpatched exit markers, and LEAVE jumps to that patched loop end.",
      "I and J expose the current and outer loop indices, while >R, R>, and R@ move values through the return stack.",
      "LEAVE and I require active DO/?DO depth, and J requires nested loop depth; invalid use is rejected at compile time."
    ],
    relatedIds: ["forth-guide-loop-control", "forth-guide-conditionals", "forth-guide-definitions-compile"],
    source: "FORTH module compiler/runtime",
    summary: "Explains counted loops, loop indices, LEAVE, and the exposed return-stack helpers.",
    syntax: ">R | R> | R@ | ?DO ... LOOP | DO ... +LOOP | LEAVE | I | J",
    title: "Counted Loops and Return Stack"
  },
  {
    aliases: ["constant", "variable", "create", "does>"],
    examples: ["$1234 CONSTANT K", "VARIABLE V", "CREATE BOX DOES> DROP $1234"],
    id: "forth-guide-defining-words",
    keywords: ["dictionary", "parameter address", "custom runtime", "value word"],
    notes: [
      "CONSTANT defines a word that leaves its captured 16-bit value when executed.",
      "VARIABLE defines a word that leaves its parameter address so @ and ! can read or write the stored cell.",
      "CREATE allocates a new header and parameter space, while DOES> replaces the default parameter-address behavior with custom runtime code."
    ],
    relatedIds: ["forth-guide-memory-bit-access", "forth-guide-definitions-compile", "forth-guide-editor-workflow"],
    source: "FORTH module defining words",
    summary: "Covers the current value, storage, and parameterized defining words.",
    syntax: "n CONSTANT name | VARIABLE name | CREATE name | CREATE name DOES> ...",
    title: "Defining Words"
  },
  {
    aliases: ["here", "allot", ",", "c,", "count", "dictionary space"],
    examples: ["HERE .", "2 ALLOT", "HERE $1234 , $56 C,", "$5010 COUNT TYPE"],
    id: "forth-guide-data-space",
    keywords: ["dictionary pointer", "counted string", "parameter space", "raw data"],
    notes: [
      "HERE leaves the current dictionary/data-space pointer, and ALLOT moves that pointer by a signed count.",
      ", and C, append one cell or one byte to dictionary space respectively.",
      "COUNT converts a counted string into the addr+1 len pair expected by TYPE and similar words."
    ],
    relatedIds: ["forth-guide-defining-words", "forth-guide-memory-bit-access", "forth-guide-output-parsing"],
    source: "FORTH module dictionary runtime",
    summary: "Documents the current data-space words for reserving and filling raw dictionary storage.",
    syntax: "HERE | n ALLOT | x , | x C, | addr COUNT",
    title: "Data Space and Counted Strings"
  },
  {
    aliases: ["cr", "emit", "space", "spaces", "type", "word", "key", "accept", "expect", "query", "s quote", "comment words"],
    examples: ["SPACE 3 SPACES $5000 2 TYPE", "44 WORD HELLO,", "KEY", "$5000 8 ACCEPT", 'S" HI" TYPE', "1 2 + \\ note"],
    id: "forth-guide-output-parsing",
    keywords: ["text output", "delimiter parse", "interactive input", "strings", "comments"],
    notes: [
      "CR, EMIT, SPACE, SPACES, and TYPE cover the current character and string-output surface.",
      "WORD parses the next token using the supplied delimiter character and returns the buffer address for that parsed word.",
      "KEY reads one interactive character-style input unit, ACCEPT reads text into an explicit addr len buffer and leaves the resulting length, EXPECT is the simpler bounded input helper, and QUERY refreshes the shared FORTH line buffer.",
      "S\" ...\" yields addr len for a string in interpret and compile usage, which makes it the data-flow companion to .\" text output.",
      "Backslash comments skip the rest of the current source line, and parenthesized comments skip text until the closing )."
    ],
    relatedIds: ["forth-guide-stack-base", "forth-guide-data-space", "forth-guide-definitions-compile", "forth-guide-metaprogramming-repl", "shell-command-run"],
    source: "FORTH module utilities",
    summary: "Documents the shipped text-output, token-parsing, interactive-input, string, and comment words.",
    syntax: "CR | u8 EMIT | SPACE | n SPACES | addr len TYPE | delim WORD | KEY | addr len ACCEPT|EXPECT | QUERY | S\" ...\" | \\ ... | ( ... )",
    title: "Output, Parsing, and Input"
  },
  {
    aliases: ["fill", "move", "cmove", "erase", "dump", "memory block words"],
    examples: ["$5000 8 $AA FILL", "$5000 $5008 8 CMOVE", "$5000 $5001 8 MOVE", "$5000 8 ERASE", "$5000 8 DUMP"],
    id: "forth-guide-memory-blocks",
    keywords: ["memory block", "copy", "clear", "hex dump"],
    notes: [
      "FILL writes one byte value across a range, ERASE clears a range to zero, and DUMP prints a compact address-plus-bytes hex listing.",
      "CMOVE copies forward, while MOVE handles overlapping ranges safely."
    ],
    relatedIds: ["forth-guide-memory-bit-access", "forth-guide-output-parsing", "forth-guide-data-space"],
    source: "FORTH module block helpers",
    summary: "Covers the byte-range fill, copy, clear, and dump helpers used for low-level memory work.",
    syntax: "addr len byte FILL | src dst len MOVE|CMOVE | addr len ERASE|DUMP",
    title: "Block Memory Words"
  },
  {
    aliases: ["tick", "execute", "find", "words", "repl", "abort", "quit", "base", "state", "see", "forget", "body", "interactive forth"],
    examples: ["' HI EXECUTE", "44 WORD HI, FIND", "WORDS", "BASE @", "' V >BODY BODY>", "SEE NAME", "FORGET NAME", "REPL"],
    id: "forth-guide-metaprogramming-repl",
    keywords: ["execution token", "dictionary search", "interactive loop", "control state", "vocabulary"],
    notes: [
      "Tick (') parses the next word and leaves an execution token suitable for EXECUTE.",
      "FIND searches for a parsed name and reports either xt+flag or the original address plus 0 when no word matches; >BODY and BODY> move between a user word's execution token and its current body address.",
      "WORDS and SEE provide lightweight dictionary introspection, FORGET trims the dictionary back to the named word, and BASE/STATE expose the current control cells.",
      "ABORT and QUIT clear the current interactive control state, and REPL enters a simple line-at-a-time interactive loop that exits on an empty line."
    ],
    relatedIds: ["forth-guide-quick-stack-tools", "forth-guide-data-space", "forth-guide-output-parsing", "forth-guide-definitions-compile", "forth-guide-memory-blocks"],
    source: "FORTH module metaprogramming helpers",
    summary: "Covers execution tokens, dictionary search, control cells, lightweight introspection, and the optional interactive REPL word.",
    syntax: "' WORD | xt EXECUTE | c-addr FIND | BASE | STATE | xt >BODY | body BODY> | SEE WORD | FORGET WORD | WORDS | ABORT | QUIT | REPL",
    title: "Metaprogramming, Introspection, and REPL"
  }
] as const;

export const SHELL_COMMAND_SEEDS = [
  {
    aliases: ["assembler mode", "asm mode"],
    availability: "Any shell prompt",
    examples: ["ASSEMBLER"],
    id: "shell-command-assembler",
    keywords: ["mode switch", "module", "editor"],
    notes: [
      "Switches the active shell/editor mode to ASSEMBLER and updates the prompt to A>.",
      "The assembler module must be available on drive 0 and is loaded/validated immediately.",
      "If the current token-store owner is a different language, the shared program store is cleared before the mode becomes active."
    ],
    relatedIds: ["memory-range-language-slot", "memory-range-language-program", "shell-command-line", "shell-command-list", "shell-command-run"],
    summary: "Activates ASSEMBLER mode for the shared token editor and module-backed runtime.",
    syntax: "ASSEMBLER",
    title: "ASSEMBLER"
  },
  {
    aliases: ["basic mode", "tiny basic"],
    availability: "Any shell prompt",
    examples: ["BASIC", '10 PRINT "HELLO"'],
    id: "shell-command-basic",
    keywords: ["mode switch", "immediate mode", "editor"],
    notes: [
      "Switches the active shell/editor mode to BASIC and updates the prompt to B>.",
      "Unnumbered non-command input runs as BASIC immediate code; numbered lines go into the shared tokenized program store.",
      "BASIC RUN uses the resident lang.rom runtime and does not require a disk module."
    ],
    relatedIds: ["memory-range-language-program", "public-api-basic-exec-immediate", "public-api-run-basic-program", "shell-command-list", "shell-command-run"],
    summary: "Activates BASIC mode for immediate commands and the shared tokenized BASIC program store.",
    syntax: "BASIC",
    title: "BASIC"
  },
  {
    aliases: ["leave mode", "return to shell"],
    availability: "Active BASIC/FORTH/ASSEMBLER prompt",
    examples: ["EXIT"],
    id: "shell-command-exit",
    keywords: ["mode switch", "prompt", "token store"],
    notes: [
      "Leaves the active language mode and returns to the default READY. shell prompt.",
      "EXIT clears only the active-mode nibble; the token-store owner stays latched so re-entering the same language keeps its stored program.",
      "Using EXIT at the plain READY. shell is a syntax error."
    ],
    relatedIds: ["memory-range-language-program", "shell-command-basic", "shell-command-forth", "shell-command-assembler"],
    summary: "Leaves the current language mode and returns to the plain shell prompt.",
    syntax: "EXIT",
    title: "EXIT"
  },
  {
    aliases: ["forth mode"],
    availability: "Any shell prompt",
    examples: ["FORTH"],
    id: "shell-command-forth",
    keywords: ["mode switch", "module", "dictionary"],
    notes: [
      "Switches the active shell/editor mode to FORTH and updates the prompt to F>.",
      "The FORTH module must be available on drive 0 and is loaded/validated immediately.",
      "Unnumbered non-command input is interpreted live against the current FORTH session, while explicit LINE editing maintains the replay/save source buffer."
    ],
    relatedIds: ["memory-range-language-slot", "memory-range-forth-window", "memory-range-language-program", "shell-command-line", "shell-command-list", "shell-command-run"],
    summary: "Activates FORTH mode for module-backed editing and runtime execution.",
    syntax: "FORTH",
    title: "FORTH"
  },
  {
    aliases: ["memory read", "dump bytes"],
    availability: "Any shell prompt",
    examples: ["PEEK 0800", "PEEK 0800 4"],
    id: "shell-command-peek",
    keywords: ["memory", "hex", "inspect"],
    notes: [
      "Reads 1 to 8 bytes starting at the requested address and prints them as hexadecimal bytes.",
      "If len is omitted or zero, the shell reads 1 byte; lengths above 8 are clamped to 8.",
      "Shell numeric arguments are hex without a prefix."
    ],
    relatedIds: ["memory-range-zero-page", "memory-range-io-window", "shell-command-poke"],
    summary: "Reads a short memory span and prints the bytes in hexadecimal.",
    syntax: "PEEK <addr> [len]",
    title: "PEEK"
  },
  {
    aliases: ["memory write", "store byte"],
    availability: "Any shell prompt",
    examples: ["POKE 0800 2A"],
    id: "shell-command-poke",
    keywords: ["memory", "hex", "write"],
    notes: [
      "Writes exactly one byte to the requested address and prints OK on success.",
      "The second argument is one hex byte; shell numeric arguments use no prefix.",
      "This monitor-style shell command is separate from BASIC's own POKE statement."
    ],
    relatedIds: ["memory-range-text-screen", "memory-range-io-window", "shell-command-peek"],
    summary: "Writes one byte to memory from the shell.",
    syntax: "POKE <addr> <byte>",
    title: "POKE"
  },
  {
    aliases: ["edit line", "program line"],
    availability: "Active BASIC/FORTH/ASSEMBLER prompt",
    examples: ['LINE 100 PRINT "HELLO"', "LINE 100"],
    id: "shell-command-line",
    keywords: ["editor", "replace line", "delete line"],
    notes: [
      "Explicitly creates/replaces one numbered program line, or deletes it when only the line number is given.",
      "In FORTH, LINE is the explicit replace/delete form for the replay/save source buffer while unnumbered input is interpreted live and is not automatically persisted.",
      "In BASIC and ASSEMBLER, plain numbered input already edits lines; LINE is the explicit command form."
    ],
    relatedIds: ["memory-range-language-program", "public-api-edit-numbered-line", "public-api-append-auto-line", "shell-command-list"],
    summary: "Creates, replaces, or deletes one numbered line in the active program store.",
    syntax: "LINE <line> [text]",
    title: "LINE"
  },
  {
    aliases: ["program listing", "detokenize"],
    availability: "Active BASIC/FORTH/ASSEMBLER prompt",
    examples: ["LIST"],
    id: "shell-command-list",
    keywords: ["editor", "program", "transcript"],
    notes: [
      "Lists the stored program for the active language mode using the current token table or module callbacks.",
      "BASIC listings are token-canonical rather than raw source replay.",
      "FORTH and ASSEMBLER LIST depend on a successfully loaded matching language module."
    ],
    relatedIds: ["memory-range-language-program", "public-api-list-program", "shell-command-line"],
    summary: "Prints the stored program in the active language mode.",
    syntax: "LIST",
    title: "LIST"
  },
  {
    aliases: ["disk load", "prg load"],
    availability: "Any shell prompt",
    examples: ['LOAD "DEMO"', 'LOAD "DEMO",1,C000 RUN'],
    id: "shell-command-load",
    keywords: ["disk", "file", "prg", "run"],
    notes: [
      "Loads a PRG from drive 0 or 1; without addr, the PRG header load address is used.",
      "If RUN is present, the shell jumps to the loaded start address immediately after a successful load.",
      "When the effective target is 0x0B00, LOAD restores the packed active-mode/store-owner metadata from the image.",
      "The guest-visible disk path stays file-level; the current host/runtime backend behaves like an intelligent drive behind the storage-controller block."
    ],
    relatedIds: ["public-api-disk-load", "memory-range-language-program", "memory-range-code-ram", "shell-command-dir", "shell-command-run", "shell-command-sys"],
    summary: "Loads a PRG from disk, optionally to an override address and optionally jumping to it.",
    syntax: 'LOAD "NAME" [0|1] [addr] [RUN]',
    title: "LOAD"
  },
  {
    aliases: ["disk save", "prg save"],
    availability: "Any shell prompt; active modes unlock the language-image form",
    examples: ['SAVE "MLTEST" 2000 0030 OVERWRITE', 'SAVE "HELLO" OVERWRITE'],
    id: "shell-command-save",
    keywords: ["disk", "file", "prg", "overwrite"],
    notes: [
      "Without an active language mode, SAVE expects addr and len and stores that RAM range as a PRG.",
      "With an active language mode, SAVE can omit addr/len and instead stores the shared tokenized program image starting at 0x0B00.",
      "OVERWRITE is optional, must come last, and enables replacing an existing file.",
      "The save path remains file-level and descriptor-driven rather than exposing raw sector writes to shell code."
    ],
    relatedIds: ["public-api-disk-save", "memory-range-language-program", "memory-range-code-ram", "shell-command-load", "shell-command-dir"],
    summary: "Saves either a raw RAM range or the active language program image as a PRG.",
    syntax: 'SAVE "NAME" [0|1] <addr> <len> [OVERWRITE] | SAVE "NAME" [0|1] [OVERWRITE]',
    title: "SAVE"
  },
  {
    aliases: ["jump", "machine code call"],
    availability: "Any shell prompt",
    examples: ["SYS C000"],
    id: "shell-command-sys",
    keywords: ["jump", "machine code", "entry point"],
    notes: [
      "Parses one hex address and jumps to it immediately.",
      "If the called code returns to the shell path, the shell prints OK afterward.",
      "This is the shell jump helper; BASIC's own USR(expr) is a separate language feature."
    ],
    relatedIds: ["memory-range-code-ram", "register-pc", "shell-command-usr"],
    summary: "Jumps to a machine-code entry point from the shell.",
    syntax: "SYS <addr>",
    title: "SYS"
  },
  {
    aliases: ["start program", "execute mode runtime"],
    availability: "Active BASIC/FORTH/ASSEMBLER prompt",
    examples: ["RUN"],
    id: "shell-command-run",
    keywords: ["execute", "module", "basic runtime"],
    notes: [
      "RUN accepts no address in the shell; use LOAD ... RUN for PRG auto-start or SYS/USR for explicit machine-code entry points.",
      "In BASIC it starts the resident BASIC program; in FORTH and ASSEMBLER it dispatches the active module entry point.",
      "On missing/invalid module or no runnable target, the shell reports ?NORUN."
    ],
    relatedIds: ["public-api-run-basic-program", "public-api-stop-test", "memory-range-language-program", "memory-range-language-slot", "shell-command-basic", "shell-command-forth", "shell-command-assembler"],
    summary: "Runs the current language-mode program or module entry point.",
    syntax: "RUN",
    title: "RUN"
  },
  {
    aliases: ["sys alias", "user jump"],
    availability: "Any shell prompt",
    examples: ["USR C000"],
    id: "shell-command-usr",
    keywords: ["jump", "alias", "machine code"],
    notes: [
      "Shell-level alias for SYS <addr>.",
      "This command is distinct from BASIC's USR(expr) function inside the BASIC language.",
      "Use it when you want monitor-style jump syntax but prefer the USR spelling."
    ],
    relatedIds: ["shell-command-sys"],
    summary: "Alias of SYS for shell-level machine-code jumps.",
    syntax: "USR <addr>",
    title: "USR"
  },
  {
    aliases: ["screen clear", "reset screen"],
    availability: "Any shell prompt",
    examples: ["CLEAR"],
    id: "shell-command-clear",
    keywords: ["screen", "banner", "reset basic state"],
    notes: [
      "Rebuilds the shell screen and banner and resets the next READY./prompt row tracking.",
      "In BASIC mode it also resets BASIC runtime state before redrawing the shell UI.",
      "CLRSCR is a direct alias of the same command path."
    ],
    relatedIds: ["shell-command-clrscr", "public-api-basic-reset-state", "memory-range-text-screen"],
    summary: "Clears and redraws the shell screen; in BASIC it also resets runtime state.",
    syntax: "CLEAR",
    title: "CLEAR"
  },
  {
    aliases: ["clear screen", "screen clear alias"],
    availability: "Any shell prompt",
    examples: ["CLRSCR"],
    id: "shell-command-clrscr",
    keywords: ["screen", "alias", "banner"],
    notes: [
      "Alias of CLEAR.",
      "Uses the same redraw path and BASIC-runtime reset behavior as CLEAR.",
      "Provided for users who prefer an explicit screen-clear mnemonic."
    ],
    relatedIds: ["shell-command-clear", "public-api-basic-reset-state", "memory-range-text-screen"],
    summary: "Alias of CLEAR for shell screen redraw and optional BASIC-state reset.",
    syntax: "CLRSCR",
    title: "CLRSCR"
  },
  {
    aliases: ["directory", "disk catalog"],
    availability: "Any shell prompt",
    examples: ["DIR", "DIR 1"],
    id: "shell-command-dir",
    keywords: ["disk", "directory", "drive"],
    notes: [
      "Prints the directory for drive 0 or 1; without an argument, drive 0 is used.",
      "The listing includes blocks, quoted names, file type, and a blocks-free footer.",
      "Syntax errors are reported if the drive argument is present but not 0 or 1.",
      "Directory iteration is file-level and uses the same descriptor/controller path as the ROM disk helpers."
    ],
    relatedIds: ["public-api-disk-dir-first", "public-api-disk-dir-next", "shell-command-load", "shell-command-save"],
    summary: "Prints a disk directory listing for drive 0 or 1.",
    syntax: "DIR [0|1]",
    title: "DIR"
  }
] as const;

export const PUBLIC_API_SEEDS = [
  {
    address: 0xe000,
    addressLabel: "$E000",
    aliases: ["cold start", "reset entry"],
    binding: "JSR absolute",
    id: "public-api-cold-start",
    inputs: ["JSR $E000"],
    keywords: ["boot", "reset", "shell"],
    notes: ["Initializes bank config, text mode, screen state, and transfers control into the system shell loop."],
    outputs: ["Does not return to the caller during normal boot flow."],
    relatedIds: ["memory-range-system-rom"],
    scope: "System ROM",
    summary: "Cold-boot entry for full machine initialization.",
    title: "API_COLD_START"
  },
  {
    address: 0xe003,
    addressLabel: "$E003",
    aliases: ["warm start", "shell entry"],
    binding: "JSR absolute",
    id: "public-api-warm-start",
    inputs: ["JSR $E003"],
    keywords: ["boot", "shell", "restart"],
    notes: ["Re-enters the shell path without a full cold reset."],
    outputs: ["Returns control to the monitor / language shell flow."],
    relatedIds: ["memory-range-system-rom"],
    scope: "System ROM",
    summary: "Warm-start entry that returns to the shell path without a full cold boot.",
    title: "API_WARM_START"
  },
  {
    address: 0xe006,
    addressLabel: "$E006",
    aliases: ["chrout", "character out"],
    binding: "JSR absolute",
    id: "public-api-chrout",
    inputs: ["A = output byte"],
    keywords: ["console output", "text io"],
    notes: ["Compatibility character-output entry for ROM-side text I/O paths."],
    outputs: ["Character is emitted to the active text-output path."],
    relatedIds: ["public-api-tty-out"],
    scope: "System ROM",
    summary: "Compatibility single-character output entry.",
    title: "API_CHROUT"
  },
  {
    address: 0xe009,
    addressLabel: "$E009",
    aliases: ["chrin", "character in"],
    binding: "JSR absolute",
    id: "public-api-chrin",
    inputs: ["JSR $E009"],
    keywords: ["console input", "text io"],
    notes: ["Compatibility character-input entry for line-oriented ROM workflows that consume PETSCII guest-text bytes."],
    outputs: ["A = next PETSCII input byte when available."],
    relatedIds: ["public-api-getin"],
    scope: "System ROM",
    summary: "Compatibility single-character input entry.",
    title: "API_CHRIN"
  },
  {
    address: 0xe00c,
    addressLabel: "$E00C",
    aliases: ["getin", "polled input"],
    binding: "JSR absolute",
    id: "public-api-getin",
    inputs: ["JSR $E00C"],
    keywords: ["keyboard", "input", "polling"],
    notes: ["Matrix-backed GETIN prefers keyboard-matrix decoding and remains the common polled PETSCII input API."],
    outputs: ["A = PETSCII input byte or 0 when no input is ready."],
    relatedIds: ["public-api-chrin"],
    scope: "System ROM",
    summary: "Polled character-input API for the active input queue / keyboard path.",
    title: "API_GETIN"
  },
  {
    address: 0xe00f,
    addressLabel: "$E00F",
    aliases: ["screen clear", "clear text screen"],
    binding: "JSR absolute",
    id: "public-api-screen-clear",
    inputs: ["JSR $E00F"],
    keywords: ["screen", "text mode", "clear"],
    notes: ["Clears both the default text characters and their packed foreground-color storage."],
    outputs: ["Text screen is filled with spaces and packed Color-RAM is reset to the ROM default nibble pattern."],
    relatedIds: ["memory-range-text-screen", "memory-range-color-ram"],
    scope: "System ROM",
    summary: "Clears the default 40x25 text screen plus packed foreground-color storage.",
    title: "API_SCREEN_CLEAR"
  },
  {
    address: 0xe012,
    addressLabel: "$E012",
    aliases: ["line input", "legacy line input"],
    binding: "JSR absolute",
    id: "public-api-line-input",
    inputs: ["JSR $E012"],
    keywords: ["input", "line editor", "legacy"],
    notes: ["Legacy line-input helper beside the newer API_LINE_INPUT_EX entry; stores PETSCII bytes in the ROM-side input buffer."],
    outputs: ["PETSCII line-edit result in the ROM-side input buffer."],
    relatedIds: ["public-api-line-input-ex"],
    scope: "System ROM",
    summary: "Legacy ROM line-input routine.",
    title: "API_LINE_INPUT"
  },
  {
    address: 0xe015,
    addressLabel: "$E015",
    aliases: ["set text mode", "text preset"],
    binding: "JSR absolute",
    id: "public-api-set-text",
    inputs: ["JSR $E015"],
    keywords: ["voc", "text mode", "video preset"],
    notes: ["Switches VOC into the ROM's default text-mode preset without changing application-owned RAM contents."],
    outputs: ["VOC text preset is active with D011=$10, D016=$28, D020=$06, and D021=$06."],
    relatedIds: ["register-voc-ctrl1", "register-voc-ctrl2", "register-voc-border-color", "register-voc-bg-color", "register-voc-memptr"],
    scope: "System ROM",
    summary: "Applies the standard text-mode VOC preset used by ROM UI flows.",
    title: "API_SET_TEXT"
  },
  {
    address: 0xe018,
    addressLabel: "$E018",
    aliases: ["set gfx mode", "graphics preset"],
    binding: "JSR absolute",
    id: "public-api-set-gfx",
    inputs: ["JSR $E018"],
    keywords: ["voc", "graphics mode", "bitmap"],
    notes: ["Switches VOC into the ROM's default graphics-mode preset."],
    outputs: ["VOC Mode C bitmap preset is active with D011=$10 and D016=$18."],
    relatedIds: ["register-voc-ctrl1", "register-voc-ctrl2", "memory-range-graphics-plane"],
    scope: "System ROM",
    summary: "Applies the standard graphics-mode VOC preset used by ROM helpers.",
    title: "API_SET_GFX"
  },
  {
    address: 0xe01b,
    addressLabel: "$E01B",
    aliases: ["print hex8", "hex byte"],
    binding: "JSR absolute",
    id: "public-api-print-hex8",
    inputs: ["A = byte value"],
    keywords: ["formatting", "hex"],
    notes: ["Formats one byte as two hexadecimal characters."],
    outputs: ["Two hex digits emitted through the active text-output path."],
    relatedIds: [],
    scope: "System ROM",
    summary: "Prints one byte as two hexadecimal digits.",
    title: "API_PRINT_HEX8"
  },
  {
    address: 0xe01e,
    addressLabel: "$E01E",
    aliases: ["cursor setxy", "set cursor"],
    binding: "JSR absolute",
    id: "public-api-cursor-setxy",
    inputs: ["X = col (0..39)", "Y = row (0..24)"],
    keywords: ["cursor", "text mode", "position"],
    notes: ["Primary text-cursor positioning API for ROM and user code."],
    outputs: ["Cursor position updated."],
    relatedIds: ["memory-range-text-screen"],
    scope: "System ROM",
    summary: "Moves the text cursor to a specific column and row.",
    title: "API_CURSOR_SETXY"
  },
  {
    address: 0xe021,
    addressLabel: "$E021",
    aliases: ["cursor show"],
    binding: "JSR absolute",
    id: "public-api-cursor-show",
    inputs: ["JSR $E021"],
    keywords: ["cursor", "ui"],
    notes: ["Enables the ROM-side text cursor."],
    outputs: ["Cursor becomes visible."],
    relatedIds: ["public-api-cursor-hide", "public-api-cursor-tick"],
    scope: "System ROM",
    summary: "Shows the ROM-managed text cursor.",
    title: "API_CURSOR_SHOW"
  },
  {
    address: 0xe024,
    addressLabel: "$E024",
    aliases: ["cursor hide"],
    binding: "JSR absolute",
    id: "public-api-cursor-hide",
    inputs: ["JSR $E024"],
    keywords: ["cursor", "ui"],
    notes: ["Disables the ROM-side text cursor."],
    outputs: ["Cursor becomes hidden."],
    relatedIds: ["public-api-cursor-show"],
    scope: "System ROM",
    summary: "Hides the ROM-managed text cursor.",
    title: "API_CURSOR_HIDE"
  },
  {
    address: 0xe027,
    addressLabel: "$E027",
    aliases: ["cursor tick", "blink tick"],
    binding: "JSR absolute",
    id: "public-api-cursor-tick",
    inputs: ["JSR $E027"],
    keywords: ["cursor", "blink", "polling"],
    notes: ["Polling-driven cursor blink update helper."],
    outputs: ["Cursor blink state updated."],
    relatedIds: ["public-api-cursor-show", "public-api-cursor-hide"],
    scope: "System ROM",
    summary: "Advances the ROM cursor blink state.",
    title: "API_CURSOR_TICK"
  },
  {
    address: 0xe02a,
    addressLabel: "$E02A",
    aliases: ["put char at", "raw screen code"],
    binding: "JSR absolute",
    id: "public-api-put-char-at",
    inputs: ["A = screen_code", "X = col", "Y = row"],
    keywords: ["screen code", "text output", "raw text"],
    notes: ["Uses raw screen-code bytes directly and does not apply PETSCII-to-screen translation."],
    outputs: ["One text cell updated at the requested position."],
    relatedIds: ["assembler-guide-directive-screen", "memory-range-text-screen"],
    scope: "System ROM",
    summary: "Writes one raw screen-code byte at an explicit text-cell position.",
    title: "API_PUT_CHAR_AT"
  },
  {
    address: 0xe02d,
    addressLabel: "$E02D",
    aliases: ["print at", "screen string"],
    binding: "JSR absolute",
    id: "public-api-print-at",
    inputs: ["A = ptr_lo", "X = ptr_hi"],
    keywords: ["screen code", "string", "text output"],
    notes: ["Prints a NUL-terminated screen-code string starting at the current cursor position."],
    outputs: ["Cursor advances while string bytes are written."],
    relatedIds: ["assembler-guide-directive-screenz", "memory-range-text-screen"],
    scope: "System ROM",
    summary: "Prints a NUL-terminated raw screen-code string.",
    title: "API_PRINT_AT"
  },
  {
    address: 0xe030,
    addressLabel: "$E030",
    aliases: ["system version"],
    binding: "JSR absolute",
    id: "public-api-get-sys-version",
    inputs: ["JSR $E030"],
    keywords: ["version", "system info"],
    notes: ["Compatibility single-byte system-ROM version query."],
    outputs: ["A = system ROM major version."],
    relatedIds: ["public-api-get-sys-info"],
    scope: "System ROM",
    summary: "Returns the system-ROM major version byte.",
    title: "API_GET_SYS_VERSION"
  },
  {
    address: 0xe033,
    addressLabel: "$E033",
    aliases: ["scroll up"],
    binding: "JSR absolute",
    id: "public-api-scroll-up",
    inputs: ["JSR $E033"],
    keywords: ["text mode", "scroll"],
    notes: ["Scrolls both text characters and the packed foreground-color plane by one row."],
    outputs: ["Text view advances by one row."],
    relatedIds: ["memory-range-text-screen", "memory-range-color-ram"],
    scope: "System ROM",
    summary: "Scrolls the text screen and packed foreground-color plane upward by one row.",
    title: "API_SCROLL_UP"
  },
  {
    address: 0xe036,
    addressLabel: "$E036",
    aliases: ["tty out", "petscii out"],
    binding: "JSR absolute",
    id: "public-api-tty-out",
    inputs: ["A = PETSCII byte"],
    keywords: ["tty", "petscii", "console output"],
    notes: ["Handles PETSCII CR/LF/BS, wrap, auto-scroll, and reverse-on/off control bytes before writing screen codes."],
    outputs: ["Translated text output emitted to the screen."],
    relatedIds: ["public-api-tty-print", "assembler-guide-directive-petscii"],
    scope: "System ROM",
    summary: "TTY-style PETSCII text-output API with control handling and screen-code translation.",
    title: "API_TTY_OUT"
  },
  {
    address: 0xe039,
    addressLabel: "$E039",
    aliases: ["tty print", "petscii string"],
    binding: "JSR absolute",
    id: "public-api-tty-print",
    inputs: ["A = ptr_lo", "X = ptr_hi"],
    keywords: ["tty", "string", "petscii"],
    notes: ["Prints a NUL-terminated PETSCII string through API_TTY_OUT."],
    outputs: ["Translated text string emitted to the screen."],
    relatedIds: ["public-api-tty-out"],
    scope: "System ROM",
    summary: "Prints a NUL-terminated PETSCII text string.",
    title: "API_TTY_PRINT"
  },
  {
    address: 0xe03c,
    addressLabel: "$E03C",
    aliases: ["line input ex"],
    binding: "JSR absolute",
    id: "public-api-line-input-ex",
    inputs: ["A = buf_lo", "X = buf_hi", "Y = buf_size"],
    keywords: ["input", "line editor", "nul-terminated"],
    notes: ["Writes a NUL-terminated PETSCII line and returns the resulting length in A and mirrored in $12."],
    outputs: ["A = length", "$12 = length mirror", "Buffer filled with a NUL-terminated PETSCII line."],
    relatedIds: ["public-api-line-input"],
    scope: "System ROM",
    summary: "Extended line-input API with explicit caller-owned buffer and length reporting.",
    title: "API_LINE_INPUT_EX"
  },
  {
    address: 0xe03f,
    addressLabel: "$E03F",
    aliases: ["print hex16"],
    binding: "JSR absolute",
    id: "public-api-print-hex16",
    inputs: ["A = lo", "X = hi"],
    keywords: ["formatting", "hex"],
    notes: ["Formats a 16-bit value as HHLL."],
    outputs: ["Four hex digits emitted through the active text-output path."],
    relatedIds: ["public-api-print-hex8"],
    scope: "System ROM",
    summary: "Prints a 16-bit value as four hexadecimal digits.",
    title: "API_PRINT_HEX16"
  },
  {
    address: 0xe042,
    addressLabel: "$E042",
    aliases: ["clear line"],
    binding: "JSR absolute",
    id: "public-api-clear-line",
    inputs: ["Y = row"],
    keywords: ["text mode", "line clear"],
    notes: ["Clears both characters and packed foreground-color bytes on the selected row."],
    outputs: ["Requested text row reset to blank/default state."],
    relatedIds: ["memory-range-text-screen", "memory-range-color-ram"],
    scope: "System ROM",
    summary: "Clears one text row and its packed foreground-color storage.",
    title: "API_CLEAR_LINE"
  },
  {
    address: 0xe045,
    addressLabel: "$E045",
    aliases: ["gfx clear", "bitmap clear"],
    binding: "JSR absolute",
    id: "public-api-gfx-clear",
    inputs: ["JSR $E045"],
    keywords: ["graphics", "bitmap", "clear"],
    notes: ["Software-clears the active graphics plane selected by the current IOC_VCTRL/memory-pointer state."],
    outputs: ["Active graphics plane cleared."],
    relatedIds: ["memory-range-graphics-plane", "register-voc-memptr", "register-ioc-vctrl"],
    scope: "System ROM",
    summary: "Clears the active graphics plane in software.",
    title: "API_GFX_CLEAR"
  },
  {
    address: 0xe048,
    addressLabel: "$E048",
    aliases: ["pset", "plot pixel"],
    binding: "JSR absolute",
    id: "public-api-gfx-pset",
    inputs: ["A = color (0..3)", "X = x (0..159)", "Y = y (0..199)"],
    keywords: ["graphics", "pixel", "plot"],
    notes: ["Writes one logical 2-bpp pixel in the active bitmap plane; the shipped ROM graphics preset uses Mode C (160x200 logical pixels doubled to 320x200 scanout)."],
    outputs: ["Selected pixel updated."],
    relatedIds: ["memory-range-graphics-plane"],
    scope: "System ROM",
    summary: "Sets one pixel in the active graphics plane.",
    title: "API_GFX_PSET"
  },
  {
    address: 0xe04b,
    addressLabel: "$E04B",
    aliases: ["pget", "read pixel"],
    binding: "JSR absolute",
    id: "public-api-gfx-pget",
    inputs: ["X = x (0..159)", "Y = y (0..199)"],
    keywords: ["graphics", "pixel", "readback"],
    notes: ["Reads one logical 2-bpp pixel from the active bitmap plane."],
    outputs: ["A = color (0..3)."],
    relatedIds: ["memory-range-graphics-plane"],
    scope: "System ROM",
    summary: "Reads one pixel from the active graphics plane.",
    title: "API_GFX_PGET"
  },
  {
    address: 0xe04e,
    addressLabel: "$E04E",
    aliases: ["horizontal line", "hline"],
    binding: "JSR absolute",
    id: "public-api-gfx-hline",
    inputs: ["A = ptr_lo", "X = ptr_hi", "Struct = [x, y, len, color]"],
    keywords: ["graphics", "line drawing"],
    notes: ["Consumes a caller-owned parameter struct."],
    outputs: ["Horizontal line drawn into the active graphics plane."],
    relatedIds: ["memory-range-graphics-plane"],
    scope: "System ROM",
    summary: "Draws a horizontal line from a small parameter struct.",
    title: "API_GFX_HLINE"
  },
  {
    address: 0xe051,
    addressLabel: "$E051",
    aliases: ["vertical line", "vline"],
    binding: "JSR absolute",
    id: "public-api-gfx-vline",
    inputs: ["A = ptr_lo", "X = ptr_hi", "Struct = [x, y, len, color]"],
    keywords: ["graphics", "line drawing"],
    notes: ["Consumes a caller-owned parameter struct."],
    outputs: ["Vertical line drawn into the active graphics plane."],
    relatedIds: ["memory-range-graphics-plane"],
    scope: "System ROM",
    summary: "Draws a vertical line from a small parameter struct.",
    title: "API_GFX_VLINE"
  },
  {
    address: 0xe054,
    addressLabel: "$E054",
    aliases: ["rectangle", "filled rect"],
    binding: "JSR absolute",
    id: "public-api-gfx-rect",
    inputs: ["A = ptr_lo", "X = ptr_hi", "Struct = [x, y, w, h, color]"],
    keywords: ["graphics", "rectangle", "filled"],
    notes: ["Draws a filled rectangle using a caller-owned parameter struct."],
    outputs: ["Filled rectangle written into the active graphics plane."],
    relatedIds: ["memory-range-graphics-plane"],
    scope: "System ROM",
    summary: "Draws a filled rectangle in the active graphics plane.",
    title: "API_GFX_RECT"
  },
  {
    address: 0xe057,
    addressLabel: "$E057",
    aliases: ["wait vbl", "wait frame"],
    binding: "JSR absolute",
    id: "public-api-wait-vbl",
    inputs: ["JSR $E057"],
    keywords: ["timing", "frame sync", "raster"],
    notes: ["Waits until the next frame start where D011.7 is clear and D012 reads 0."],
    outputs: ["Returns after the next visible frame boundary."],
    relatedIds: ["register-voc-raster"],
    scope: "System ROM",
    summary: "Blocks until the next frame start / vertical blank boundary.",
    title: "API_WAIT_VBL"
  },
  {
    address: 0xe05a,
    addressLabel: "$E05A",
    aliases: ["delay ticks", "busy wait"],
    binding: "JSR absolute",
    id: "public-api-delay-ticks",
    inputs: ["A = count_lo", "X = count_hi"],
    keywords: ["timing", "delay"],
    notes: ["Busy-wait delay helper for simple ROM-side timing needs."],
    outputs: ["Returns after the requested delay interval."],
    relatedIds: ["public-api-wait-vbl"],
    scope: "System ROM",
    summary: "Busy-waits for a caller-specified tick count.",
    title: "API_DELAY_TICKS"
  },
  {
    address: 0xe05d,
    addressLabel: "$E05D",
    aliases: ["palette set"],
    binding: "JSR absolute",
    id: "public-api-palette-set",
    inputs: ["A = index (0..15)", "X = rgb_lo", "Y = rgb_hi_low_nibble"],
    keywords: ["palette", "video", "rgb444"],
    notes: ["Writes one palette entry in the VOC palette RAM."],
    outputs: ["Palette entry updated."],
    relatedIds: ["register-voc-ctrl1"],
    scope: "System ROM",
    summary: "Sets one VOC palette entry.",
    title: "API_PALETTE_SET"
  },
  {
    address: 0xe060,
    addressLabel: "$E060",
    aliases: ["palette get"],
    binding: "JSR absolute",
    id: "public-api-palette-get",
    inputs: ["A = index (0..15)"],
    keywords: ["palette", "video", "rgb444"],
    notes: ["Reads one palette entry from the VOC palette RAM."],
    outputs: ["A = rgb_lo", "X = rgb_hi_low_nibble"],
    relatedIds: ["public-api-palette-set"],
    scope: "System ROM",
    summary: "Reads one VOC palette entry.",
    title: "API_PALETTE_GET"
  },
  {
    address: 0xe063,
    addressLabel: "$E063",
    aliases: ["input read", "ioc read"],
    binding: "JSR absolute",
    id: "public-api-input-read",
    inputs: ["X = IOC offset (0..15)"],
    keywords: ["ioc", "input", "mmio helper"],
    notes: ["Reads from the IOC compatibility/input register window via an offset."],
    outputs: ["A = value at $D050 + offset."],
    relatedIds: ["memory-range-io-window"],
    scope: "System ROM",
    summary: "Reads one IOC register through a ROM helper entry.",
    title: "API_INPUT_READ"
  },
  {
    address: 0xe066,
    addressLabel: "$E066",
    aliases: ["system info", "feature bits"],
    binding: "JSR absolute",
    id: "public-api-get-sys-info",
    inputs: ["A = field (0=version, 1=features_lo, 2=features_hi)"],
    keywords: ["feature discovery", "system info"],
    notes: ["Feature bits advertise optional ROM helper families such as graphics, palette, parse, and input helpers."],
    outputs: ["A = requested field value."],
    relatedIds: ["public-api-get-sys-version"],
    scope: "System ROM",
    summary: "Returns version and feature-discovery bytes from the system ROM.",
    title: "API_GET_SYS_INFO"
  },
  {
    address: 0xe069,
    addressLabel: "$E069",
    aliases: ["parse hex8"],
    binding: "JSR absolute",
    id: "public-api-parse-hex8",
    inputs: ["A = ptr_lo", "X = ptr_hi", "Y = start_idx"],
    keywords: ["parser", "hex", "text"],
    notes: ["Parses one hex byte from text starting at the supplied index."],
    outputs: ["C = 1 on success", "A = parsed value", "X = next index"],
    relatedIds: ["public-api-parse-hex16"],
    scope: "System ROM",
    summary: "Parses one hexadecimal byte from text.",
    title: "API_PARSE_HEX8"
  },
  {
    address: 0xe06c,
    addressLabel: "$E06C",
    aliases: ["parse hex16"],
    binding: "JSR absolute",
    id: "public-api-parse-hex16",
    inputs: ["A = ptr_lo", "X = ptr_hi", "Y = start_idx"],
    keywords: ["parser", "hex", "text"],
    notes: ["Parses one 16-bit hex value from text starting at the supplied index."],
    outputs: ["C = 1 on success", "A = lo", "Y = hi", "X = next index"],
    relatedIds: ["public-api-parse-hex8"],
    scope: "System ROM",
    summary: "Parses one hexadecimal 16-bit value from text.",
    title: "API_PARSE_HEX16"
  },
  {
    address: 0xe06f,
    addressLabel: "$E06F",
    aliases: ["disk status"],
    binding: "JSR absolute",
    id: "public-api-disk-status",
    inputs: ["JSR $E06F"],
    keywords: ["disk", "storage"],
    notes: [
      "Returns the current disk/storage status via the shared disk descriptor contract area.",
      "The helper reports the state of the guest-visible storage controller frontend rather than exposing backend-private block details."
    ],
    outputs: ["Disk status fields updated in the storage descriptor workspace."],
    relatedIds: [],
    scope: "System ROM",
    summary: "Fetches current disk status through the ROM storage helper layer.",
    title: "API_DISK_STATUS"
  },
  {
    address: 0xe072,
    addressLabel: "$E072",
    aliases: ["disk dir first"],
    binding: "JSR absolute",
    id: "public-api-disk-dir-first",
    inputs: ["JSR $E072"],
    keywords: ["disk", "directory", "storage"],
    notes: [
      "Starts directory iteration using the shared disk descriptor workspace.",
      "Directory traversal is intentionally file-level and matches the shell-visible drive model."
    ],
    outputs: ["First directory entry loaded into the descriptor/result area when available."],
    relatedIds: ["public-api-disk-dir-next"],
    scope: "System ROM",
    summary: "Begins directory iteration for the active disk unit.",
    title: "API_DISK_DIR_FIRST"
  },
  {
    address: 0xe075,
    addressLabel: "$E075",
    aliases: ["disk dir next"],
    binding: "JSR absolute",
    id: "public-api-disk-dir-next",
    inputs: ["JSR $E075"],
    keywords: ["disk", "directory", "storage"],
    notes: [
      "Advances directory iteration after API_DISK_DIR_FIRST.",
      "Uses the same file-level descriptor contract as the other ROM storage helpers."
    ],
    outputs: ["Next directory entry loaded into the descriptor/result area when available."],
    relatedIds: ["public-api-disk-dir-first"],
    scope: "System ROM",
    summary: "Continues directory iteration for the active disk unit.",
    title: "API_DISK_DIR_NEXT"
  },
  {
    address: 0xe078,
    addressLabel: "$E078",
    aliases: ["disk load"],
    binding: "JSR absolute",
    id: "public-api-disk-load",
    inputs: ["Populate the shared disk descriptor workspace, then JSR $E078."],
    keywords: ["disk", "load", "storage"],
    notes: [
      "Uses the shared storage descriptor at $0340..$035F.",
      "This remains a file-level helper path backed by the storage-controller block rather than a raw block I/O call.",
      "Large transfers stream through the controller data path in 256-byte chunks behind the ROM helper."
    ],
    outputs: ["Buffer/result fields updated in the disk descriptor workspace."],
    relatedIds: ["public-api-disk-save", "public-api-disk-status"],
    scope: "System ROM",
    summary: "Loads a file through the ROM storage descriptor interface.",
    title: "API_DISK_LOAD"
  },
  {
    address: 0xe07b,
    addressLabel: "$E07B",
    aliases: ["disk save"],
    binding: "JSR absolute",
    id: "public-api-disk-save",
    inputs: ["Populate the shared disk descriptor workspace, then JSR $E07B."],
    keywords: ["disk", "save", "storage"],
    notes: [
      "Uses the shared storage descriptor at $0340..$035F.",
      "This remains a file-level helper path backed by the storage-controller block rather than a raw block I/O call.",
      "Large transfers stream through the controller data path in 256-byte chunks behind the ROM helper."
    ],
    outputs: ["Buffer/result fields updated in the disk descriptor workspace."],
    relatedIds: ["public-api-disk-load", "public-api-disk-sync"],
    scope: "System ROM",
    summary: "Saves a file through the ROM storage descriptor interface.",
    title: "API_DISK_SAVE"
  },
  {
    address: 0xe07e,
    addressLabel: "$E07E",
    aliases: ["disk delete"],
    binding: "JSR absolute",
    id: "public-api-disk-delete",
    inputs: ["Populate the shared disk descriptor workspace, then JSR $E07E."],
    keywords: ["disk", "delete", "storage"],
    notes: [
      "Uses the shared storage descriptor at $0340..$035F.",
      "Delete remains a file-level controller command handled by the active drive service."
    ],
    outputs: ["Descriptor result fields updated with delete outcome."],
    relatedIds: ["public-api-disk-rename"],
    scope: "System ROM",
    summary: "Deletes a file through the ROM storage descriptor interface.",
    title: "API_DISK_DELETE"
  },
  {
    address: 0xe081,
    addressLabel: "$E081",
    aliases: ["disk rename"],
    binding: "JSR absolute",
    id: "public-api-disk-rename",
    inputs: ["Populate the shared disk descriptor workspace, then JSR $E081."],
    keywords: ["disk", "rename", "storage"],
    notes: [
      "Uses the shared storage descriptor at $0340..$035F.",
      "Rename remains a file-level controller command handled by the active drive service."
    ],
    outputs: ["Descriptor result fields updated with rename outcome."],
    relatedIds: ["public-api-disk-delete"],
    scope: "System ROM",
    summary: "Renames a file through the ROM storage descriptor interface.",
    title: "API_DISK_RENAME"
  },
  {
    address: 0xe084,
    addressLabel: "$E084",
    aliases: ["disk sync"],
    binding: "JSR absolute",
    id: "public-api-disk-sync",
    inputs: ["JSR $E084"],
    keywords: ["disk", "flush", "storage"],
    notes: [
      "Flushes pending disk state through the active backend.",
      "SYNC is still exposed as a file-level drive-service operation even when the backend path uses the controller/link split internally."
    ],
    outputs: ["Disk/backend state synchronized."],
    relatedIds: ["public-api-disk-save"],
    scope: "System ROM",
    summary: "Flushes pending disk changes to the active backend.",
    title: "API_DISK_SYNC"
  },
  {
    address: 0xe087,
    addressLabel: "$E087",
    aliases: ["disk label"],
    binding: "JSR absolute",
    id: "public-api-disk-label",
    inputs: ["Populate the shared disk descriptor workspace, then JSR $E087."],
    keywords: ["disk", "label", "storage"],
    notes: [
      "Reads or updates disk label information through the shared descriptor workspace.",
      "Label access remains part of the file-level drive-service vocabulary rather than a raw medium-format interface."
    ],
    outputs: ["Descriptor/result fields updated with label data or status."],
    relatedIds: ["public-api-disk-status"],
    scope: "System ROM",
    summary: "Accesses disk label information through the ROM storage helper layer.",
    title: "API_DISK_LABEL"
  },
  {
    address: 0xe08a,
    addressLabel: "$E08A",
    aliases: ["parse dec16"],
    binding: "JSR absolute",
    id: "public-api-parse-dec16",
    inputs: ["A = ptr_lo", "X = ptr_hi", "Y = start_idx"],
    keywords: ["parser", "decimal", "text"],
    notes: ["Decimal parser companion to the hex parser helpers."],
    outputs: ["C = 1 on success", "A/Y = parsed 16-bit value", "X = next index"],
    relatedIds: ["public-api-print-u16-dec"],
    scope: "System ROM",
    summary: "Parses one unsigned decimal 16-bit value from text.",
    title: "API_PARSE_DEC16"
  },
  {
    address: 0xe08d,
    addressLabel: "$E08D",
    aliases: ["print u16 dec", "decimal print"],
    binding: "JSR absolute",
    id: "public-api-print-u16-dec",
    inputs: ["A = lo", "Y = hi"],
    keywords: ["formatting", "decimal"],
    notes: ["Formats one 16-bit value as unsigned decimal text."],
    outputs: ["Decimal string emitted through the active text-output path."],
    relatedIds: ["public-api-parse-dec16"],
    scope: "System ROM",
    summary: "Prints an unsigned 16-bit value in decimal.",
    title: "API_PRINT_U16_DEC"
  },
  {
    address: 0xe0ab,
    addressLabel: "$E0AB",
    aliases: ["transcript break", "transcript separator"],
    binding: "JSR absolute",
    id: "public-api-transcript-record-break",
    inputs: ["JSR $E0AB"],
    keywords: ["transcript", "host tooling"],
    notes: ["Marks a logical break in host-visible transcript flows."],
    outputs: ["Transcript break marker recorded for the active host/runtime path."],
    relatedIds: [],
    scope: "System ROM",
    summary: "Records a transcript break marker for tooling/host flows.",
    title: "API_TRANSCRIPT_RECORD_BREAK"
  },
  {
    address: 0xe0ae,
    addressLabel: "$E0AE",
    aliases: ["abort to shell", "stop to shell"],
    binding: "JSR absolute",
    id: "public-api-host-abort-to-shell",
    inputs: ["JSR $E0AE"],
    keywords: ["shell", "abort", "runtime control"],
    notes: ["Shared break entry used after a runtime notices a cooperative RUN/STOP request and wants to return to the shell."],
    outputs: ["Control returns to the shell path."],
    relatedIds: ["public-api-stop-test"],
    scope: "System ROM",
    summary: "Shared non-reset break entry that returns control to the shell.",
    title: "API_HOST_ABORT_TO_SHELL"
  },
  {
    address: 0xe0b1,
    addressLabel: "$E0B1",
    aliases: ["stop test", "run/stop poll"],
    binding: "JSR absolute",
    id: "public-api-stop-test",
    inputs: ["JSR $E0B1"],
    keywords: ["run stop", "keyboard matrix", "cooperative break"],
    notes: ["Used by running BASIC/FORTH loops to test the IOC keyboard-matrix RUNSTOP key without consuming unrelated input bytes."],
    outputs: ["A = 0 when no cooperative stop is pending.", "A = 1 when RUN/STOP should abort the active loop."],
    relatedIds: ["public-api-host-abort-to-shell"],
    scope: "System ROM",
    summary: "Cooperative RUN/STOP test helper for long-running ROM or module code.",
    title: "API_STOP_TEST"
  },
  {
    address: 0xa000,
    addressLabel: "$A000",
    aliases: ["lang init state", "user api init language state"],
    binding: "JSR absolute",
    id: "public-api-init-language-state",
    inputs: ["JSR $A000"],
    keywords: ["lang rom", "editor mode", "program store"],
    notes: ["Clears the active language mode and resets the in-RAM tokenized program store state."],
    outputs: ["Mode byte cleared; token store reset."],
    relatedIds: ["memory-range-upper-rom", "memory-range-language-program"],
    scope: "lang ROM",
    summary: "Initializes the language-mode state and shared tokenized program store.",
    title: "USER_API_INIT_LANGUAGE_STATE"
  },
  {
    address: 0xa003,
    addressLabel: "$A003",
    aliases: ["edit numbered line"],
    binding: "JSR absolute",
    id: "public-api-edit-numbered-line",
    inputs: ["Call from the shell/editor path with current numbered source input."],
    keywords: ["lang rom", "editor", "numbered line"],
    notes: ["Shared BASIC/ASSEMBLER numbered-line editor path."],
    outputs: ["Numbered line created, replaced, or deleted in the token store."],
    relatedIds: ["memory-range-language-program"],
    scope: "lang ROM",
    summary: "Edits or deletes one numbered program line in BASIC/ASSEMBLER mode.",
    title: "USER_API_EDIT_NUMBERED_LINE"
  },
  {
    address: 0xa006,
    addressLabel: "$A006",
    aliases: ["append auto line", "forth auto append"],
    binding: "JSR absolute",
    id: "public-api-append-auto-line",
    inputs: ["Call from the shell/editor path with unnumbered FORTH input."],
    keywords: ["lang rom", "forth", "editor"],
    notes: ["FORTH-specific append path that assigns implicit line numbers (10, 20, 30, ...)."],
    outputs: ["New line appended to the token store."],
    relatedIds: ["memory-range-language-program"],
    scope: "lang ROM",
    summary: "Appends an implicit-numbered source line for FORTH mode.",
    title: "USER_API_APPEND_AUTO_LINE"
  },
  {
    address: 0xa009,
    addressLabel: "$A009",
    aliases: ["list program", "detokenize listing"],
    binding: "JSR absolute",
    id: "public-api-list-program",
    inputs: ["JSR $A009"],
    keywords: ["lang rom", "list", "detokenize"],
    notes: ["Detokenizes and prints stored program lines through the active language/token table."],
    outputs: ["Program listing emitted through ROM text output."],
    relatedIds: ["memory-range-language-program"],
    scope: "lang ROM",
    summary: "Lists the active tokenized program through the current language rules.",
    title: "USER_API_LIST_PROGRAM"
  },
  {
    address: 0xa00c,
    addressLabel: "$A00C",
    aliases: ["basic immediate", "execute immediate basic"],
    binding: "JSR absolute",
    id: "public-api-basic-exec-immediate",
    inputs: ["Call with current BASIC immediate line prepared in the shared input path."],
    keywords: ["lang rom", "basic", "immediate"],
    notes: ["Runs one BASIC immediate command line without storing it."],
    outputs: ["Immediate BASIC statement executed."],
    relatedIds: ["public-api-run-basic-program"],
    scope: "lang ROM",
    summary: "Executes one immediate BASIC command line.",
    title: "USER_API_BASIC_EXEC_IMMEDIATE"
  },
  {
    address: 0xa00f,
    addressLabel: "$A00F",
    aliases: ["run basic program", "basic run"],
    binding: "JSR absolute",
    id: "public-api-run-basic-program",
    inputs: ["JSR $A00F"],
    keywords: ["lang rom", "basic", "run"],
    notes: ["Runs the stored BASIC program using the resident BASIC runtime in lang.rom."],
    outputs: ["Stored BASIC program executes until END/STOP/error/abort."],
    relatedIds: ["public-api-basic-exec-immediate", "public-api-stop-test"],
    scope: "lang ROM",
    summary: "Runs the current stored BASIC program from the shared token store.",
    title: "USER_API_RUN_BASIC_PROGRAM"
  },
  {
    address: 0xa012,
    addressLabel: "$A012",
    aliases: ["find last line", "program end helper"],
    binding: "JSR absolute",
    id: "public-api-prog-find-last-and-end",
    inputs: ["JSR $A012"],
    keywords: ["lang rom", "program store", "editor helper"],
    notes: ["Shared helper for locating the last record and end pointer in the tokenized program store."],
    outputs: ["Last-record and end-of-program pointers prepared for caller use."],
    relatedIds: ["memory-range-language-program"],
    scope: "lang ROM",
    summary: "Finds the last stored program record and the current end pointer.",
    title: "USER_API_PROG_FIND_LAST_AND_END"
  },
  {
    address: 0xa015,
    addressLabel: "$A015",
    aliases: ["basic reset state"],
    binding: "JSR absolute",
    id: "public-api-basic-reset-state",
    inputs: ["JSR $A015"],
    keywords: ["lang rom", "basic", "runtime reset"],
    notes: ["Resets BASIC runtime state without wiping the stored token program itself."],
    outputs: ["Resident BASIC runtime state reset."],
    relatedIds: ["public-api-run-basic-program"],
    scope: "lang ROM",
    summary: "Resets resident BASIC runtime state.",
    title: "USER_API_BASIC_RESET_STATE"
  },
  {
    address: 0xa018,
    addressLabel: "$A018",
    aliases: ["user api get version", "lang rom version", "upper rom version"],
    binding: "JSR absolute",
    id: "public-api-get-version",
    inputs: ["JSR $A018"],
    keywords: ["lang rom", "version", "startup banner"],
    notes: [
      "Returns the active upper-ROM major version byte so system.rom can render the startup banner without depending on lang.rom's internal layout."
    ],
    outputs: ["A = lang.rom major version byte for the selected upper-ROM image."],
    relatedIds: ["public-api-init-language-state"],
    scope: "lang ROM",
    summary: "Returns the active lang.rom major version byte.",
    title: "USER_API_GET_VERSION"
  },
  {
    address: 0x9000,
    addressLabel: "$9000+$00",
    aliases: ["am8x magic", "module header magic"],
    binding: "Module header field",
    id: "public-api-module-am8x-magic",
    inputs: ["Bytes +0..+3 must contain ASCII 'A', 'M', '8', 'X'."],
    keywords: ["module abi", "header", "magic"],
    notes: ["Host validation rejects the module when the slot header magic does not match AM8X."],
    outputs: ["Valid header magic allows further ABI validation."],
    relatedIds: ["memory-range-language-slot"],
    scope: "Extension Module ABI",
    summary: "Required AM8X header magic for disk-loaded language modules.",
    title: "AM8X Magic"
  },
  {
    address: 0x9004,
    addressLabel: "$9000+$04",
    aliases: ["module abi version", "am8x version"],
    binding: "Module header field",
    id: "public-api-module-abi-version",
    inputs: ["Byte +4 must equal 0x02."],
    keywords: ["module abi", "version"],
    notes: ["Host validation rejects modules with an unsupported ABI version byte."],
    outputs: ["Matching ABI version allows further module validation."],
    relatedIds: ["public-api-module-mode-byte", "memory-range-language-slot"],
    scope: "Extension Module ABI",
    summary: "Required ABI version byte for AM8X language modules.",
    title: "AM8X ABI Version"
  },
  {
    address: 0x9005,
    addressLabel: "$9000+$05",
    aliases: ["module mode byte", "am8x mode"],
    binding: "Module header field",
    id: "public-api-module-mode-byte",
    inputs: ["Byte +5 selects mode 0x01 = assembler, 0x02 = forth."],
    keywords: ["module abi", "mode", "forth", "assembler"],
    notes: ["Host validation rejects a module whose mode byte does not match the requested runtime mode."],
    outputs: ["Matching mode byte authorizes runtime activation for that language."],
    relatedIds: ["memory-range-language-slot"],
    scope: "Extension Module ABI",
    summary: "Required mode byte identifying the module as ASSEMBLER or FORTH.",
    title: "AM8X Mode Byte"
  },
  {
    address: 0x9006,
    addressLabel: "$9000+$06",
    aliases: ["module entry pointer", "am8x entry"],
    binding: "Module header field",
    id: "public-api-module-entry-pointer",
    inputs: ["Bytes +6..+7 are a non-zero little-endian entry pointer."],
    keywords: ["module abi", "entry", "run"],
    notes: [
      "The host invokes this entry as a subroutine and expects an RTS return with carry flag success/failure.",
      "ABI-v2 requires this pointer to resolve into the visible 0x9000..0x9FFF stub window rather than the overlapped upper-ROM range."
    ],
    outputs: ["Valid non-zero pointer becomes the module RUN entry."],
    relatedIds: ["memory-range-language-slot"],
    scope: "Extension Module ABI",
    summary: "Subroutine entry pointer used to start a validated language module.",
    title: "AM8X Entry Pointer"
  },
  {
    address: 0x9008,
    addressLabel: "$9000+$08",
    aliases: ["module tokenizer callback", "am8x tokenizer"],
    binding: "Module header field",
    id: "public-api-module-tokenizer-callback",
    inputs: ["Bytes +8..+9 are a non-zero little-endian tokenizer callback pointer."],
    keywords: ["module abi", "tokenizer", "editor"],
    notes: [
      "FORTH/ASSEMBLER editor tokenization uses this callback after the module is validated and loaded.",
      "ABI-v2 requires this callback pointer to remain inside the visible 0x9000..0x9FFF stub window."
    ],
    outputs: ["Valid non-zero pointer becomes the active tokenizer callback."],
    relatedIds: ["memory-range-language-slot"],
    scope: "Extension Module ABI",
    summary: "Callback pointer used for language-specific tokenization in disk-loaded modules.",
    title: "AM8X Tokenizer Callback"
  },
  {
    address: 0x900a,
    addressLabel: "$9000+$0A",
    aliases: ["module detokenizer callback", "am8x detokenizer"],
    binding: "Module header field",
    id: "public-api-module-detokenizer-callback",
    inputs: ["Bytes +10..+11 are a non-zero little-endian detokenizer callback pointer."],
    keywords: ["module abi", "detokenizer", "list"],
    notes: [
      "FORTH/ASSEMBLER LIST output uses this callback after the module is validated and loaded.",
      "ABI-v2 requires this callback pointer to remain inside the visible 0x9000..0x9FFF stub window."
    ],
    outputs: ["Valid non-zero pointer becomes the active detokenizer callback."],
    relatedIds: ["memory-range-language-slot"],
    scope: "Extension Module ABI",
    summary: "Callback pointer used for language-specific detokenization in disk-loaded modules.",
    title: "AM8X Detokenizer Callback"
  }
] as const;

export const OPCODE_VARIANT_ROWS = `
0x00|INC|acc|2|N,Z
0x06|INC|absy|6|N,Z
0x07|INC|zp|5|N,Z
0x08|INC|zpy|6|N,Z
0x09|INC|indx|7|N,Z
0x0A|INC|indy|8|N,Z
0x0B|INC|indy_pre|7|N,Z
0x0C|INC|indx_post|8|N,Z
0x0D|INC|abs|6|N,Z
0x0E|INC|zpx|6|N,Z
0x0F|INC|absx|6|N,Z
0x11|LDA|imm|3|N,Z
0x12|LDA|zp|4|N,Z
0x13|LDA|abs|5|N,Z
0x14|LDA|zpx|5|N,Z
0x15|LDA|absx|5 (+1 page cross)|N,Z
0x16|LDA|absy|5 (+1 page cross)|N,Z
0x18|LDA|zpy|5|N,Z
0x19|LDA|indx|6|N,Z
0x1A|LDA|indy|6 (+1 page cross)|N,Z
0x1B|LDA|indy_pre|6|N,Z
0x1C|LDA|indx_post|6 (+1 page cross)|N,Z
0x22|STA|zp|3|-
0x23|STA|abs|4|-
0x24|STA|zpx|4|-
0x25|STA|absx|4|-
0x26|STA|absy|4|-
0x28|STA|zpy|4|-
0x29|STA|indx|6|-
0x2A|STA|indy|6|-
0x2B|STA|indy_pre|6|-
0x2C|STA|indx_post|6|-
0x31|LDX|imm|3|N,Z
0x32|LDX|zp|4|N,Z
0x33|LDX|abs|5|N,Z
0x34|LDX|zpx|5|N,Z
0x35|LDX|absx|5 (+1 page cross)|N,Z
0x36|LDX|absy|5 (+1 page cross)|N,Z
0x38|LDX|zpy|5|N,Z
0x39|LDX|indx|6|N,Z
0x3A|LDX|indy|6 (+1 page cross)|N,Z
0x3B|LDX|indy_pre|6|N,Z
0x3C|LDX|indx_post|6 (+1 page cross)|N,Z
0x42|STX|zp|3|-
0x43|STX|abs|4|-
0x44|STX|zpx|4|-
0x45|STX|absx|4|-
0x46|STX|absy|4|-
0x48|STX|zpy|4|-
0x49|STX|indx|6|-
0x4A|STX|indy|6|-
0x4B|STX|indy_pre|6|-
0x4C|STX|indx_post|6|-
0x51|LDY|imm|3|N,Z
0x52|LDY|zp|4|N,Z
0x53|LDY|abs|5|N,Z
0x54|LDY|zpx|5|N,Z
0x55|LDY|absx|5 (+1 page cross)|N,Z
0x56|LDY|absy|5 (+1 page cross)|N,Z
0x58|LDY|zpy|5|N,Z
0x59|LDY|indx|6|N,Z
0x5A|LDY|indy|6 (+1 page cross)|N,Z
0x5B|LDY|indy_pre|6|N,Z
0x5C|LDY|indx_post|6 (+1 page cross)|N,Z
0x62|STY|zp|3|-
0x63|STY|abs|4|-
0x64|STY|zpx|4|-
0x65|STY|absx|4|-
0x66|STY|absy|4|-
0x67|DEC|zp|5|N,Z
0x68|STY|zpy|4|-
0x69|STY|indx|6|-
0x6A|STY|indy|6|-
0x6B|STY|indy_pre|6|-
0x6C|STY|indx_post|6|-
0x6D|DEC|abs|6|N,Z
0x6E|DEC|zpx|6|N,Z
0x6F|DEC|absx|6|N,Z
0x70|DEC|absy|6|N,Z
0x71|AND|imm|3|N,Z
0x72|AND|zp|4|N,Z
0x73|AND|abs|5|N,Z
0x74|AND|zpx|5|N,Z
0x75|AND|absx|5 (+1 page cross)|N,Z
0x76|AND|absy|5 (+1 page cross)|N,Z
0x77|DEC|zpy|6|N,Z
0x78|AND|zpy|5|N,Z
0x79|AND|indx|6|N,Z
0x7A|AND|indy|6 (+1 page cross)|N,Z
0x7B|AND|indy_pre|6|N,Z
0x7C|AND|indx_post|6 (+1 page cross)|N,Z
0x7D|DEC|indx|7|N,Z
0x7E|DEC|indy|8|N,Z
0x7F|DEC|indy_pre|7|N,Z
0x80|DEC|indx_post|8|N,Z
0x81|ORA|imm|3|N,Z
0x82|ORA|zp|4|N,Z
0x83|ORA|abs|5|N,Z
0x84|ORA|zpx|5|N,Z
0x85|ORA|absx|5 (+1 page cross)|N,Z
0x86|ORA|absy|5 (+1 page cross)|N,Z
0x88|ORA|zpy|5|N,Z
0x89|ORA|indx|6|N,Z
0x8A|ORA|indy|6 (+1 page cross)|N,Z
0x8B|ORA|indy_pre|6|N,Z
0x8C|ORA|indx_post|6 (+1 page cross)|N,Z
0x91|EOR|imm|3|N,Z
0x92|EOR|zp|4|N,Z
0x93|EOR|abs|5|N,Z
0x94|EOR|zpx|5|N,Z
0x95|EOR|absx|5 (+1 page cross)|N,Z
0x96|EOR|absy|5 (+1 page cross)|N,Z
0x98|EOR|zpy|5|N,Z
0x99|EOR|indx|6|N,Z
0x9A|EOR|indy|6 (+1 page cross)|N,Z
0x9B|EOR|indy_pre|6|N,Z
0x9C|EOR|indx_post|6 (+1 page cross)|N,Z
0xA1|ADC|imm|3|N,Z,C,V
0xA2|ADC|zp|4|N,Z,C,V
0xA3|ADC|abs|5|N,Z,C,V
0xA4|ADC|zpx|5|N,Z,C,V
0xA5|ADC|absx|5 (+1 page cross)|N,Z,C,V
0xA6|ADC|absy|5 (+1 page cross)|N,Z,C,V
0xA8|ADC|zpy|5|N,Z,C,V
0xA9|ADC|indx|6|N,Z,C,V
0xAA|ADC|indy|6 (+1 page cross)|N,Z,C,V
0xAB|ADC|indy_pre|6|N,Z,C,V
0xAC|ADC|indx_post|6 (+1 page cross)|N,Z,C,V
0xB1|SBC|imm|3|N,Z,C,V
0xB2|SBC|zp|4|N,Z,C,V
0xB3|SBC|abs|5|N,Z,C,V
0xB4|SBC|zpx|5|N,Z,C,V
0xB5|SBC|absx|5 (+1 page cross)|N,Z,C,V
0xB6|SBC|absy|5 (+1 page cross)|N,Z,C,V
0xB8|SBC|zpy|5|N,Z,C,V
0xB9|SBC|indx|6|N,Z,C,V
0xBA|SBC|indy|6 (+1 page cross)|N,Z,C,V
0xBB|SBC|indy_pre|6|N,Z,C,V
0xBC|SBC|indx_post|6 (+1 page cross)|N,Z,C,V
0xC1|CMP|imm|3|N,Z,C
0xC2|CMP|zp|4|N,Z,C
0xC3|CMP|abs|5|N,Z,C
0xC4|CMP|zpx|5|N,Z,C
0xC5|CMP|absx|5 (+1 page cross)|N,Z,C
0xC6|CMP|absy|5 (+1 page cross)|N,Z,C
0xC8|CMP|zpy|5|N,Z,C
0xC9|CMP|indx|6|N,Z,C
0xCA|CMP|indy|6 (+1 page cross)|N,Z,C
0xCB|CMP|indy_pre|6|N,Z,C
0xCC|CMP|indx_post|6 (+1 page cross)|N,Z,C
0x10|ASL|acc|2|N,Z,C
0x17|ASL|zp|5|N,Z,C
0x1D|ASL|abs|6|N,Z,C
0x1E|ASL|zpx|6|N,Z,C
0x1F|ASL|absx|6|N,Z,C
0x20|LSR|acc|2|N,Z,C
0x27|LSR|zp|5|N,Z,C
0x2D|LSR|abs|6|N,Z,C
0x2E|LSR|zpx|6|N,Z,C
0x2F|LSR|absx|6|N,Z,C
0x30|ROL|acc|2|N,Z,C
0x37|ROL|zp|5|N,Z,C
0x3D|ROL|abs|6|N,Z,C
0x3E|ROL|zpx|6|N,Z,C
0x3F|ROL|absx|6|N,Z,C
0x40|ROR|acc|2|N,Z,C
0x47|ROR|zp|5|N,Z,C
0x4D|ROR|abs|6|N,Z,C
0x4E|ROR|zpx|6|N,Z,C
0x4F|ROR|absx|6|N,Z,C
0x50|ASR|acc|2|N,Z,C
0x57|ASR|zp|5|N,Z,C
0x5D|ASR|abs|6|N,Z,C
0x5E|ASR|zpx|6|N,Z,C
0x5F|ASR|absx|6|N,Z,C
0x60|DEC|acc|2|N,Z
0xD0|BEQ|rel|3 / 4 / 5*|-
0xD1|BNE|rel|3 / 4 / 5*|-
0xD2|BCS|rel|3 / 4 / 5*|-
0xD3|BCC|rel|3 / 4 / 5*|-
0xD4|BMI|rel|3 / 4 / 5*|-
0xD5|BPL|rel|3 / 4 / 5*|-
0xD6|BVS|rel|3 / 4 / 5*|-
0xD7|BVC|rel|3 / 4 / 5*|-
0xD8|JEQ|abs|4|-
0xD9|JNE|abs|4|-
0xDA|JCS|abs|4|-
0xDB|JCC|abs|4|-
0xDC|JMI|abs|4|-
0xDD|JPL|abs|4|-
0xDE|JVS|abs|4|-
0xDF|JVC|abs|4|-
0xE0|JMP|abs|4|-
0xE4|JMP|ind|6|-
0xE1|JSR|abs|6|-
0xE2|RTS|imp|4|-
0xE3|RTI|imp|4|P from stack (N,V,I,Z,C)
0xE8|PHA|imp|2|-
0xE9|PLA|imp|2|N,Z
0xEA|PHP|imp|2|-
0xEB|PLP|imp|2|P from stack (N,V,I,Z,C)
0xEC|CLC|imp|2|C cleared
0xED|CLI|imp|2|I cleared
0xEE|SEI|imp|2|I set
0xEF|SEC|imp|2|C set
0xF0|NOP|imp|2|-
0xF1|HLT|imp|2|-
0xF2|BRK|imm|8|I set
0xF3|TSX|imp|2|N,Z
0xF4|TXS|imp|2|-
0xF5|TSY|imp|2|N,Z
0xF6|TYS|imp|2|-
0xF7|TAX|imp|2|N,Z
0xF8|TXA|imp|2|N,Z
0xF9|TAY|imp|2|N,Z
0xFA|TYA|imp|2|N,Z
0xFB|INX|imp|2|N,Z
0xFC|DEX|imp|2|N,Z
0xFD|INY|imp|2|N,Z
0xFE|DEY|imp|2|N,Z
`.trim();
